
import { Component, OnInit, Input, Output, EventEmitter, HostListener, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize, forkJoin, Observable } from 'rxjs';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { LookupService, MaterialLookup, MaterialSearchResult } from '../../services/lookup.service';
import { CisProSearchComponent } from '../cispro-search/cispro-search.component';
import { CisProSearchResult } from '../../services/cispro-search.service';
import { MaterialAutosuggestComponent } from '../material-autosuggest/material-autosuggest.component';
import { FinalReportDialogComponent } from '../final-report-dialog/final-report-dialog.component';
import { AppendNoteDialogComponent } from '../append-note-dialog/append-note-dialog.component';
import { TestAttachmentsDialogComponent } from '../test-attachments-dialog/test-attachments-dialog.component';
import { ProjectDetailsService } from '../../services/project-details.service';
import { OtherTestRow, TestNoteRow, SingleTestDialogTab, SingleTestTypeOption, OtherTestKind, OtherTestSectionConfig, PrintableBatchTestField, PrintableBatchTestBlock, PrintableBatchTestEntry, HpHtReadingRow, HpHtTestRow, DryAdditiveRow, CisProSearchTarget, SlurryOutputActionContext } from './slurry-details.models';
import { buildSlurryBatchDetailsCommands, buildSlurryDetailsCommands, resolveSlurryNavigationContext, SlurryNavigationContext } from './slurry-routing';
import { SlurryBatchesTabComponent } from './tabs/slurry-batches-tab/slurry-batches-tab.component';
import { SlurryMixabilityTabComponent } from './tabs/slurry-mixability-tab/slurry-mixability-tab.component';
import { SlurryFoamTabComponent } from './tabs/slurry-foam-tab/slurry-foam-tab.component';
import { SlurryRedesignTabComponent } from './tabs/slurry-redesign-tab/slurry-redesign-tab.component';
import { SlurryOtherTabComponent } from './tabs/slurry-other-tab/slurry-other-tab.component';
import { SlurryBreadcrumbNavComponent } from './sections/slurry-breadcrumb-nav/slurry-breadcrumb-nav.component';
import { SlurryHeaderStripComponent } from './sections/slurry-header-strip/slurry-header-strip.component';
import { SlurryInfoFooterActionsComponent } from './sections/slurry-info-footer-actions/slurry-info-footer-actions.component';
import { SlurryInfoSectionComponent } from './sections/slurry-info-section/slurry-info-section.component';
import { SlurryLiquidMaterialsSectionComponent } from './sections/slurry-liquid-materials-section/slurry-liquid-materials-section.component';
import { SlurryMaterialErrorsComponent } from './sections/slurry-material-errors/slurry-material-errors.component';
import { SlurryTabsNavComponent } from './sections/slurry-tabs-nav/slurry-tabs-nav.component';
import { densityFromAbsoluteVolume, formatFixedNumber, getFluidBasedSpecificGravity as getFluidBasedSpecificGravityValue, isD182MaterialName as isD182MaterialNameValue, isFluidBasedPercentMaterialId, isFluidBasedWeightMaterialId, minutesToHHMM as formatMinutesToHHMM, normalizeDryAdditiveByWeightOfTypeId as normalizeDryAdditiveByWeightOfTypeIdValue, parseLegacyTimeToMinutes, POTASSIUM_PERCENT_MATERIAL_ID, POTASSIUM_WEIGHT_MATERIAL_ID, resolveDryMaterialDensity, SLURRY_D182_PH_NAME, SODIUM_PERCENT_MATERIAL_ID, SODIUM_WEIGHT_MATERIAL_ID, sumFiniteNumbers, toFiniteNumber } from './utils/slurry-material-calculation.helpers';
import { GenericTestOutputKind, TestOutputScheduleDefaults, ThickeningOutputDialogComponent, ThickeningOutputDialogData } from './popups/thickening-output-dialog/thickening-output-dialog.component';
import { SlurryTestOutputSummaryComponent } from './popups/slurry-test-output-summary/slurry-test-output-summary.component';
import { BatchMeasureMaterialDialogComponent, BatchMeasureMaterialDialogData, BatchMeasureMaterialDialogResult } from './popups/batch-measure-material-dialog/batch-measure-material-dialog.component';
import { SlurryCisproContainerDialogComponent } from './dialogs/slurry-cispro-container-dialog/slurry-cispro-container-dialog.component';
import { addDisposedCisProContainerIds, getCisProContainerTooltip, hasDisposedCisProContainer, parseCisProContainerIds, setCisProContainerDisposedState } from './utils/slurry-cispro-disposed.helpers';
import { SlurryService, SaveSlurryRequestDto, SaveSlurryMaterialDto, SlurryDetailDto, SlurryDetailMaterialDto, SlurryStatusAction, MixabilityDto, RedesignReasonOptionDto, SlurryRedesignReasonInputDto,
  SlurryTestInputDto, ThickeningInputRow, RheologyRow, HpHtReadingDto, HpHtTestDto, FluidLossRow, FreeFluidRow, StaticGelRow, CompStrRow, FoamStabilityRow, BatchTestBriefDto, SlurryBatchDto, CreateBatchRequestDto, SlurryBriefDto,
  AddSingleTestRequestDto, UpdateBatchRequestDto, MeasuredMaterialDto, UpsertMeasuredMaterialRequestDto, MeasuredFoamDto,
  UpsertMeasuredFoamRequestDto, MeasuredMudDto, UpsertMeasuredMudRequestDto, BatchMaterialOptionDto, SlurryCapabilitiesDto,
  SlurryCapabilityItemDto, BatchExecutionResultDto, SubmitBatchRequestDto } from '../../services/slurry.service';

interface GenericTestOutputMetadata {
  staticGelEquipmentTypeId?: number | null;
  compressiveStrengthTypeId?: number | null;
  foamedStabilityTypeId?: number | null;
  rheologyIsSurface?: boolean;
  testInputTemperature?: number | null;
  testInputPressure?: number | null;
}

@Component({
  selector: 'app-slurry-details',
  imports: [
    CommonModule, FormsModule, MatTooltipModule,
    SpinnerComponent, CisProSearchComponent, MaterialAutosuggestComponent,
    SlurryCisproContainerDialogComponent,
    SlurryBatchesTabComponent, SlurryMixabilityTabComponent, SlurryFoamTabComponent, SlurryRedesignTabComponent, SlurryOtherTabComponent,
    SlurryBreadcrumbNavComponent, SlurryHeaderStripComponent, SlurryInfoFooterActionsComponent, SlurryInfoSectionComponent, SlurryLiquidMaterialsSectionComponent, SlurryMaterialErrorsComponent, SlurryTabsNavComponent,
    SlurryTestOutputSummaryComponent
  ],
  templateUrl: './slurry-details.component.html',
  styleUrls: ['./slurry-details.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class SlurryDetailsComponent implements OnInit {
  readonly self = this;
  private readonly editableBatchOutputStatuses = new Set<number>([1, 3, 4]);
  private readonly defaultBatchLabVolume = 600;
  private readonly defaultBatchMeasurementTolerance = 0.5;
  private readonly lbPerGalInGPerMl = 0.119826;
  private readonly defaultExcessFoamPercent = 10;
  private readonly potassiumPercentMaterialId = POTASSIUM_PERCENT_MATERIAL_ID;
  private readonly potassiumWeightMaterialId = POTASSIUM_WEIGHT_MATERIAL_ID;
  private readonly sodiumPercentMaterialId = SODIUM_PERCENT_MATERIAL_ID;
  private readonly sodiumWeightMaterialId = SODIUM_WEIGHT_MATERIAL_ID;
  private readonly fluidBasedCalculationError = 0.00001;
  private readonly fluidBasedCalculationIterations = 25;
  readonly d182ByWeightSelectionError = `${SLURRY_D182_PH_NAME} requires selection of BWOC or BWOW.`;
  private activeEditableNumberTarget: any = null;
  private activeEditableNumberKey: string | null = null;
  private activeEditableNumberRawValue: unknown = null;
  private legacyEquivalentSackOverrideLb: number | null = null;
  private routeBatchId: number | null = null;
  private navigationContext: SlurryNavigationContext = 'standalone';
  testAttachmentCounts: Record<number, number> = {};
  private readonly capabilityDescriptors: Array<{ label: string; selector: (c: SlurryCapabilitiesDto) => SlurryCapabilityItemDto; }> = [
    { label: 'Update Slurry', selector: c => c.updateSlurry },
    { label: 'Send To Lab', selector: c => c.sendToLab },
    { label: 'Revert', selector: c => c.revertToDraft },
    { label: 'Complete Slurry', selector: c => c.completeSlurry },
    { label: 'Save Tests', selector: c => c.saveTests },
    { label: 'Update Mixability', selector: c => c.updateMixability },
    { label: 'Update Base Density', selector: c => c.updateBaseDensity },
    { label: 'Update Redesign', selector: c => c.updateRedesign },
    { label: 'Create Batch', selector: c => c.createBatch },
    { label: 'Edit Batch Execution', selector: c => c.editBatchExecution },
    { label: 'Append/Clear Notes', selector: c => c.appendClearNotes },
    { label: 'Create Final Report', selector: c => c.createFinalReport },
    { label: 'Add Single Test', selector: c => c.addSingleTest },
    { label: 'Edit Crush Length Of Test', selector: c => c.editTestCrushLength }
  ];
  private readonly completeSlurryWorkflowBlockedReason = 'Action is blocked until every batch output is submitted, approved, rejected, or marked for retake.';

  slurryId!: number;
  isLoading = false;
  isSaving = false;
  showTestValidationErrors = false;
  isBlendTest = false;
  projectId: number | null = null;
  projectName = '';

  // ── Embedded mode (rendered inside Projects Dashboard content area) ──
  /** When true, the component uses the *Input bindings instead of route params/state. */
  @Input() embedded = false;
  isAddSingleTestDialogOpen = false;
  @Input() slurryIdInput: number | null = null;
  @Input() slurryUfidInput = '';
  @Input() projectIdInput: number | null = null;
  @Input() projectNameInput = '';
  @Input() projectTypeInput: number | null = null;
  @Input() redesignCommentsInput = '';
  /** Raised to return to Project Details within the dashboard layout. */
  @Output() back = new EventEmitter<void>();
  /** Raised to return to Project Details with Attachments tab selected. */
  @Output() openProjectAttachmentsRequested = new EventEmitter<void>();

  // ── Tabs (mirror WPF SlurryDetailView TabControl) ──
  activeTab: 'info' | 'props' | 'foam' | 'other' | 'mixability' | 'redesign' | 'batches' = 'info';
  setTab(tab: 'info' | 'props' | 'foam' | 'other' | 'mixability' | 'redesign' | 'batches'): void {
    this.activeTab = this.isTabVisible(tab) ? tab : this.getDefaultSavedSlurryTab();
    this.clearBlockedActionMessages();
  }
  /** Test / Mixability tabs require a saved slurry (mixId > 0 in WPF). */
  get hasSavedSlurry(): boolean {
    return this.slurryId > 0;
  }

  showPropsTab(): boolean {
    return this.hasSavedSlurry &&
      this.getTabVisibility(c => c.viewSlurryPropertiesTests, this.hasActiveSlurryPropertiesTests());
  }

  showFoamTab(): boolean {
    return this.hasSavedSlurry &&
      this.getTabVisibility(c => c.viewFoamTests, this.hasActiveFoamTests());
  }

  showOtherTab(): boolean {
    return this.hasSavedSlurry &&
      this.getTabVisibility(c => c.viewOtherTests, this.hasActiveOtherTests());
  }

  showMixabilityTab(): boolean {
    return this.hasSavedSlurry;
  }

  showRedesignTab(): boolean {
    return this.hasSavedSlurry;
  }

  showBatchesTab(): boolean {
    return this.hasSavedSlurry && (this.statusId !== 5 || this.batches.length > 0);
  }

  showCreateBatchSection(): boolean {
    return this.allTestInputs.length > 0 && this.canCreateBatch();
  }

  get isBatchDetailsView(): boolean {
    return !this.embedded && this.routeBatchId != null;
  }

  get showBatchDetailsSections(): boolean {
    return this.selectedBatchId != null && this.isBatchDetailsView;
  }

  // Project type (1=Slurry, 2=Spacer, 3=NonLab) — needed for redesign reasons.
  projectType = 1;

  // ── Mixability tab state ──
  mixabilityId: number | null = null;
  mixScore: number | null = null;
  mixTimeToWetSolids: number | null = null;
  mixNotes = '';
  readonly byWeightOfTypes = [
    { id: 1, label: 'BWOC', tooltip: 'By Weight Of Cement' },
    { id: 2, label: 'BWOW', tooltip: 'By Weight Of Water' }
  ];

  // ── Redesign tab state ──

  // Lookup data
  liquidBaseMaterials: MaterialLookup[] = [];
  blendBaseMaterials: MaterialLookup[] = [];

  // CisPRO Search Modal
  isCisProSearchOpen = false;
  cisProSearchTarget: CisProSearchTarget | null = null;
  isCisProContainersOpen = false;
  cisProContainerTarget: CisProSearchTarget | null = null;
  cisProContainerDraftValue = '';

  // Validation
  blendBaseValidationError = '';
  liquidBaseValidationError = '';
  pozSackWeightValidationErrors: { [key: number]: string } = {};

  // Slurry Info
  ufid = '';
  status = 'Draft';
  statusId = 1;
  isParentProjectCompleted = false;
  capabilities: SlurryCapabilitiesDto | null = null;
  saveMessage = '';
  targetThickeningTimeFrom = '';
  targetThickeningTimeTo = '';
  slurryYield = 0.00;
  totalFluidPercent = 0.00;
  labVolumeMl = 600;
  slurryDensityPpg = 0.00;
  totalFluidGal = 0.00;
  equivalentSackLb = 0.00;
  measuredDepth: number | null = null;
  notes1 = '';
  jobType = '';
  jobSubtype = '';
  projectReceivedBy = '';
  projectRigName = '';
  projectWellName = '';
  projectPipeSize = '';
  sentToLabDate: string | null = null;
  preferredBlendCisProContainerLocation = '';
  private loadedSlurryMaterialDetails: SlurryDetailMaterialDto[] = [];
  readonly disposedSlurryInfoContainers = new Set<string>();

  // Dry Materials
  cementSackLb: any = '0.00';
  isCementSackFocused = false;
  blendCisProContainer = '';
  calculatedSG = 0.000;

  // Blend Base - initial row (no delete button)
  blendBaseInitial = {
    material: '',
    percent: '' as any,
    absVolume: '' as any,
    density: 0,
    weightLb: 0,
    volumeGal: 0,
    labWeightG: 0,
    labVolume: 0,
    cisPRO: ''
  };

  // Blend Base - additional rows (with delete buttons)
  blendBasePozRows: Array<{
    material: string;
    percent: any;
    absVolume: any;
    density: number;
    weightLb: number;
    volumeGal: number;
    labWeightG: number;
    cisPRO: string;
    pozSackWeight: any;
  }> = [];

  // Blend Additives
  blendAdditives: DryAdditiveRow[] = [];

  // Additives (second section)
  additives: DryAdditiveRow[] = [];

  // Liquid Materials
  liquidMaterials: Array<{
    liquid: string;
    volumeGal: number;
    specificGravity: number;
    weightLb: number;
    labWeightG: number;
    cisPRO: string;
  }> = [{
    liquid: '',
    volumeGal: 0.000,
    specificGravity: 0.000,
    weightLb: 0.000,
    labWeightG: 0.00,
    cisPRO: ''
  }];

  // Liquid Additives
  liquidAdditives: Array<{
    additive: string;
    name?: string;
    volumeGal: number;
    specificGravity: number;
    weightLb: number;
    labWeightG: number;
    cisPRO: string;
  }> = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private lookupService: LookupService,
    private slurryService: SlurryService,
    private dialog: MatDialog,
    private projectDetailsService: ProjectDetailsService
  ) {}

  ngOnInit(): void {
    this.scrollToTop();
    // Load lookup data
       this.loadGetSlurryMaterials();

    // Embedded mode: context comes from @Input bindings (dashboard content area).
    if (this.embedded) {
      this.navigationContext = 'dashboard';
      this.projectId = this.projectIdInput;
      this.projectName = this.projectNameInput ?? '';
      if (this.redesignCommentsInput) {
        this.notes1 = this.redesignCommentsInput;
      }
      this.slurryId = this.slurryIdInput ?? 0;
      this.ufid = this.slurryUfidInput;
      this.projectType = this.projectTypeInput ?? 1;
      this.loadSlurryDetails();
      this.initTabsData();
      return;
    }

    this.navigationContext = resolveSlurryNavigationContext(this.route);

    const navState = history.state as {
      projectId?: number;
      slurryUfid?: string;
      projectName?: string;
      projectType?: number;
      redesign?: { comments?: string };
    };

    if (navState?.projectId) {
      this.projectId = navState.projectId;
    }
    if (navState?.slurryUfid) {
      this.ufid = navState.slurryUfid;
    }
    if (navState?.projectName) {
      this.projectName = navState.projectName;
    }
    this.projectType = navState?.projectType ?? 1;
    if (navState?.redesign?.comments) {
      this.notes1 = navState.redesign.comments;
    }

    this.route.paramMap.subscribe(params => {
      const id = params.get('slurryId');
      if (id) {
        this.slurryId = +id;
        const batchId = Number(params.get('batchId'));
        this.routeBatchId = Number.isFinite(batchId) && batchId > 0 ? batchId : null;
        this.loadSlurryDetails();
        this.initTabsData();
        if (this.routeBatchId != null) {
          this.activeTab = 'batches';
          this.selectBatch(this.routeBatchId);
        } else if (this.selectedBatchId != null) {
          this.clearSelectedBatch();
        }
      }
    });
    
    // Always run initial validation to show messages immediately
    this.runAllValidations();
  }
  loadGetSlurryMaterials(): void { 
    this.lookupService.getSlurryMaterial().subscribe({
      next: (materials) => {
        this.liquidBaseMaterials = materials.filter(m => m.materialTypeId == 4);
        this.blendBaseMaterials = materials.filter(m => m.materialTypeId == 3 || m.materialTypeId == 5);
      },
      error: (err) => {
        console.error('Failed to load slurry materials:', err);
        this.liquidBaseMaterials = [];
        this.blendBaseMaterials = [];
      }
    });
  }

  loadSlurryDetails(showLoading = true, preserveActiveTab = false): void {
    if (!this.slurryId || this.slurryId <= 0) {
      // New slurry – nothing to load from the API.
      this.loadProjectPrintHeaderDetails();
      return;
    }
    if (showLoading) {
      this.scrollToTop();
      this.isLoading = true;
    }
    this.slurryService.getById(this.slurryId).subscribe({
      next: (dto) => {
        this.applySlurryDetail(dto);
        this.loadProjectPrintHeaderDetails();
        if (!preserveActiveTab) {
          this.selectActiveTabOnLoad();    // Legacy tab auto-selection per WPF behavior.
        }
        this.loadCapabilities();
        if (showLoading) {
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error('Failed to load slurry details:', err);
        if (showLoading) {
          this.isLoading = false;
        }
      }
    });
  }

  private refreshSlurryAfterMutation(preserveTestSettings = false): void {
    this.loadSlurryDetails(false, true);
    this.loadTests(preserveTestSettings);
  }


  /**
   * Legacy WPF picks the first visible saved-slurry test tab in props -> foam -> other order.
   */
  private selectActiveTabOnLoad(): void {
    this.activeTab = this.isBatchDetailsView ? 'batches' : this.getDefaultSavedSlurryTab();
  }

  private normalizeVisibleTabSelection(): void {
    if (this.isBatchDetailsView) {
      return;
    }

    if (!this.isTabVisible(this.activeTab)) {
      this.activeTab = this.getDefaultSavedSlurryTab();
      return;
    }

    if (this.activeTab === 'props' && !this.showPropsTab()) {
      this.activeTab = this.getDefaultSavedSlurryTab();
      return;
    }

    if (this.activeTab === 'foam' && !this.showFoamTab()) {
      this.activeTab = this.getDefaultSavedSlurryTab();
      return;
    }

    if (this.activeTab === 'other' && !this.showOtherTab()) {
      this.activeTab = this.getDefaultSavedSlurryTab();
      return;
    }

    if (this.activeTab === 'batches' && !this.showBatchesTab()) {
      this.activeTab = this.getDefaultSavedSlurryTab();
      return;
    }
  }

  /** SlurryStatuses enum (1..7) → display label. */
  private readonly statusLabels: Record<number, string> = {
    1: 'Draft',
    2: 'Ready to Test',
    3: 'Ongoing in Lab',
    4: 'Test Failed',
    5: 'Test Completed',
    6: 'Draft Again',
    7: 'Sent to Lab Again'
  };

  /** Maps the loaded slurry header/parameters onto the form fields. */
  private applySlurryDetail(dto: SlurryDetailDto): void {
    if (!dto) {
      return;
    }
    this.ufid = dto.ufSlurryID ?? dto.ufSlurryId ?? this.ufid;
    this.statusId = dto.status ?? this.statusId;
    this.status = this.statusLabels[dto.status] ?? this.status;
    this.isParentProjectCompleted = !!dto.isParentProjectCompleted;
    this.projectId = this.projectId ?? dto.cementProjID;
    this.refreshTestAttachmentCounts();
    this.targetThickeningTimeFrom = this.minutesToHHMM(dto.targetThickeningTimeFrMins);
    this.targetThickeningTimeTo = this.minutesToHHMM(dto.targetThickeningTimeToMins);
    this.measuredDepth = dto.measuredDepth ?? null;
    this.jobType = dto.jobType ?? '';
    this.jobSubtype = dto.jobSubtype ?? '';
    this.projectReceivedBy = dto.projectReceivedBy ?? this.projectReceivedBy;
    this.projectRigName = dto.rigName ?? this.projectRigName;
    this.projectWellName = dto.wellName ?? this.projectWellName;
    this.projectPipeSize = dto.pipeSize ?? this.projectPipeSize;
    this.sentToLabDate = dto.sentToLabDate ?? this.sentToLabDate;
    this.preferredBlendCisProContainerLocation = dto.preferredBlendCisPROContainerLocation ?? this.preferredBlendCisProContainerLocation;
    this.slurryDensityPpg = dto.slurryDensity ?? 0;
    const labVol = (dto.targetLabVolume ?? 0) > 0 ? dto.targetLabVolume ?? 600 : 600;
    this.labVolumeMl = labVol;
    this.legacyEquivalentSackOverrideLb =
      dto.cementSackWeight == null && dto.equivalentSackWeight != null
        ? dto.equivalentSackWeight
        : null;
    this.equivalentSackLb = dto.equivalentSackWeight ?? 0;
    // Legacy CementSackWeight getter exposes the historical equivalent-sack
    // override while the persisted CementSackWeight remains null.
    this.cementSackLb = dto.cementSackWeight ?? this.legacyEquivalentSackOverrideLb ?? 0;
    this.notes1 = dto.comments ?? '';
    this.isBlendTest = !!dto.isBlendTest;
    this.blendCisProContainer = dto.preferredBlendCisPROContainerID ?? '';
    this.loadedSlurryMaterialDetails = dto.materials ?? [];
    this.disposedSlurryInfoContainers.clear();
    if (dto.preferredCisPROContainerIDsHaveDeleted) {
      this.addDisposedSlurryInfoContainers(this.blendCisProContainer);
    }
    for (const material of dto.materials ?? []) {
      if (material.preferredCisProContainerIdsHaveDeleted) {
        this.addDisposedSlurryInfoContainers(material.preferredCisProContainerId);
      }
    }
    // Legacy SlurryMaterials always recalculates after rebuilding its persisted model.
    // Recompute active state rather than displaying potentially stale derived values.
    this.applySlurryMaterials(dto.materials ?? []);
    this.recalculate();
  }

  private loadProjectPrintHeaderDetails(): void {
    if (!this.projectId || this.projectId <= 0) {
      return;
    }

    this.projectDetailsService.getProjectById(this.projectId).subscribe({
      next: (project) => {
        this.projectName = this.projectName || project.name || '';
        this.projectReceivedBy = project.receivedBy || '';
        this.projectRigName = project.rigName || '';
        this.projectWellName = project.wellName || '';
        this.projectPipeSize = project.pipeSize || '';
        if (!this.slurryId || this.slurryId <= 0) {
          this.isBlendTest = !!project.isBlendTest;
        }
      },
      error: (err) => {
        console.error('Failed to load project print header details:', err);
      }
    });
  }

  /**
   * Rebuilds the dry/liquid material grids from the rows returned by the API.
   * Section is taken from the server-derived `sectionType`
   * (CementBase | PozBase | BlendAdditive | Additive | LiquidBase | LiquidAdditive).
   */
  private applySlurryMaterials(materials: SlurryDetailMaterialDto[]): void {
    // Reset all collections to a clean state.
    this.blendBasePozRows = [];
    this.blendAdditives = [];
    this.additives = [];
    this.liquidAdditives = [];
    const labVol = this.labVolumeMl;
    this.blendBaseInitial = {
      material: '', percent: 0, absVolume: 0, density: 0,
      weightLb: 0, volumeGal: 0, labWeightG: 0, labVolume: labVol, cisPRO: ''
    };
    this.liquidMaterials = [{
      liquid: '', volumeGal: 0, specificGravity: 0, weightLb: 0, labWeightG: 0, cisPRO: ''
    }];

    let liquidBaseSet = false;

    for (const m of materials) {
      const id = m.materialId ? String(m.materialId) : '';
      const cis = m.preferredCisProContainerId ?? '';
      switch (m.sectionType) {
        case 'CementBase':
          this.blendBaseInitial.material = id;
          this.blendBaseInitial.percent = m.basePercent ?? 0;
          this.blendBaseInitial.absVolume = m.absoluteVolume ?? 0;
          this.blendBaseInitial.density = m.density ?? 0;
          this.blendBaseInitial.weightLb = m.weight ?? 0;
          this.blendBaseInitial.volumeGal = m.volume ?? 0;
          this.blendBaseInitial.labWeightG = m.calculatedLabWeight ?? 0;
          this.blendBaseInitial.cisPRO = cis;
          break;
        case 'PozBase':
          this.blendBasePozRows.push({
            material: id,
            percent: m.basePercent ?? 0,
            absVolume: m.absoluteVolume ?? 0,
            density: m.density ?? 0,
            weightLb: m.weight ?? 0,
            volumeGal: m.volume ?? 0,
            labWeightG: m.calculatedLabWeight ?? 0,
            cisPRO: cis,
            pozSackWeight: m.pozSackWeight ?? m.sackWeight ?? 0
          });
          break;
        case 'BlendAdditive':
          this.blendAdditives.push({
            additive: id,
            name: m.materialName ?? '',
            percent: m.basePercent ?? 0,
            absVolume: m.absoluteVolume ?? 0,
            density: m.density ?? 0,
            weightLb: m.weight ?? 0,
            volumeGal: m.volume ?? 0,
            labWeightG: m.calculatedLabWeight ?? 0,
            byWeightOfTypeId: this.normalizeDryAdditiveByWeightOfTypeId(m.materialName, m.byWeightOfTypeId),
            cisPRO: cis
          });
          break;
        case 'Additive':
          this.additives.push({
            additive: id,
            name: m.materialName ?? '',
            percent: m.basePercent ?? 0,
            absVolume: m.absoluteVolume ?? 0,
            density: m.density ?? 0,
            weightLb: m.weight ?? 0,
            volumeGal: m.volume ?? 0,
            labWeightG: m.calculatedLabWeight ?? 0,
            byWeightOfTypeId: this.normalizeDryAdditiveByWeightOfTypeId(m.materialName, m.byWeightOfTypeId),
            cisPRO: cis
          });
          break;
        case 'LiquidBase':
          this.liquidMaterials[0] = {
            liquid: id,
            // Legacy LiquidBaseMaterial does not hydrate persisted derived
            // volume/weight. Its constructor starts at zero and recalculates.
            volumeGal: 0,
            specificGravity: m.specificGravity ?? 0,
            weightLb: 0,
            labWeightG: m.calculatedLabWeight ?? 0,
            cisPRO: cis
          };
          liquidBaseSet = true;
          break;
        case 'LiquidAdditive':
          this.liquidAdditives.push({
            additive: id,
            name: m.materialName ?? '',
            volumeGal: m.volume ?? 0,
            specificGravity: m.specificGravity ?? 0,
            weightLb: m.weight ?? 0,
            labWeightG: m.calculatedLabWeight ?? 0,
            cisPRO: cis
          });
          break;
      }
    }

    // Keep a default empty liquid-base row when none was returned.
    if (!liquidBaseSet && this.liquidMaterials.length === 0) {
      this.liquidMaterials = [{
        liquid: '', volumeGal: 0, specificGravity: 0, weightLb: 0, labWeightG: 0, cisPRO: ''
      }];
    }
  }

  /** Converts minutes to "H:MM" for the thickening-time inputs. */
  private minutesToHHMM(minutes: number | null | undefined): string {
    return formatMinutesToHHMM(minutes);
  }


  addBlendBasePoz(): void {
    // Maximum 4 pozzolan materials allowed
    if (this.blendBasePozRows.length >= 4) {
      return;
    }
    
    this.blendBasePozRows.push({
      material: '',
      percent: '0.000' as any,
      absVolume: '0.0000' as any,
      density: 0,
      weightLb: 0,
      volumeGal: 0,
      labWeightG: 0,
      cisPRO: '',
      pozSackWeight: '' // Empty field initially
    });
  }

  getSelectedMaterialName(materialId: string): string {
    const material = this.blendBaseMaterials.find(m => m.id === Number(materialId));
    return material ? material.name : '';
  }
  
  removeBlendBasePoz(index: number) {
    this.blendBasePozRows.splice(index, 1);
    this.recalculate();
  }

  /** Poz options excluding materials already used as the cement base or in other poz rows. */
  availablePozOptions(currentIndex: number): MaterialLookup[] {
    const taken = new Set<string>();
    if (this.blendBaseInitial.material) {
      taken.add(this.blendBaseInitial.material);
    }
    this.blendBasePozRows.forEach((r, i) => {
      if (i !== currentIndex && r.material) {
        taken.add(r.material);
      }
    });
    return this.blendBaseMaterials.filter(m => !taken.has(String(m.id)));
  }


  /** Dry material validation errors. */
  get dryMaterialErrors(): string[] {
    const errors: string[] = [];
    if ([...this.blendAdditives, ...this.additives].some(additive => this.isD182Additive(additive) && additive.byWeightOfTypeId === 0)) {
      errors.push(this.d182ByWeightSelectionError);
    }
    if (!this.blendBaseInitial.material) {
      errors.push('Slurry base is not specified');
    } else {
      const p = this.n(this.blendBaseInitial.percent);
      if (p <= 0 || p > 100) {
        errors.push("Slurry base is required and can't be greater than 100%");
      }
      const totalBase = p + this.blendBasePozRows.reduce((s, r) => s + this.n(r.percent), 0);
      if (totalBase !== 100) {
        errors.push('Total dry base should be exactly 100%');
      }
    }
    return errors;
  }

  /** Liquid material validation errors. */
  get liquidMaterialErrors(): string[] {
    const errors: string[] = [];
    if (!this.liquidMaterials.length || !this.liquidMaterials[0].liquid) {
      errors.push('Base is not specified');
    }
    return errors;
  }

  /** All material validation errors. */
  get materialErrors(): string[] {
    const errors = [
      ...this.dryMaterialErrors,
      ...this.liquidMaterialErrors,
      ...Object.values(this.pozSackWeightValidationErrors)
    ];
    if (this.hasSelectedDisposedSlurryInfoContainer()) {
      errors.push('Cispro container(s) disposed!');
    }
    return errors;
  }

  private addDisposedSlurryInfoContainers(value: string | null | undefined): void {
    addDisposedCisProContainerIds(this.disposedSlurryInfoContainers, value);
  }

  private parseCisProContainers(value: string | null | undefined): string[] {
    return parseCisProContainerIds(value);
  }

  isDisposedSlurryInfoContainer(value: string | null | undefined): boolean {
    return hasDisposedCisProContainer(value, this.disposedSlurryInfoContainers);
  }

  cisProContainerTooltip(value: string | null | undefined, multipleContainers = true): string {
    return getCisProContainerTooltip(value, this.disposedSlurryInfoContainers, multipleContainers);
  }

  private hasSelectedDisposedSlurryInfoContainer(): boolean {
    const selectedContainers = [
      this.blendCisProContainer,
      this.blendBaseInitial.cisPRO,
      ...this.blendBasePozRows.map(row => row.cisPRO),
      ...this.blendAdditives.map(row => row.cisPRO),
      ...this.additives.map(row => row.cisPRO),
      ...this.liquidMaterials.map(row => row.cisPRO),
      ...this.liquidAdditives.map(row => row.cisPRO)
    ];

    return selectedContainers.some(value =>
      this.parseCisProContainers(value).some(barcode => this.disposedSlurryInfoContainers.has(barcode)));
  }

  /** Whether the slurry can be saved (no validation errors). */
  canSave(): boolean {
    return this.materialErrors.length === 0;
  }


  // ── Additive add/remove via autosuggest (mirrors WPF AddAdditiveStrategy) ──

  /** Material ids already used among dry materials (excluded from dry autosuggest). */
  get dryAdditiveExcludeIds(): number[] {
    const ids: number[] = [];
    if (this.blendBaseInitial.material) { ids.push(+this.blendBaseInitial.material); }
    this.blendBasePozRows.forEach(r => { if (r.material) { ids.push(+r.material); } });
    this.blendAdditives.forEach(r => { if (r.additive) { ids.push(+r.additive); } });
    this.additives.forEach(r => { if (r.additive) { ids.push(+r.additive); } });
    if ([...this.blendAdditives, ...this.additives].some(additive => this.isFluidBasedAdditive(additive))) {
      ids.push(
        this.potassiumPercentMaterialId,
        this.potassiumWeightMaterialId,
        this.sodiumPercentMaterialId,
        this.sodiumWeightMaterialId
      );
    }
    return ids;
  }

  /** Material ids already used among liquid materials (excluded from liquid autosuggest). */
  get liquidAdditiveExcludeIds(): number[] {
    const ids: number[] = [];
    this.liquidMaterials.forEach(r => { if (r.liquid) { ids.push(+r.liquid); } });
    this.liquidAdditives.forEach(r => { if (r.additive) { ids.push(+r.additive); } });
    return ids;
  }

  private isD182MaterialName(name?: string | null): boolean {
    return isD182MaterialNameValue(name);
  }

  isD182Additive(row: DryAdditiveRow): boolean {
    return this.isD182MaterialName(row.name);
  }

  private getDryAdditiveMaterialId(row: DryAdditiveRow): number {
    return this.n(row.additive);
  }

  isFluidBasedAdditive(row: DryAdditiveRow): boolean {
    return this.isFluidBasedPercentAdditive(row) || this.isFluidBasedWeightAdditive(row);
  }

  isFluidBasedPercentAdditive(row: DryAdditiveRow): boolean {
    const materialId = this.getDryAdditiveMaterialId(row);
    return isFluidBasedPercentMaterialId(materialId);
  }

  isFluidBasedWeightAdditive(row: DryAdditiveRow): boolean {
    const materialId = this.getDryAdditiveMaterialId(row);
    return isFluidBasedWeightMaterialId(materialId);
  }

  isDryAdditiveWeightEditable(row: DryAdditiveRow): boolean {
    return !this.isFluidBasedPercentAdditive(row);
  }

  private normalizeDryAdditiveByWeightOfTypeId(name?: string | null, byWeightOfTypeId?: number | null): number {
    return normalizeDryAdditiveByWeightOfTypeIdValue(name, byWeightOfTypeId);
  }

  private getDryAdditiveEffectiveByWeightOfTypeId(row: DryAdditiveRow): number {
    return this.normalizeDryAdditiveByWeightOfTypeId(row.name, row.byWeightOfTypeId);
  }

  onDryAdditiveByWeightOfTypeChange(): void {
    this.recalculate();
  }

  private makeDryAdditiveRow(m: MaterialSearchResult): DryAdditiveRow {
    return {
      additive: String(m.id),
      name: m.name ?? '',
      percent: 0,
      absVolume: m.absoluteVolume ?? 0,
      density: m.density ?? 0,
      weightLb: 0,
      volumeGal: 0,
      labWeightG: 0,
      byWeightOfTypeId: this.normalizeDryAdditiveByWeightOfTypeId(m.name, undefined),
      cisPRO: ''
    };
  }

  onBlendAdditiveSelected(m: MaterialSearchResult): void {
    this.blendAdditives.push(this.makeDryAdditiveRow(m));
    this.recalculate();
  }

  onAdditiveSelected(m: MaterialSearchResult): void {
    this.additives.push(this.makeDryAdditiveRow(m));
    this.recalculate();
  }

  onLiquidAdditiveSelected(m: MaterialSearchResult): void {
    this.liquidAdditives.push({
      additive: String(m.id),
      name: m.name ?? '',
      volumeGal: 0,
      specificGravity: m.specificGravity ?? 0,
      weightLb: 0,
      labWeightG: 0,
      cisPRO: ''
    });
    this.recalculate();
  }

  removeBlendAdditive(index: number): void {
    this.blendAdditives.splice(index, 1);
    this.recalculate();
  }

  removeAdditive(index: number): void {
    this.additives.splice(index, 1);
    this.recalculate();
  }

  removeLiquidAdditive(index: number): void {
    this.liquidAdditives.splice(index, 1);
    this.recalculate();
  }

  onBlendBaseMaterialChange(index?: number): void {
    const materialId = index === undefined
      ? +this.blendBaseInitial.material
      : +this.blendBasePozRows[index].material;

    if (!materialId) {
      // No material selected - keep percent and absVolume empty
      if (index === undefined) {
        this.blendBaseInitial.percent = '' as any;
        this.blendBaseInitial.absVolume = '' as any;
      } else {
        this.blendBasePozRows[index].percent = '0.000' as any;
        this.blendBasePozRows[index].absVolume = '0.0000' as any;
      }
      this.recalculate();
      return;
    }
    
    // Material selected - initialize percent to formatted string to preserve formatting
    if (index === undefined) {
      this.blendBaseInitial.percent = '0.000' as any;
    } else {
      this.blendBasePozRows[index].percent = '0.000' as any;
    }
    
    const absVol = this.blendBaseMaterials.find(m => m.id === materialId)?.absoluteVolume ?? 0;
    const density = this.blendBaseMaterials.find(m => m.id === materialId)?.density ?? 0;
    if (index === undefined) {
      this.blendBaseInitial.absVolume = +absVol;
      this.blendBaseInitial.density = +density;
    } else {
      this.blendBasePozRows[index].absVolume = (+absVol).toFixed(4);
      this.blendBasePozRows[index].density = +density;
    }
    const sackWeight = this.blendBaseMaterials.find(m => m.id === materialId)?.sackWeight ?? 0;
    if (index === undefined) {
      this.cementSackLb = +sackWeight;
      if (+sackWeight > 0) {
        this.clearLegacyEquivalentSackOverride();
      }
    } else {
      // Don't auto-populate pozSackWeight for add poz rows - let user enter manually
      // Keep it empty if it hasn't been filled in yet
    }
    this.recalculate();
  }

  onLiquidMaterialChange(index: number): void {
    const selectedMaterialId = +this.liquidMaterials[index].liquid;
    if (!selectedMaterialId) {
      this.validateLiquidBase();
      return;
    }
    this.liquidMaterials[index].specificGravity = +(this.liquidBaseMaterials.find(m => m.id === selectedMaterialId)?.specificGravity ?? 0);
    this.recalculate();
  }

  calculateLiquidMaterialWeight(index: number): void {
    this.recalculate();
  }

  // ---------------------------------------------------------------
  //  Validation
  // ---------------------------------------------------------------
  validateBlendBase(): void {
    // Check if blend base material is selected first
    if (!this.blendBaseInitial.material) {
      this.blendBaseValidationError = 'Slurry base is required and can\'t be greater then 100%';
      return;
    }
    
    // Calculate total blend base percent
    const initialPercent = this.n(this.blendBaseInitial.percent);
    const pozTotalPercent = this.blendBasePozRows.reduce((sum, row) => sum + this.n(row.percent), 0);
    const totalPercent = initialPercent + pozTotalPercent;

    // Check if slurry base is required and not greater than 100%
    if (initialPercent === 0) {
      this.blendBaseValidationError = 'Slurry base is required and can\'t be greater then 100%';
    } else if (totalPercent !== 100) {
      this.blendBaseValidationError = 'Total dry base should be exactly 100%';
    } else {
      this.blendBaseValidationError = '';
    }
  }

  validateLiquidBase(): void {
    // Check if first liquid material is selected
    if (this.liquidMaterials.length > 0 && !this.liquidMaterials[0].liquid) {
      this.liquidBaseValidationError = 'Base is not specified';
    } else {
      this.liquidBaseValidationError = '';
    }
  }

  validatePozSackWeight(index: number): void {
    const pozRow = this.blendBasePozRows[index];
    const rawValue = `${pozRow.pozSackWeight ?? ''}`.trim();
    if (pozRow.material && (rawValue === '' || !Number.isFinite(Number(rawValue)))) {
      this.pozSackWeightValidationErrors[index] = 'Value is required';
    } else {
      delete this.pozSackWeightValidationErrors[index];
    }
  }

  validateAllPozSackWeights(): void {
    this.blendBasePozRows.forEach((row, index) => {
      if (row.material) {
        this.validatePozSackWeight(index);
      }
    });
  }

  runAllValidations(): void {
    this.validateBlendBase();
    this.validateLiquidBase();
    this.validateAllPozSackWeights();
  }

  canCreateSlurry(): boolean {
    // Check if blend base material is selected
    const hasBlendBaseMaterial = this.blendBaseInitial.material !== '' && this.blendBaseInitial.material !== null;
    
    // Check if blend base percent is provided (greater than 0)
    const hasBlendBasePercent = this.n(this.blendBaseInitial.percent) > 0;
    
    // Check if liquid base material is selected (first liquid material)
    const hasLiquidBaseMaterial = this.liquidMaterials.length > 0 && 
                                   this.liquidMaterials[0].liquid !== '' && 
                                   this.liquidMaterials[0].liquid !== null;
    
    // All three conditions must be met
    return hasBlendBaseMaterial && hasBlendBasePercent && hasLiquidBaseMaterial && this.materialErrors.length === 0;
  }

  //  Material Constants  (from WPF MaterialConstants.cs)
  private readonly WATER_DENSITY = 8.34;
  private readonly LB_PER_GAL_IN_G_PER_ML = 0.119826;
  private readonly GAL_IN_FT_CUBICLE = 7.48052;

  //  Display formatting helpers
  /** Format a number to a fixed number of decimal places (for template [value] binding). */
  fmt(value: number, decimals: number): string {
    return formatFixedNumber(value, decimals);
  }

  /** Parse number from input event */
  parseNumber(event: Event): number {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    return isNaN(value) ? 0 : value;
  }

  /** Select all text in input field on focus */
  selectAllOnFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  editableNumberValue(target: any, key: string, decimals: number): string {
    if (this.activeEditableNumberTarget === target && this.activeEditableNumberKey === key) {
      return `${this.activeEditableNumberRawValue ?? target?.[key] ?? ''}`;
    }

    return this.fmt(this.n(target?.[key]), decimals);
  }

  focusEditableNumber(event: FocusEvent, target: any, key: string): void {
    this.activeEditableNumberTarget = target;
    this.activeEditableNumberKey = key;
    this.activeEditableNumberRawValue = target?.[key] ?? '';
    this.selectAllOnFocus(event);
  }

  onEditableNumberInput(target: any, key: string, value: unknown): void {
    target[key] = value;
    if (this.activeEditableNumberTarget === target && this.activeEditableNumberKey === key) {
      this.activeEditableNumberRawValue = value;
    }
  }

  private clearEditableNumberFocus(target?: any, key?: string): void {
    if (!target || !key || (this.activeEditableNumberTarget === target && this.activeEditableNumberKey === key)) {
      this.activeEditableNumberTarget = null;
      this.activeEditableNumberKey = null;
      this.activeEditableNumberRawValue = null;
    }
  }

  /** Format pozzolan percent on blur */
  formatPozPercentOnBlur(index: number, event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    if (!isNaN(value)) {
      // Store the formatted string to preserve decimal places
      this.blendBasePozRows[index].percent = value.toFixed(3);
    } else if (input.value === '') {
      this.blendBasePozRows[index].percent = '0.000';
    }
    this.clearEditableNumberFocus(this.blendBasePozRows[index], 'percent');
  }

  /** Format pozzolan absVolume on blur */
  formatPozAbsVolumeOnBlur(index: number, event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    if (!isNaN(value)) {
      // Store the formatted string to preserve decimal places
      this.blendBasePozRows[index].absVolume = value.toFixed(4);
    } else if (input.value === '') {
      this.blendBasePozRows[index].absVolume = '0.0000';
    }
    this.clearEditableNumberFocus(this.blendBasePozRows[index], 'absVolume');
  }

  /** Format cement sack weight on blur */
  formatCementSackOnBlur(event: FocusEvent): void {
    this.isCementSackFocused = false;
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    if (!isNaN(value)) {
      this.cementSackLb = value.toFixed(2);
    } else if (input.value === '') {
      this.cementSackLb = '0.00' as any;
    }
  }

  /** Format pozzolan sack weight on blur */
  formatPozSackWeightOnBlur(index: number, event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    if (!isNaN(value)) {
      this.blendBasePozRows[index].pozSackWeight = value.toFixed(2);
    } else if (input.value === '') {
      this.blendBasePozRows[index].pozSackWeight = '';
    }
  }

  /** Format initial blend base percent on blur */
  formatInitialPercentOnBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    if (!isNaN(value)) {
      this.blendBaseInitial.percent = value.toFixed(3);
    } else if (input.value === '') {
      this.blendBaseInitial.percent = '0.000';
    }
    this.clearEditableNumberFocus(this.blendBaseInitial, 'percent');
  }

  /** Format initial blend base absVolume on blur */
  formatInitialAbsVolumeOnBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    if (!isNaN(value)) {
      this.blendBaseInitial.absVolume = value.toFixed(4);
    } else if (input.value === '') {
      this.blendBaseInitial.absVolume = '0.0000';
    }
    this.clearEditableNumberFocus(this.blendBaseInitial, 'absVolume');
  }

  /** Legacy DecimalStringPrecisionConverter-style formatting for editable numeric strings. */
  formatNumberOnBlur(target: any, key: string, decimals: number): void {
    const value = this.n(target[key]);
    target[key] = value;
    this.recalculate();
    target[key] = value.toFixed(decimals);
    this.clearEditableNumberFocus(target, key);
  }

  /** Legacy Slurry Density uses DecimalStringPrecisionConverter default F2 on lost focus. */
  formatSlurryDensityOnBlur(): void {
    this.slurryDensityPpg = this.n(this.slurryDensityPpg);
    this.recalculate();
    this.slurryDensityPpg = this.slurryDensityPpg.toFixed(2) as any;
  }

  /** Legacy target time converter formats hour-only input like "12" to "12:00" on lost focus. */
  formatTargetThickeningTime(field: 'from' | 'to'): void {
    const current = field === 'from' ? this.targetThickeningTimeFrom : this.targetThickeningTimeTo;
    const minutes = this.parseTimeToMins(current);
    if (minutes == null) {
      return;
    }
    const formatted = this.minutesToHHMM(minutes);
    if (field === 'from') {
      this.targetThickeningTimeFrom = formatted;
    } else {
      this.targetThickeningTimeTo = formatted;
    }
  }

  /** Numeric guard to avoid string/NaN propagation from text inputs. */
  private n(value: unknown): number {
    return toFiniteNumber(value);
  }

  private clearLegacyEquivalentSackOverride(): void {
    this.legacyEquivalentSackOverrideLb = null;
  }

  onCementSackWeightChange(): void {
    this.clearLegacyEquivalentSackOverride();
    this.recalculate();
  }

  //  Master Recalculate  (mirrors WPF ICalculationController.Recalculate)
  /**
   * Full recalculation chain triggered whenever Percent, SlurryDensity,
   * LabVolume, CementSackWeight, AbsoluteVolume, or liquid volume/SG changes.
   *
   * Order mirrors the WPF sequence:
   *  1. Compute dry material Weight & Volume (depend only on Percent + SackWeights)
   *  2. Compute EquivalentSackWeight
   *  3. Compute dry-additive Weight & Volume (BWOC uses EquivalentSack)
   *  4. Compute liquid-additive Weight (from user-entered Volume + SG)
   *  5. Compute liquid-base Volume via formula, then Weight
   *  6. Compute LabWeight for every material
   *  7. Compute summary fields (SlurryYield, TotalFluid%, CalculatedSG, etc.)
   */
  recalculate(): void {
    // Validate blend base percentages
    this.validateBlendBase();
    // Validate liquid base
    this.validateLiquidBase();
    // Validate all poz sack weights
    this.validateAllPozSackWeights();
    
    // Normalize key numeric inputs first (text inputs can arrive as strings)
    this.cementSackLb = this.n(this.cementSackLb);
    this.labVolumeMl = this.n(this.labVolumeMl);
    this.blendBaseInitial.labVolume = this.labVolumeMl;
    this.slurryDensityPpg = this.n(this.slurryDensityPpg);

    // -- Step 1: Cement weight & volume --
    const blendBaseInitialPercent = this.n(this.blendBaseInitial.percent);
    // const cementDensity = this.densityFromAbsVolume(this.blendBaseInitial.absVolume);
    const cementDensity = this.n(this.blendBaseInitial.density);
    const effectiveCementSackWeight = this.legacyEquivalentSackOverrideLb ?? this.cementSackLb;
    this.blendBaseInitial.weightLb = (effectiveCementSackWeight / 100) * blendBaseInitialPercent;
    this.blendBaseInitial.volumeGal = cementDensity > 0
      ? this.blendBaseInitial.weightLb / cementDensity
      : 0;

    // ── Step 2: Pozzolan weights & volumes ──
    for (const poz of this.blendBasePozRows) {
      // Don't convert percent and absVolume if they're strings - preserve formatting
      if (typeof poz.percent === 'number') {
        poz.percent = this.n(poz.percent);
      }
      if (typeof poz.absVolume === 'number') {
        poz.absVolume = this.n(poz.absVolume);
      }
      poz.density = this.n(poz.density);
      // Don't convert pozSackWeight if it's empty string or formatted string - keep formatting
      if (poz.pozSackWeight !== '' && 
          poz.pozSackWeight !== null && 
          poz.pozSackWeight !== undefined && 
          typeof poz.pozSackWeight === 'number') {
        poz.pozSackWeight = this.n(poz.pozSackWeight);
      }
      // WPF PozzolanMaterial: Weight = PozSackWeight / 100 * Percent
      const pozSackWeightNum = this.n(poz.pozSackWeight);
      const pozPercentNum = this.n(poz.percent);
      poz.weightLb = (pozSackWeightNum / 100) * pozPercentNum;
      poz.volumeGal = poz.density > 0 ? (poz.weightLb / poz.density) : 0;
    }

    // ── Step 3: EquivalentSackWeight ──
    //  WPF: CementSackWeight * CementPercent / 100 + Σ(Poz.Weight)
    const cementWeight = this.blendBaseInitial.weightLb;
    const pozTotalWeight = sumFiniteNumbers(this.blendBasePozRows.map(poz => poz.weightLb));
    this.equivalentSackLb = this.legacyEquivalentSackOverrideLb ?? (cementWeight + pozTotalWeight);

    // ── Step 4: Liquid additive weights (user-entered Volume × Density) ──
    for (const liq of this.liquidAdditives) {
      const specificGravity = this.n(liq.specificGravity);
      const volumeGal = this.n(liq.volumeGal);
      const liqDensity = specificGravity * this.WATER_DENSITY;
      liq.weightLb = liqDensity * volumeGal;
    }

    // ── Step 5: Dry additive weights & volumes (BWOC / BWOW / fluid-based legacy paths) ──
    this.computeDryAdditiveValues(this.blendAdditives);
    this.computeDryAdditiveValues(this.additives);

    // ── Step 6: Liquid base volume via formula, then weight ──
    this.computeLiquidBaseVolume();
    // Legacy performs one liquid strategy calculation, followed by one dry
    // invalidation. A second liquid solve makes BWOW and fluid totals diverge.
    this.computeDryAdditiveValues(this.blendAdditives);
    this.computeDryAdditiveValues(this.additives);

    // ── Step 7: Lab weights for every material ──
    this.computeAllLabWeights();

    // ── Step 8: Summary fields ──
    this.computeSummaryFields();
  }

  //  Dry-additive helper
  private computeDryAdditiveValues(
    additives: DryAdditiveRow[]
  ): void {
    for (const add of additives) {
      const percent = this.n(add.percent);
      const weightLb = this.n(add.weightLb);

      if (this.isFluidBasedPercentAdditive(add)) {
        const specificGravity = this.getFluidBasedSpecificGravity(this.getDryAdditiveMaterialId(add), percent);
        const density = specificGravity * this.WATER_DENSITY;
        const liquidBaseWeight = this.getLiquidBaseWeight();
        add.absVolume = density > 0 ? 1 / density : 0;
        add.weightLb = (liquidBaseWeight / 100) * percent;
        add.volumeGal = density > 0 ? (add.weightLb / density) : 0;
        continue;
      }

      if (this.isFluidBasedWeightAdditive(add)) {
        const specificGravity = this.getFluidBasedSpecificGravity(this.getDryAdditiveMaterialId(add), percent);
        const density = specificGravity * this.WATER_DENSITY;
        add.absVolume = density > 0 ? 1 / density : 0;
        const normalizedWeight = Math.max(0, weightLb);
        // Editable fields retain their F3 display string after blur. Legacy stores
        // the parsed value as decimal; keep the calculation model numeric as well.
        add.weightLb = normalizedWeight;
        add.volumeGal = density > 0 ? (normalizedWeight / density) : 0;
        continue;
      }

      // Legacy Material keeps full-precision SpecificGravity and exposes AV as
      // a derived F4 display value. Do not invert that rounded display value:
      // doing so changes dry volume and the weight-fluid bisection (e.g. KCL).
      const density = resolveDryMaterialDensity(add.density, add.absVolume);
      const bwot = this.getDryAdditiveEffectiveByWeightOfTypeId(add);

      if (bwot === 2) {
        const liquidBaseWeight = this.getLiquidBaseWeight();
        add.weightLb = (liquidBaseWeight / 100) * percent;
      } else if (bwot === 1) {
        add.weightLb = (this.equivalentSackLb / 100) * percent;
      } else {
        add.weightLb = 0;
      }
      add.volumeGal = density > 0 ? (add.weightLb / density) : 0;
    }
  }

  //  Liquid-base Volume  (mirrors WPF VolumeCalculationHelper)
  private computeLiquidBaseVolume(): void {
    if (this.liquidMaterials.length === 0) {
      return;
    }
    const liquidBase = this.liquidMaterials[0];
    const sgBase = this.n(liquidBase.specificGravity);
    if (this.slurryDensityPpg <= 0 || sgBase <= 0) {
      // Legacy VolumeCalculationHelper clears liquid-base volume for invalid
      // density inputs instead of leaving the previous calculated state visible.
      liquidBase.volumeGal = 0;
      liquidBase.weightLb = 0;
      return;
    }

    const fluidWeightBasedAdditive = [...this.blendAdditives, ...this.additives]
      .find(additive => this.isFluidBasedWeightAdditive(additive));

    if (fluidWeightBasedAdditive) {
      const solved = this.solveFluidBasedWeightPercent(fluidWeightBasedAdditive, liquidBase);
      fluidWeightBasedAdditive.percent = solved.percent;
      fluidWeightBasedAdditive.absVolume = solved.absoluteVolume;
      fluidWeightBasedAdditive.volumeGal = solved.volume;
      liquidBase.volumeGal = solved.liquidBaseVolume;
    } else {
      liquidBase.volumeGal = this.computeLegacyLiquidBaseVolume(liquidBase);
    }

    const baseDensity = sgBase * this.WATER_DENSITY;
    liquidBase.weightLb = baseDensity * liquidBase.volumeGal;
  }

  private computeLegacyLiquidBaseVolume(
    liquidBase: { specificGravity: number; volumeGal: number; weightLb: number },
    weightFluidPercent = 0,
    weightFluidSpecificGravity = 0
  ): number {
    const sgBase = this.n(liquidBase.specificGravity);
    if (sgBase <= 0 || this.slurryDensityPpg <= 0) {
      return 0;
    }

    const percentBasedRows = [...this.blendAdditives, ...this.additives]
      .filter(additive => this.isFluidBasedPercentAdditive(additive));
    const normalizedPercentSum = percentBasedRows.reduce((sum, row) => sum + (this.n(row.percent) / 100), 0)
      + (weightFluidPercent > 0 ? (weightFluidPercent / 100) : 0);
    const sgContributionSum = percentBasedRows.reduce((sum, row) => {
      const percent = this.n(row.percent);
      const specificGravity = this.getFluidBasedSpecificGravity(this.getDryAdditiveMaterialId(row), percent);
      return specificGravity > 0 ? sum + ((percent / 100) / specificGravity) : sum;
    }, 0) + (weightFluidPercent > 0 && weightFluidSpecificGravity > 0
      ? ((weightFluidPercent / 100) / weightFluidSpecificGravity)
      : 0);

    const dryWeight = this.getNonFluidBasedDryTotalWeight();
    const dryVolume = this.getNonFluidBasedDryTotalVolume();
    const liqAddWt = sumFiniteNumbers(this.liquidAdditives.map(additive => additive.weightLb));
    const liqAddVol = sumFiniteNumbers(this.liquidAdditives.map(additive => additive.volumeGal));

    const denominator = this.slurryDensityPpg * (1 + (sgBase * sgContributionSum))
      - (this.WATER_DENSITY * sgBase * (1 + normalizedPercentSum));
    if (denominator === 0) {
      return 0;
    }

    const numerator = dryWeight + liqAddWt - this.slurryDensityPpg * (dryVolume + liqAddVol);
    const volume = numerator / denominator;
    return volume > 0 ? volume : 0;
  }

  private solveFluidBasedWeightPercent(
    row: DryAdditiveRow,
    liquidBase: { specificGravity: number; volumeGal: number; weightLb: number }
  ): { percent: number; absoluteVolume: number; volume: number; liquidBaseVolume: number } {
    const desiredWeight = Math.max(0, this.n(row.weightLb));
    if (desiredWeight <= 0) {
      return this.buildFluidBasedWeightSolution(
        desiredWeight,
        0,
        this.getFluidBasedSpecificGravity(this.getDryAdditiveMaterialId(row), 0),
        this.computeLegacyLiquidBaseVolume(liquidBase)
      );
    }

    const trySolve = (percent: number) => {
      const specificGravity = this.getFluidBasedSpecificGravity(this.getDryAdditiveMaterialId(row), percent);
      const liquidBaseVolume = this.computeLegacyLiquidBaseVolume(liquidBase, percent, specificGravity);
      const liquidWeight = liquidBase.specificGravity * this.WATER_DENSITY * liquidBaseVolume;
      const currentWeight = (percent * liquidWeight) / 100;
      return { percent, specificGravity, liquidBaseVolume, currentWeight };
    };

    let low = trySolve(0);
    if (Math.abs(low.currentWeight - desiredWeight) <= this.fluidBasedCalculationError) {
      return this.buildFluidBasedWeightSolution(desiredWeight, low.percent, low.specificGravity, low.liquidBaseVolume);
    }

    let high = trySolve(100);
    if (Math.abs(high.currentWeight - desiredWeight) <= this.fluidBasedCalculationError) {
      return this.buildFluidBasedWeightSolution(desiredWeight, high.percent, high.specificGravity, high.liquidBaseVolume);
    }

    const desiredOutsideRange =
      (desiredWeight > low.currentWeight && desiredWeight > high.currentWeight) ||
      (desiredWeight < low.currentWeight && desiredWeight < high.currentWeight);
    if (desiredOutsideRange) {
      return this.buildFluidBasedWeightSolution(
        desiredWeight,
        0,
        this.getFluidBasedSpecificGravity(this.getDryAdditiveMaterialId(row), 0),
        0
      );
    }

    for (let iteration = 0; iteration < this.fluidBasedCalculationIterations; iteration++) {
      const mid = trySolve((low.percent + high.percent) / 2);
      if (Math.abs(mid.currentWeight - desiredWeight) <= this.fluidBasedCalculationError) {
        return this.buildFluidBasedWeightSolution(desiredWeight, mid.percent, mid.specificGravity, mid.liquidBaseVolume);
      }

      const desiredOutsideLowerMid =
        (desiredWeight > low.currentWeight && desiredWeight > mid.currentWeight) ||
        (desiredWeight < low.currentWeight && desiredWeight < mid.currentWeight);
      if (desiredOutsideLowerMid) {
        low = mid;
      } else {
        high = mid;
      }
    }

    return this.buildFluidBasedWeightSolution(
      desiredWeight,
      0,
      this.getFluidBasedSpecificGravity(this.getDryAdditiveMaterialId(row), 0),
      this.computeLegacyLiquidBaseVolume(liquidBase)
    );
  }

  private buildFluidBasedWeightSolution(
    desiredWeight: number,
    percent: number,
    specificGravity: number,
    liquidBaseVolume: number
  ): { percent: number; absoluteVolume: number; volume: number; liquidBaseVolume: number } {
    const density = specificGravity * this.WATER_DENSITY;
    return {
      percent,
      absoluteVolume: density > 0 ? 1 / density : 0,
      volume: density > 0 ? desiredWeight / density : 0,
      liquidBaseVolume
    };
  }

  private getFluidBasedSpecificGravity(materialId: number, percent: number): number {
    return getFluidBasedSpecificGravityValue(materialId, percent);
  }

  //  Lab-weight  (mirrors WPF Material.LabWeight)
  //
  //  BWOC (ByWeightOfTypeId == 1):
  //    LabWt = (TotalDensity × LabVolume × Weight × 0.119826) / TotalWeight
  //
  //  BWOW (ByWeightOfTypeId == 2):
  //    LabWt = (Σ LiquidBase.LabWeight / 100) × Percent
  private computeAllLabWeights(): void {
    const totalWeight = this.getTotalWeight();
    if (totalWeight <= 0) {
      this.clearAllLabWeights();
      return;
    }
    const labWeightFactor = (this.slurryDensityPpg * this.labVolumeMl * this.LB_PER_GAL_IN_G_PER_ML) / totalWeight;

    // Cement
  this.blendBaseInitial.labWeightG = this.blendBaseInitial.weightLb * labWeightFactor;

    // Poz
    for (const poz of this.blendBasePozRows) {
      poz.labWeightG = poz.weightLb * labWeightFactor;
    }

    // Dry additives
    for (const add of [...this.blendAdditives, ...this.additives]) {
      const bwot = this.getDryAdditiveEffectiveByWeightOfTypeId(add);
      if (bwot === 1) {
        add.labWeightG = add.weightLb * labWeightFactor;
      } else if (bwot !== 2) {
        add.labWeightG = 0;
      }
    }

    // Liquid base
    if (this.liquidMaterials.length > 0) {
      this.liquidMaterials[0].labWeightG = this.liquidMaterials[0].weightLb * labWeightFactor;
    }

    // Liquid additives
    for (const liq of this.liquidAdditives) {
      liq.labWeightG = liq.weightLb * labWeightFactor;
    }

    // Additional liquid materials (index > 0) – treated like additives
    for (let i = 1; i < this.liquidMaterials.length; i++) {
      this.liquidMaterials[i].labWeightG = this.liquidMaterials[i].weightLb * labWeightFactor;
    }

    const liquidBaseLabWeight = sumFiniteNumbers(this.liquidMaterials.map(liquid => liquid.labWeightG));
    for (const add of [...this.blendAdditives, ...this.additives]) {
      if (this.getDryAdditiveEffectiveByWeightOfTypeId(add) === 2) {
        add.labWeightG = (liquidBaseLabWeight / 100) * add.percent;
      }
    }
  }

  private clearAllLabWeights(): void {
    this.blendBaseInitial.labWeightG = 0;
    this.blendBasePozRows.forEach(p => p.labWeightG = 0);
    this.blendAdditives.forEach(a => a.labWeightG = 0);
    this.additives.forEach(a => a.labWeightG = 0);
    this.liquidMaterials.forEach(l => l.labWeightG = 0);
    this.liquidAdditives.forEach(l => l.labWeightG = 0);
  }

  //  Summary Fields
  private computeSummaryFields(): void {
    const totalVolume = this.getTotalVolume();

    // SlurryYield (ft³/sk)  = TotalVolume / 7.48052
    this.slurryYield = totalVolume / this.GAL_IN_FT_CUBICLE;

    // TotalFluidPercent = 100 × LiquidTotalWeight / EquivalentSackWeight
    const liquidTotalWeight = this.getLiquidTotalWeight();
    this.totalFluidPercent = this.equivalentSackLb > 0
      ? (100 * liquidTotalWeight / this.equivalentSackLb)
      : 0;

    // TotalFluidGal = LiquidTotalVolume
    this.totalFluidGal = this.getLiquidTotalVolume();

    // CalculatedSG (BlendSpecificGravity)
    //   = (PrimaryDryTotalWeight / PrimaryDryTotalVolume) / 8.33
    const primaryDryWeight = this.getPrimaryDryTotalWeight();
    const primaryDryVolume = this.getPrimaryDryTotalVolume();
    this.calculatedSG = primaryDryVolume > 0
      ? ((primaryDryWeight / primaryDryVolume) / 8.33)
      : 0;
  }

  get displayedTotalFluidGal(): number {
    // Legacy SlurryMaterials.TotalFluidVolume delegates directly to the
    // current liquid-material collection rather than a persisted summary.
    return this.getLiquidTotalVolume();
  }

  get displayedTotalFluidPercent(): number {
    // Legacy SlurryMaterials.TotalFluidPercent uses the current total liquid
    // weight and effective Equivalent Sack on every read.
    const liquidTotalWeight = this.getLiquidTotalWeight();
    return liquidTotalWeight > 0 && this.equivalentSackLb > 0
      ? (100 * liquidTotalWeight / this.equivalentSackLb)
      : 0;
  }

  //  Weight-to-Percent reverse calculation
  //  (mirrors WPF SlurryDryMaterial.WeightValue setter)
  //  Called when user edits a Weight field instead of Percent
  onWeightChange(row: { percent: number; weightLb: number }, isCement: boolean, pozIndex?: number): void {
    if (isCement) {
      // Cement: Percent = Weight × 100 / CementSackWeight
      row.percent = this.cementSackLb > 0 ? (row.weightLb * 100) / this.cementSackLb : 0;
    } else if (pozIndex !== undefined) {
      // Poz: Percent = Weight × 100 / PozSackWeight
      const pozSW = this.blendBasePozRows[pozIndex].pozSackWeight || 0;
      row.percent = pozSW > 0 ? (row.weightLb * 100) / pozSW : 0;
    } else {
      // Additive: Percent = Weight × 100 / EquivalentSackWeight
      row.percent = this.equivalentSackLb > 0 ? (row.weightLb * 100) / this.equivalentSackLb : 0;
    }
    this.recalculate();
  }

  onDryAdditiveWeightChange(row: DryAdditiveRow, value: unknown): void {
    const weightLb = Math.max(0, this.n(value));
    row.weightLb = value as any;

    if (!this.isFluidBasedWeightAdditive(row)) {
      // Legacy SlurryDetailView.xaml uses EditableWeightTemplate for regular dry additives.
      // SlurryDryMaterial.WeightValue updates PercentValue from EquivalentSackWeight instead of storing Weight directly.
      row.percent = this.equivalentSackLb > 0 ? (weightLb * 100) / this.equivalentSackLb : 0;
    }

    this.recalculate();
  }

  //  AbsoluteVolume change handler
  //  (mirrors WPF SlurryDryMaterial.AbsoluteVolumeValue setter)
  onAbsoluteVolumeChange(): void {
    // Legacy updates SpecificGravity from AV, then Material.Density from SG.
    // The UI stores density (lb/gal), so keep it in lockstep before recalculation.
    this.blendBaseInitial.density = this.densityFromAbsVolume(this.n(this.blendBaseInitial.absVolume));
    for (const row of this.blendBasePozRows) {
      row.density = this.densityFromAbsVolume(this.n(row.absVolume));
    }
    this.recalculate();
  }

  onDryAdditiveAbsoluteVolumeChange(row: DryAdditiveRow): void {
    row.density = this.densityFromAbsVolume(this.n(row.absVolume));
    this.recalculate();
  }

  //  Helper: density from absolute volume
  //  Density = 1 / AbsoluteVolume   (WPF: AbsoluteVolume = 1/Density)
  private densityFromAbsVolume(absVolume: number): number {
    return densityFromAbsoluteVolume(absVolume);
  }

  //  Totals helpers  (mirror WPF GetTotal... methods)

  /** Total dry material weight (cement + poz + dry additives). */
  private getDryTotalWeight(): number {
    return sumFiniteNumbers([
      this.blendBaseInitial.weightLb,
      ...this.blendBasePozRows.map(row => row.weightLb),
      ...this.blendAdditives.map(row => row.weightLb),
      ...this.additives.map(row => row.weightLb)
    ]);
  }

  private getNonFluidBasedDryTotalWeight(): number {
    return sumFiniteNumbers([
      this.blendBaseInitial.weightLb,
      ...this.blendBasePozRows.map(row => row.weightLb),
      ...this.blendAdditives.filter(additive => !this.isFluidBasedAdditive(additive)).map(row => row.weightLb),
      ...this.additives.filter(additive => !this.isFluidBasedAdditive(additive)).map(row => row.weightLb)
    ]);
  }

  /** Total dry material volume. */
  private getDryTotalVolume(): number {
    return sumFiniteNumbers([
      this.blendBaseInitial.volumeGal,
      ...this.blendBasePozRows.map(row => row.volumeGal),
      ...this.blendAdditives.map(row => row.volumeGal),
      ...this.additives.map(row => row.volumeGal)
    ]);
  }

  private getNonFluidBasedDryTotalVolume(): number {
    return sumFiniteNumbers([
      this.blendBaseInitial.volumeGal,
      ...this.blendBasePozRows.map(row => row.volumeGal),
      ...this.blendAdditives.filter(additive => !this.isFluidBasedAdditive(additive)).map(row => row.volumeGal),
      ...this.additives.filter(additive => !this.isFluidBasedAdditive(additive)).map(row => row.volumeGal)
    ]);
  }

  /** Primary dry weight (cement + poz + primary additives). Used for CalculatedSG. */
  private getPrimaryDryTotalWeight(): number {
    return sumFiniteNumbers([
      this.blendBaseInitial.weightLb,
      ...this.blendBasePozRows.map(row => row.weightLb),
      ...this.blendAdditives.map(row => row.weightLb)
    ]);
  }

  /** Primary dry volume. */
  private getPrimaryDryTotalVolume(): number {
    return sumFiniteNumbers([
      this.blendBaseInitial.volumeGal,
      ...this.blendBasePozRows.map(row => row.volumeGal),
      ...this.blendAdditives.map(row => row.volumeGal)
    ]);
  }

  /** Total liquid weight (base + additives). */
  private getLiquidTotalWeight(): number {
    return sumFiniteNumbers([
      ...this.liquidMaterials.map(row => row.weightLb),
      ...this.liquidAdditives.map(row => row.weightLb)
    ]);
  }

  /** Total liquid volume. */
  private getLiquidTotalVolume(): number {
    return sumFiniteNumbers([
      ...this.liquidMaterials.map(row => row.volumeGal),
      ...this.liquidAdditives.map(row => row.volumeGal)
    ]);
  }

  /** Liquid base weight only (first liquid material). */
  private getLiquidBaseWeight(): number {
    return this.liquidMaterials.length > 0 ? this.n(this.liquidMaterials[0].weightLb) : 0;
  }

  /** Grand total weight (dry + liquid). */
  private getTotalWeight(): number {
    return this.getDryTotalWeight() + this.getLiquidTotalWeight();
  }

  /** Grand total volume (dry + liquid). */
  private getTotalVolume(): number {
    return this.getDryTotalVolume() + this.getLiquidTotalVolume();
  }

  addLiquidMaterial(): void {
    this.liquidMaterials.push({
      liquid: '',
      volumeGal: 0,
      specificGravity: 0,
      weightLb: 0,
      labWeightG: 0,
      cisPRO: ''
    });
  }

  openCisProSearch(): void {
    this.cisProSearchTarget = { kind: 'blend' };
    this.isCisProSearchOpen = true;
    this.openCisProSearchDialog();
  }

  clearBlendCisProContainer(): void {
    this.blendCisProContainer = '';
  }

  openLiquidBaseCisProSearch(index: number): void {
    this.openCisProContainersDialog({ kind: 'liquidBase', index });
  }

  openLiquidAdditiveCisProSearch(index: number): void {
    this.openCisProContainersDialog({ kind: 'liquidAdditive', index });
  }

  openBlendBaseCisProSearch(): void {
    if (!this.canEditPrimaryDryCisPro()) {
      return;
    }
    this.openCisProContainersDialog({ kind: 'blendBase' });
  }

  openPozBaseCisProSearch(index: number): void {
    if (!this.canEditPrimaryDryCisPro()) {
      return;
    }
    this.openCisProContainersDialog({ kind: 'pozBase', index });
  }

  openBlendAdditiveCisProSearch(index: number): void {
    if (!this.canEditPrimaryDryCisPro()) {
      return;
    }
    this.openCisProContainersDialog({ kind: 'blendAdditive', index });
  }

  openAdditiveCisProSearch(index: number): void {
    this.openCisProContainersDialog({ kind: 'additive', index });
  }

  closeCisProSearch(): void {
    this.isCisProSearchOpen = false;
    this.cisProSearchTarget = null;
  }

  onCisProSelect(result: CisProSearchResult): void {
    setCisProContainerDisposedState(
      this.disposedSlurryInfoContainers,
      result.barcodeId,
      result.isDeleted
    );

    if (this.isCisProContainersOpen && this.cisProContainerTarget) {
      this.cisProContainerDraftValue = result.barcodeId;
      this.closeCisProSearch();
      return;
    }

    if (!this.cisProSearchTarget) {
      return;
    }

    this.applyCisProValue(this.cisProSearchTarget, result.barcodeId);
    this.closeCisProSearch();
  }

  closeCisProContainersDialog(): void {
    this.isCisProContainersOpen = false;
    this.cisProContainerTarget = null;
    this.cisProContainerDraftValue = '';
  }

  openCisProLookupFromContainersDialog(): void {
    this.isCisProSearchOpen = true;
    this.openCisProSearchDialog();
  }

  saveCisProContainersDialog(draftValue = this.cisProContainerDraftValue): void {
    if (!this.cisProContainerTarget) {
      return;
    }

    this.applyCisProValue(this.cisProContainerTarget, draftValue);
    this.closeCisProContainersDialog();
  }

  private openCisProContainersDialog(target: CisProSearchTarget): void {
    this.cisProContainerTarget = target;
    this.cisProContainerDraftValue = this.getCisProValue(target);
    this.isCisProContainersOpen = true;
  }

  private openCisProSearchDialog(): void {
    const dialogRef = this.dialog.open(CisProSearchComponent, {
      width: '90%',
      maxWidth: '1300px',
      maxHeight: '85vh',
      panelClass: 'cispro-search-dialog'
    });

    dialogRef.afterClosed().subscribe((result: CisProSearchResult | undefined) => {
      if (result) {
        this.onCisProSelect(result);
        return;
      }

      this.closeCisProSearch();
    });
  }

  private getCisProValue(target: CisProSearchTarget): string {
    if (target.kind === 'blend') {
      return this.blendCisProContainer || '';
    }

    if (target.kind === 'blendBase') {
      return this.blendBaseInitial.cisPRO || '';
    }

    if (target.kind === 'pozBase') {
      return this.blendBasePozRows[target.index]?.cisPRO || '';
    }

    if (target.kind === 'blendAdditive') {
      return this.blendAdditives[target.index]?.cisPRO || '';
    }

    if (target.kind === 'additive') {
      return this.additives[target.index]?.cisPRO || '';
    }

    if (target.kind === 'liquidBase') {
      return this.liquidMaterials[target.index]?.cisPRO || '';
    }

    return this.liquidAdditives[target.index]?.cisPRO || '';
  }

  private applyCisProValue(target: CisProSearchTarget, barcodeId: string): void {
    if (target.kind === 'blend') {
      this.blendCisProContainer = barcodeId;
      return;
    }

    if (target.kind === 'blendBase') {
      this.blendBaseInitial.cisPRO = barcodeId;
      return;
    }

    if (target.kind === 'pozBase') {
      const row = this.blendBasePozRows[target.index];
      if (row) {
        row.cisPRO = barcodeId;
      }
      return;
    }

    if (target.kind === 'blendAdditive') {
      const row = this.blendAdditives[target.index];
      if (row) {
        row.cisPRO = barcodeId;
      }
      return;
    }

    if (target.kind === 'additive') {
      const row = this.additives[target.index];
      if (row) {
        row.cisPRO = barcodeId;
      }
      return;
    }

    if (target.kind === 'liquidBase') {
      const row = this.liquidMaterials[target.index];
      if (row) {
        row.cisPRO = barcodeId;
      }
      return;
    }

    const row = this.liquidAdditives[target.index];
    if (row) {
      row.cisPRO = barcodeId;
    }
  }

  /** Builds the full save/update request from the current form + material grids. */
  private buildSaveRequest(): SaveSlurryRequestDto | null {
    if (!this.projectId) {
      console.error('No projectId – cannot save slurry.');
      return null;
    }

    const thickeningRangeError = this.getTargetThickeningRangeError();
    if (thickeningRangeError) {
      this.saveMessage = thickeningRangeError;
      return null;
    }

    if (this.materialErrors.length > 0) {
      this.saveMessage = this.materialErrors[0];
      return null;
    }

    if (this.n(this.labVolumeMl) <= 0) {
      this.saveMessage = 'Lab Volume is required and must be greater than 0.';
      return null;
    }

    if (this.n(this.slurryDensityPpg) <= 0) {
      this.saveMessage = 'Slurry Density is required and must be greater than 0.';
      return null;
    }

    // ── Build the materials list ──────────────────────────────────────────
    const materials: SaveSlurryMaterialDto[] = [];

    // Cement base
    if (this.blendBaseInitial.material) {
      materials.push({
        materialId:         +this.blendBaseInitial.material,
        sectionType:        'CementBase',
        basePercent:        this.blendBaseInitial.percent,
        absoluteVolume:     this.blendBaseInitial.absVolume,
        specificGravity:    this.n(this.blendBaseInitial.density) / this.WATER_DENSITY,
        weight:             this.blendBaseInitial.weightLb,
        volume:             this.blendBaseInitial.volumeGal,
        calculatedLabWeight: this.blendBaseInitial.labWeightG,
        byWeightOfTypeId:   1,
        afterBlend:         false,
        cisProContainerId:  this.blendBaseInitial.cisPRO || undefined
      });
    }

    // Pozzolan base rows
    for (const poz of this.blendBasePozRows) {
      if (poz.material) {
        materials.push({
          materialId:         +poz.material,
          sectionType:        'PozBase',
          basePercent:        poz.percent,
          absoluteVolume:     poz.absVolume,
          specificGravity:    this.n(poz.density) / this.WATER_DENSITY,
          weight:             poz.weightLb,
          volume:             poz.volumeGal,
          calculatedLabWeight: poz.labWeightG,
          pozSackWeight:      poz.pozSackWeight,
          byWeightOfTypeId:   1,
          afterBlend:         false,
          cisProContainerId:  poz.cisPRO || undefined
        });
      }
    }

    // Blend Additives
    for (const add of this.blendAdditives) {
      if (add.additive) {
        materials.push({
          materialId:         +add.additive,
          sectionType:        'BlendAdditive',
          basePercent:        add.percent,
          absoluteVolume:     add.absVolume,
          specificGravity:    resolveDryMaterialDensity(add.density, add.absVolume) / this.WATER_DENSITY,
          weight:             add.weightLb,
          volume:             add.volumeGal,
          calculatedLabWeight: add.labWeightG,
          byWeightOfTypeId:   this.getDryAdditiveEffectiveByWeightOfTypeId(add),
          afterBlend:         false,
          cisProContainerId:  add.cisPRO || undefined
        });
      }
    }

    // Additives
    for (const add of this.additives) {
      if (add.additive) {
        materials.push({
          materialId:         +add.additive,
          sectionType:        'Additive',
          basePercent:        add.percent,
          absoluteVolume:     add.absVolume,
          specificGravity:    resolveDryMaterialDensity(add.density, add.absVolume) / this.WATER_DENSITY,
          weight:             add.weightLb,
          volume:             add.volumeGal,
          calculatedLabWeight: add.labWeightG,
          byWeightOfTypeId:   this.getDryAdditiveEffectiveByWeightOfTypeId(add),
          afterBlend:         true,
          cisProContainerId:  add.cisPRO || undefined
        });
      }
    }

    // Liquid base (first liquid material)
    if (this.liquidMaterials.length > 0 && this.liquidMaterials[0].liquid) {
      const lb = this.liquidMaterials[0];
      materials.push({
        materialId:         +lb.liquid,
        sectionType:        'LiquidBase',
        weight:             lb.weightLb,
        volume:             lb.volumeGal,
        calculatedLabWeight: lb.labWeightG,
        specificGravity:    lb.specificGravity,
        byWeightOfTypeId:   1,
        afterBlend:         false,
        cisProContainerId:  lb.cisPRO || undefined
      });
    }

    // Additional liquid materials (index > 0) and liquid additives
    for (let i = 1; i < this.liquidMaterials.length; i++) {
      const lm = this.liquidMaterials[i];
      if (lm.liquid) {
        materials.push({
          materialId:         +lm.liquid,
          sectionType:        'LiquidAdditive',
          weight:             lm.weightLb,
          volume:             lm.volumeGal,
          calculatedLabWeight: lm.labWeightG,
          specificGravity:    lm.specificGravity,
          byWeightOfTypeId:   1,
          afterBlend:         false,
          cisProContainerId:  lm.cisPRO || undefined
        });
      }
    }
    for (const la of this.liquidAdditives) {
      if (la.additive) {
        materials.push({
          materialId:         +la.additive,
          sectionType:        'LiquidAdditive',
          weight:             la.weightLb,
          volume:             la.volumeGal,
          calculatedLabWeight: la.labWeightG,
          specificGravity:    la.specificGravity,
          byWeightOfTypeId:   1,
          afterBlend:         false,
          cisProContainerId:  la.cisPRO || undefined
        });
      }
    }

    // ── Build the request ─────────────────────────────────────────────────
    const request: SaveSlurryRequestDto = {
      projectId:              this.projectId,
      slurryTypeId:           1,                    // 1 = Slurry
      slurryDensity:          this.slurryDensityPpg || undefined,
      measuredDepth:          this.showMeasuredDepth() ? this.measuredDepth ?? undefined : undefined,
      labVolumeMl:            this.labVolumeMl,
      slurryYield:            this.slurryYield,
      totalFluidPercent:      this.totalFluidPercent,
      totalFluidGal:          this.totalFluidGal,
      equivalentSackWeight:   this.equivalentSackLb,
      notes:                  this.notes1 || undefined,
      targetThickeningTimeFrMins: this.parseTimeToMins(this.targetThickeningTimeFrom),
      targetThickeningTimeToMins: this.parseTimeToMins(this.targetThickeningTimeTo),
      // Preserve the legacy null CementSackWeight marker while an historical
      // EquivalentSack override is active; the displayed sack value is derived.
      cementSackWeight:       this.legacyEquivalentSackOverrideLb == null ? this.cementSackLb || undefined : undefined,
      blendCisProContainerId: this.blendCisProContainer || undefined,
      calculatedSg:           this.calculatedSG,
      materials
    };

    return request;
  }

  /** Whether the loaded slurry may be updated (Draft / DraftAgain only). */
  canUpdate(): boolean {
    const fallback = !this.isParentProjectCompleted && (this.statusId === 1 || this.statusId === 6);
    return this.getCapabilityAllowed(c => c.updateSlurry, fallback);
  }

  showUpdateFooterAction(): boolean {
    return this.slurryId > 0 && this.canUpdate();
  }

  /**
   * Whether the slurry form is editable. Mirrors WPF MixEditInfo:
   * editable for Draft (1), OngoinginLab (3) and DraftAgain (6); new slurries
   * (no id yet) are always editable. Other statuses are read-only.
   */
  get isEditable(): boolean {
    if (!this.slurryId || this.slurryId <= 0) {
      return !this.isParentProjectCompleted;
    }
    return !this.isParentProjectCompleted && (this.statusId === 1 || this.statusId === 3 || this.statusId === 6);
  }

  /**
   * Legacy WPF disables CisPRO editing for primary dry rows on blend tests.
   * The whole Dry Materials block is also gated by MixEditInfo.
   */
  canEditPrimaryDryCisPro(): boolean {
    return this.isEditable && !this.isBlendTest;
  }

  /** Send To Lab is available from Draft (1) / DraftAgain (6). */
  canSendToLab(): boolean {
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && (this.statusId === 1 || this.statusId === 6);
    return this.getCapabilityAllowed(c => c.sendToLab, fallback);
  }

  showSendToLabFooterAction(): boolean {
    return this.slurryId > 0 && this.canSendToLab();
  }

  /** Revert is available from ReadytoTest (2) / SentToLabAgain (7). */
  canRevert(): boolean {
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && (this.statusId === 2 || this.statusId === 7);
    return this.getCapabilityAllowed(c => c.revertToDraft, fallback);
  }

  showRevertFooterAction(): boolean {
    return this.slurryId > 0 && this.canRevert();
  }

  /** Complete is available from any status except TestCompleted (5). */
  canComplete(): boolean {
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && this.statusId !== 5;
    return this.getCapabilityAllowed(c => c.completeSlurry, fallback);
  }

  showCompleteFooterAction(): boolean {
    if (!(this.slurryId > 0)) {
      return false;
    }

    if (this.canComplete()) {
      return true;
    }

    const metadataReason = this.getCapabilityReason(c => c.completeSlurry);
    return metadataReason === this.completeSlurryWorkflowBlockedReason;
  }

  canSaveTests(): boolean {
    const fallback = !this.isParentProjectCompleted && (this.statusId === 1 || this.statusId === 6);
    return this.getCapabilityAllowed(c => c.saveTests, fallback);
  }

  /**
   * Test input editability mirrors legacy TestInputEditInfo:
   * Draft / DraftAgain only, gated by test-input security metadata.
   */
  get isTestsEditable(): boolean {
    return this.canSaveTests();
  }

  canUpdateBaseDensity(): boolean {
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && (this.statusId === 2 || this.statusId === 3 || this.statusId === 7);
    return this.getCapabilityAllowed(c => c.updateBaseDensity, fallback);
  }

  canEditTestCrushLength(): boolean {
    if (this.canSaveTests()) {
      return true;
    }

    return this.getCapabilityAllowed(c => c.editTestCrushLength, false);
  }

  canEditFoamBaseDensity(): boolean {
    return this.canSaveTests() || this.canUpdateBaseDensity();
  }

  canSubmitBaseDensityUpdate(): boolean {
    return this.canUpdateBaseDensity() && this.testSettings.baseDensity != null && this.getTestsValidationError() == null;
  }

  canAppendClearNotes(): boolean {
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && this.isActiveSlurryStatus();
    return this.getCapabilityAllowed(c => c.appendClearNotes, fallback);
  }

  private isActiveSlurryStatus(): boolean {
    return this.statusId === 1 ||
      this.statusId === 2 ||
      this.statusId === 3 ||
      this.statusId === 4 ||
      this.statusId === 6 ||
      this.statusId === 7;
  }

  canCreateFinalReport(): boolean {
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0;
    return this.getCapabilityAllowed(c => c.createFinalReport, fallback);
  }

  showThickeningHesitationSection(): boolean {
    return this.isTestsEditable || this.thickening.isHesitation;
  }

  showThickeningSrtSection(): boolean {
    return this.isTestsEditable || this.thickening.hasSrt;
  }

  private getMaterialValidationBlockedReason(actionLabel: string): string {
    if (!this.materialErrors.length) {
      return '';
    }

    return `${actionLabel} is blocked until slurry validation errors are fixed: ${this.materialErrors.join(' ')}`;
  }

  private getTestsValidationBlockedReason(actionLabel: string, includeOtherTests = true): string {
    if (!this.canSaveTests()) {
      return '';
    }

    const validationError = this.getTestsValidationError(includeOtherTests);
    return validationError
      ? `${actionLabel} is blocked until test validation errors are fixed: ${validationError}`
      : '';
  }

  getCreateSlurryBlockedReason(): string {
    if (!this.slurryId || this.slurryId <= 0) {
      if (this.isParentProjectCompleted) {
        return 'This slurry is read-only because the parent project is completed.';
      }
      return this.getMaterialValidationBlockedReason('Create');
    }

    return '';
  }

  getUpdateSlurryBlockedReason(): string {
    if (this.canUpdate() && this.canSave()) {
      return '';
    }

    const metadataReason = this.getCapabilityReason(c => c.updateSlurry);
    if (metadataReason) {
      return metadataReason;
    }
    if (this.isParentProjectCompleted) {
      return 'This slurry is read-only because the parent project is completed.';
    }
    if (!this.canUpdate()) {
      return 'Update is available only while the slurry is in Draft or Draft Again.';
    }
    const testsBlockedReason = this.getTestsValidationBlockedReason('Update');
    if (testsBlockedReason) {
      return testsBlockedReason;
    }
    return this.getMaterialValidationBlockedReason('Update');
  }

  getSendToLabBlockedReason(): string {
    if (this.canSendToLab() && this.canSave()) {
      return '';
    }

    const metadataReason = this.getCapabilityReason(c => c.sendToLab);
    if (metadataReason) {
      return metadataReason;
    }
    if (this.isParentProjectCompleted) {
      return 'Send To Lab is blocked because the parent project is completed.';
    }
    if (this.slurryId <= 0) {
      return 'Save the slurry before sending it to the lab.';
    }
    if (!this.canSendToLab()) {
      return 'Send To Lab is available only while the slurry is in Draft or Draft Again.';
    }
    const testsBlockedReason = this.getTestsValidationBlockedReason('Send To Lab', false);
    if (testsBlockedReason) {
      return testsBlockedReason;
    }
    return this.getMaterialValidationBlockedReason('Send To Lab');
  }

  getRevertBlockedReason(): string {
    if (this.canRevert()) {
      return '';
    }

    const metadataReason = this.getCapabilityReason(c => c.revertToDraft);
    if (metadataReason) {
      return metadataReason;
    }
    if (this.isParentProjectCompleted) {
      return 'Revert is blocked because the parent project is completed.';
    }
    if (this.slurryId <= 0) {
      return 'Save the slurry before reverting it.';
    }
    return 'Revert is available only while the slurry is Ready to Test or Sent to Lab Again.';
  }

  getCompleteSlurryBlockedReason(): string {
    if (this.canComplete()) {
      return '';
    }

    const metadataReason = this.getCapabilityReason(c => c.completeSlurry);
    if (metadataReason) {
      return metadataReason;
    }
    if (this.isParentProjectCompleted) {
      return 'Complete Slurry is blocked because the parent project is completed.';
    }
    if (this.slurryId <= 0) {
      return 'Save the slurry before completing it.';
    }
    if (this.statusId === 5) {
      return 'This slurry is already completed.';
    }
    return 'Complete Slurry is unavailable for the current slurry.';
  }

  getSaveTestsBlockedReason(): string {
    if (this.canSaveTests()) {
      return '';
    }

    const metadataReason = this.getCapabilityReason(c => c.saveTests);
    if (metadataReason) {
      return metadataReason;
    }
    if (this.isParentProjectCompleted) {
      return 'Tests are read-only because the parent project is completed.';
    }
    return 'Tests can only be saved while the slurry is in Draft or Draft Again.';
  }

  getAppendClearNotesBlockedReason(): string {
    if (this.canAppendClearNotes()) {
      return '';
    }

    const metadataReason = this.getCapabilityReason(c => c.appendClearNotes);
    if (metadataReason) {
      return metadataReason;
    }
    if (this.isParentProjectCompleted) {
      return 'Append / Clear Note is blocked because the parent project is completed.';
    }
    if (this.slurryId <= 0) {
      return 'Save the slurry before appending or clearing notes.';
    }
    return 'Append / Clear Note is available only while the slurry is active and the parent project is not completed.';
  }

  getCreateFinalReportBlockedReason(): string {
    if (this.canCreateFinalReport()) {
      return '';
    }

    const metadataReason = this.getCapabilityReason(c => c.createFinalReport);
    if (metadataReason) {
      return metadataReason;
    }
    if (this.isParentProjectCompleted) {
      return 'Final report creation is blocked because the parent project is completed.';
    }
    if (this.slurryId <= 0) {
      return 'Save the slurry before creating the final report.';
    }
    return 'Final report creation is unavailable for the current slurry.';
  }

  getSubmitBaseDensityUpdateBlockedReason(): string {
    if (this.canSubmitBaseDensityUpdate()) {
      return '';
    }
    if (!this.canUpdateBaseDensity()) {
      const metadataReason = this.getCapabilityReason(c => c.updateBaseDensity);
      if (metadataReason) {
        return metadataReason;
      }
      if (this.isParentProjectCompleted) {
        return 'Base density is read-only because the parent project is completed.';
      }
      if (this.slurryId <= 0) {
        return 'Save the slurry before updating base density.';
      }
      return 'Base density can only be updated while the slurry is Ready to Test, In Lab, or Sent to Lab Again.';
    }
    if (this.testSettings.baseDensity == null) {
      return 'Enter Base Density before updating it.';
    }

    return this.getTestsValidationError() ?? '';
  }

  getTestAttachmentsBlockedReason(testInputMasterId: number | null | undefined): string {
    if (this.canOpenTestAttachments(testInputMasterId)) {
      return '';
    }
    if (this.slurryId <= 0) {
      return 'Slurry details must be loaded before test attachments are available.';
    }
    return 'Save this test first to view its attachments.';
  }

  getTestNoteActionsBlockedReason(testInputMasterId: number | null | undefined): string {
    if (this.canOpenTestNoteActions(testInputMasterId)) {
      return '';
    }

    return this.isPersistedTestInputMaster(testInputMasterId)
      ? this.getAppendClearNotesBlockedReason()
      : (this.getSaveTestsBlockedReason() || 'Save this test first to append or clear its notes.');
  }

  getTestOutputBlockedReason(outputs: BatchTestBriefDto[] | null | undefined): string {
    return this.getTestPrimaryOutput(outputs) ? '' : 'No batch output is recorded for this test yet.';
  }

  getCreateBatchBlockedReason(): string {
    return this.selectedTestMasterIds.length > 0 ? '' : 'Select at least one test before creating a batch.';
  }

  getAddSingleTestBlockedReason(): string {
    if (this.canAddSingleTest()) {
      return '';
    }

    return this.getCapabilityReason(c => c.addSingleTest)
      ?? 'Add Test is available only while the slurry is Ready to Test, Ongoing in Lab, or Sent To Lab Again.';
  }

  getSaveBatchHeaderBlockedReason(): string {
    return this.canSaveBatchHeader() ? '' : this.getBatchHeaderSaveBlockedReason();
  }

  canSaveBatchHeader(): boolean {
    return this.canEditSelectedBatchSetup() || this.canEditSelectedBatchTests();
  }

  getBatchHeaderSaveBlockedReason(): string {
    if (this.canEditSelectedBatchTests()) {
      return '';
    }

    return this.getBatchEditLockReason();
  }

  getSubmitSelectedBatchBlockedReason(): string {
    if (this.canSubmitSelectedBatch()) {
      return '';
    }
    if (this.batchSubmitInProgress) {
      return 'Submitting mix...';
    }
    if (!this.getCapabilityAllowed(c => c.editBatchExecution, !this.isParentProjectCompleted && this.slurryId > 0 && this.selectedBatchId != null && (this.statusId === 3 || this.statusId === 4))) {
      return this.getCapabilityReason(c => c.editBatchExecution) ?? this.getBatchEditLockReason();
    }
    if (this.selectedBatchId == null) {
      return 'Select a batch before submitting it.';
    }
    if (!(this.statusId === 3 || this.statusId === 4)) {
      return 'Submit Mix is available only while the slurry is In Lab or Test Failed.';
    }
    if (this.batchMeasurementsLoading) {
      return 'Submit Mix is disabled while saved measurements are loading.';
    }
    if (this.batchMeasurementsLoadError) {
      return this.batchMeasurementsLoadError;
    }
    if (this.persistedBatchMaterialsLocked) {
      return 'Submit Mix is disabled because this batch has already been submitted.';
    }
    const missingCommentReason = this.getMissingBatchMeasurementCommentReason();
    if (missingCommentReason) {
      return missingCommentReason;
    }
    if (!this.areAllBatchMaterialsMeasured()) {
      return 'Capture all required measured materials before submitting the batch.';
    }
    if (!this.isMixAsFoamSelectionReadyForSubmit()) {
      return 'Select at least one foamer before submitting the batch.';
    }
    if (!this.isMeasuredFoamReadyForSubmit()) {
      return 'Capture the required measured foam values before submitting the batch.';
    }
    if (!this.isMeasuredMudReadyForSubmit()) {
      return 'Capture measured mud before submitting the batch.';
    }
    return '';
  }

  getRedoSelectedBatchBlockedReason(): string {
    return this.canRedoSelectedBatch() ? '' : this.getBatchEditLockReason();
  }

  getRedoDryMixBlockedReason(): string {
    if (this.canRedoDryMix()) {
      return '';
    }
    if (this.persistedBatchMaterialsLocked) {
      return 'Start Over - Dry is disabled because this batch has already been submitted.';
    }
    if (!this.canRedoSelectedBatch()) {
      return this.getBatchEditLockReason();
    }
    return 'Start Over - Dry is unavailable.';
  }

  getRedoLiquidMixBlockedReason(): string {
    if (this.canRedoLiquidMix()) {
      return '';
    }
    if (this.persistedBatchMaterialsLocked) {
      return 'Start Over - Liquid is disabled because this batch has already been submitted.';
    }
    if (!this.canRedoSelectedBatch()) {
      return this.getBatchEditLockReason();
    }
    return 'Start Over - Liquid is unavailable.';
  }

  getRedoFoamMixBlockedReason(): string {
    if (this.canRedoFoamMix()) {
      return '';
    }
    if (!this.canRedoSelectedBatch()) {
      return this.getBatchEditLockReason();
    }
    return 'Redo Foam is available only after foamer or foam captures exist.';
  }

  getRedoMudMixBlockedReason(): string {
    if (this.canRedoMudMix()) {
      return '';
    }
    if (!this.canShowMudMeasurement()) {
      return 'Mud measurement is not available for the selected batch tests.';
    }
    if (!this.canRedoSelectedBatch()) {
      return this.getBatchEditLockReason();
    }
    return 'Redo Mud is available only after measured mud is captured.';
  }

  getPrintBatchBlockedReason(): string {
    return this.selectedBatch ? '' : 'Select a batch before printing it.';
  }

  onBlockedActionClick(
    reason: string | null | undefined,
    section: 'infoHeader' | 'infoNotes' | 'infoFooter' | 'tests' | 'mixability' | 'batch' | 'batchDetails'
  ): void {
    this.clearBlockedActionMessages();
    if (!reason) {
      return;
    }

    switch (section) {
      case 'infoHeader':
        this.blockedInfoHeaderActionMessage = reason;
        break;
      case 'infoNotes':
        this.blockedInfoNotesActionMessage = reason;
        break;
      case 'infoFooter':
        this.blockedInfoFooterActionMessage = reason;
        if (reason.includes('test validation errors')) {
          this.showTestValidationErrors = true;
          this.navigateToFirstInvalidTest();
        }
        break;
      case 'tests':
        this.blockedTestsActionMessage = reason;
        break;
      case 'batch':
        this.blockedBatchActionMessage = reason;
        break;
      case 'batchDetails':
        this.blockedBatchDetailsActionMessage = reason;
        break;
    }
  }

  private getTargetThickeningRangeError(): string | null {
    const from = this.parseTimeToMins(this.targetThickeningTimeFrom);
    const to = this.parseTimeToMins(this.targetThickeningTimeTo);
    if (from != null && to != null && to < from) {
      return 'Target Thickening Time To must be greater than or equal to From.';
    }
    return null;
  }

  sendToLab(): void {
    if (!this.slurryId || this.slurryId <= 0) {
      return;
    }

    const request = this.buildSaveRequest();
    if (!request) {
      return;
    }

    this.isLoading = true;
    this.saveMessage = '';
    this.saveTestsBeforePrimaryAction(() => {
      this.changeStatus('SendToLab', request);
    }, 'Send To Lab', false);
  }
  revertToDraft(): void { this.changeStatus('Revert'); }
  completeSlurry(): void { this.changeStatus('Complete'); }

  /** Calls the status-change endpoint and refreshes the local status. */
  private changeStatus(action: SlurryStatusAction, saveBeforeStatusRequest?: SaveSlurryRequestDto): void {
    if (!this.slurryId || this.slurryId <= 0) {
      return;
    }
    this.isLoading = true;
    this.saveMessage = '';
    this.slurryService.updateStatus(this.slurryId, action, saveBeforeStatusRequest).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.statusId = res.status;
        this.status = this.statusLabels[res.status] ?? this.status;
        this.saveMessage = `Status changed to ${res.statusName}.`;
        this.refreshSlurryAfterMutation();
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error changing status:', err);
        this.saveMessage = this.getApiErrorMessage(err, 'Failed to change status. Please try again.');
      }
    });
  }

  createFinalReport(): void {
    if (!this.projectId || this.slurryId <= 0 || !this.canCreateFinalReport()) {
      return;
    }

    this.slurryService.getBriefByProjectId(this.projectId).subscribe({
      next: (slurries: SlurryBriefDto[]) => {
        const selected = slurries.find(s => s.slurryID === this.slurryId);
        this.dialog.open(FinalReportDialogComponent, {
          width: 'fit-content',
          maxWidth: '96vw',
          maxHeight: '90vh',
          data: {
            projectId: this.projectId,
            projectTypeId: this.projectType,
            slurries: selected ? [selected] : slurries,
            serviceCompanyName: ''
          }
        });
      },
      error: (err) => {
        console.error('Failed to load slurries for final report:', err);
        alert('Failed to open Create Final Report.');
      }
    });
  }

  openAppendClearNoteDialog(): void {
    if (this.slurryId <= 0 || !this.canAppendClearNotes()) {
      return;
    }

    const dialogRef = this.dialog.open(AppendNoteDialogComponent, {
      width: '960px',
      disableClose: true,
      data: {
        mode: 'slurry',
        slurryId: this.slurryId,
        originalNote: this.notes1 ?? ''
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result || typeof result.updatedNotes !== 'string') {
        return;
      }
      this.notes1 = result.updatedNotes;
    });
  }

  openRowAppendClearNoteDialog(originalNote: string | null | undefined, applyUpdatedNote: (updatedNote: string) => void): void {
    const dialogRef = this.dialog.open(AppendNoteDialogComponent, {
      width: '960px',
      disableClose: true,
      data: {
        mode: 'local',
        originalNote: originalNote ?? ''
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result || typeof result.updatedNotes !== 'string') {
        return;
      }
      applyUpdatedNote(result.updatedNotes);
    });
  }

  private isPersistedTestInputMaster(testInputMasterId: number | null | undefined): testInputMasterId is number {
    return typeof testInputMasterId === 'number' && testInputMasterId > 0;
  }

  canOpenTestNoteActions(testInputMasterId: number | null | undefined): boolean {
    return this.isPersistedTestInputMaster(testInputMasterId) ? this.canAppendClearNotes() : this.canSaveTests();
  }

  canShowTestNoteAction(testInputMasterId: number | null | undefined): boolean {
    return this.isPersistedTestInputMaster(testInputMasterId) && this.canAppendClearNotes();
  }

  canShowTransientTestRowRemove(testInputMasterId: number | null | undefined): boolean {
    void testInputMasterId;
    return this.isTestsEditable;
  }

  private openPersistedTestNoteDialog(testInputMasterId: number, originalNote: string | null | undefined, applyUpdatedNote: (updatedNote: string) => void): void {
    if (this.slurryId <= 0 || !this.canAppendClearNotes()) {
      return;
    }

    const dialogRef = this.dialog.open(AppendNoteDialogComponent, {
      width: '960px',
      disableClose: true,
      data: {
        mode: 'test',
        slurryId: this.slurryId,
        testInputMasterId,
        originalNote: originalNote ?? ''
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result || typeof result.updatedNotes !== 'string') {
        return;
      }
      applyUpdatedNote(result.updatedNotes);
    });
  }

  openRowAppendClearNoteDialogForRow(row: TestNoteRow): void {
    const testInputMasterId = row.testInputMasterId ?? null;
    if (this.isPersistedTestInputMaster(testInputMasterId)) {
      this.openPersistedTestNoteDialog(testInputMasterId, row.notes, (updatedNote) => {
        row.notes = updatedNote;
      });
      return;
    }

    this.openRowAppendClearNoteDialog(row.notes, (updatedNote) => {
      row.notes = updatedNote;
    });
  }

  openLocalRowNotesDialog(_sectionTitle: string, row: TestNoteRow): void {
    this.openRowAppendClearNoteDialogForRow(row);
  }

  openThickeningNoteDialog(): void {
    if (this.isPersistedTestInputMaster(this.thickeningTestInputMasterId)) {
      this.openPersistedTestNoteDialog(this.thickeningTestInputMasterId, this.thickening.notes, (updatedNote) => {
        this.thickening.notes = updatedNote;
      });
      return;
    }

    this.openRowAppendClearNoteDialog(this.thickening.notes, (updatedNote) => {
      this.thickening.notes = updatedNote;
    });
  }

  // ── Tests tab state ──
  testsMessage = '';
  foamSettingsMessage = '';
  blockedInfoHeaderActionMessage = '';
  blockedInfoNotesActionMessage = '';
  blockedInfoFooterActionMessage = '';
  blockedTestsActionMessage = '';
  blockedBatchActionMessage = '';
  blockedBatchDetailsActionMessage = '';

  // Shared settings (mirrors WPF SlurryPropertiesTests shared props)
  testSettings = {
    initialTemperature: null as number | null,
    bhct:               null as number | null,
    temperatureRampTime: null as number | null,
    initialPressure:    null as number | null,
    pressureRampTime:   null as number | null,
    finalPressure:      null as number | null,
    bhst:               null as number | null,
    baseDensity:        null as number | null,
    foamDensity:        null as number | null
  };
  private previousBhctDefault: number | null = null;
  private previousBhstDefault: number | null = null;
  private previousFoamDensityDefault: number | null = null;

  onBhctChanged(nextBhct: number | null): void {
    const oldBhct = this.previousBhctDefault;
    this.testSettings.bhct = nextBhct;

    if (nextBhct === oldBhct) {
      return;
    }

    for (const r of this.rheologyTests) {
      const previousDefault = this.getRheologyDefaultTemperature(r.rheologyTypeId, oldBhct);
      if (r.temperature == null || (previousDefault != null && r.temperature === previousDefault)) {
        r.temperature = this.getRheologyDefaultTemperature(r.rheologyTypeId, nextBhct);
      }
    }
    for (const f of this.fluidLossTests) {
      if (f.temperature == null || (oldBhct != null && f.temperature === oldBhct)) {
        f.temperature = nextBhct;
      }
    }
    for (const f of this.freeFluidTests) {
      if (f.temperature == null || (oldBhct != null && f.temperature === oldBhct)) {
        f.temperature = nextBhct;
      }
    }
    for (const sg of this.staticGelTests) {
      if (sg.temperature == null || (oldBhct != null && sg.temperature === oldBhct)) {
        sg.temperature = nextBhct;
      }
    }

    this.previousBhctDefault = nextBhct;
  }

  onBhstChanged(nextBhst: number | null): void {
    const oldBhst = this.previousBhstDefault;
    this.testSettings.bhst = nextBhst;

    if (nextBhst === oldBhst) {
      return;
    }

    if (this.thickening.bhst == null || (oldBhst != null && this.thickening.bhst === oldBhst)) {
      this.thickening.bhst = nextBhst;
    }
    for (const cs of this.compStrTests) {
      if (cs.temperature == null || (oldBhst != null && cs.temperature === oldBhst)) {
        cs.temperature = nextBhst;
      }
    }
    for (const cs of this.foamCompStrTests) {
      if (cs.temperature == null || (oldBhst != null && cs.temperature === oldBhst)) {
        cs.temperature = nextBhst;
      }
    }

    this.previousBhstDefault = nextBhst;
  }

  onFoamDensityChanged(nextFoamDensity: number | null): void {
    const oldFoamDensity = this.previousFoamDensityDefault;
    this.testSettings.foamDensity = nextFoamDensity;

    if (nextFoamDensity === oldFoamDensity) {
      return;
    }

    for (const fs of this.foamStabilityTests) {
      if (fs.foamedDensity == null || (oldFoamDensity != null && fs.foamedDensity === oldFoamDensity)) {
        fs.foamedDensity = nextFoamDensity;
      }
    }

    this.previousFoamDensityDefault = nextFoamDensity;
  }

  // Thickening Time (single test)
  private readonly hesitationDefaultConditioningTime = 30;
  private readonly hesitationDefaultGradient = 0.2;
  private readonly shutdownDefaultMotorTime = 0;
  private readonly srtDefaultTime = 0;
  private readonly surfaceRheologyDefaultTemperature = 80;
  private readonly compressiveStrengthDefaultRampTime = 4;
  private readonly compressiveStrengthDefaultLengthOfTest = 48;

  thickeningSelected = false;
  thickening: ThickeningInputRow = this.createDefaultThickening();
  thickeningTestInputMasterId: number | null = null;
  thickeningOutputs: BatchTestBriefDto[] = [];
  rheologySelected = false;
  hphtSelected = false;
  fluidLossSelected = false;
  freeFluidSelected = false;
  staticGelSelected = false;
  compStrSelected = false;

  readonly rheologyTypeOptions: Array<{ id: number; label: string; }> = [
    { id: 1, label: 'Surface' },
    { id: 2, label: 'Downhole' }
  ];
  readonly bobSleeveTypeOptions: Array<{ id: number; label: string; }> = [
    { id: 1, label: 'R1, B1' },
    { id: 2, label: 'R1, B5' },
    { id: 3, label: 'Fysa' }
  ];
  readonly hphtEquipmentTypeOptions: Array<{ id: number; label: string; }> = [
    { id: 1, label: 'Fann 77' },
    { id: 2, label: 'Chann 7600' },
    { id: 3, label: 'Chann 7550' }
  ];
  readonly foamStabilityTypeOptions: Array<{ id: number; label: string; }> = [
    { id: 1, label: 'Slurry' },
    { id: 2, label: 'Set Cement' }
  ];
  readonly fluidLossTypeOptions: Array<{ id: number; label: string; }> = [
    { id: 1, label: 'Static' },
    { id: 2, label: 'Stirred' }
  ];
  readonly staticGelEquipmentTypeOptions: Array<{ id: number; label: string; }> = [
    { id: 1, label: 'SGSA' },
    { id: 2, label: 'MACS II' }
  ];
  readonly compressiveTypeOptions: Array<{ id: number; label: string; }> = [
    { id: 1, label: 'UCA' },
    { id: 2, label: 'Crush' },
    { id: 3, label: 'Conditioned UCA' }
  ];
  readonly crushTypeOptions: Array<{ id: number; label: string; }> = [
    { id: 1, label: 'Cube' },
    { id: 2, label: 'Cylinder' }
  ];

  private createDefaultThickening(): ThickeningInputRow {
    return {
      bhst: null, isHesitation: false, conditioningTime: this.hesitationDefaultConditioningTime, gradient: this.hesitationDefaultGradient,
      isShutdown: false, stopMotor: this.shutdownDefaultMotorTime, startMotor: this.shutdownDefaultMotorTime,
      hasSrt: false, srt: this.srtDefaultTime, contamination: false, notes: '', isMuted: false
    };
  }

  get thickeningPrimaryOutput(): BatchTestBriefDto | null {
    return this.getLatestOutput(this.thickeningOutputs);
  }

  private getLatestOutput(outputs: BatchTestBriefDto[] | null | undefined): BatchTestBriefDto | null {
    if (!outputs?.length) {
      return null;
    }

    return outputs.reduce((latest, current) =>
      current.testOutputMasterId > latest.testOutputMasterId ? current : latest
    );
  }

  formatScheduleDuration(value: number | null | undefined, emptyLabel = '--'): string {
    return value == null ? emptyLabel : `${value} min`;
  }

  formatScheduleTarget(value: number | null | undefined, unit: string, emptyLabel = '--'): string {
    return value == null ? emptyLabel : `${value} ${unit}`;
  }

  private formatThickeningScheduleValue(value: number | null | undefined, suffix = '', decimals?: number): string {
    if (value == null) {
      return '--';
    }

    const numericValue = Number(value);
    const rendered = decimals == null
      ? `${numericValue}`
      : numericValue.toFixed(decimals);
    return `${rendered}${suffix}`;
  }

  getThickeningScheduleSummary(): string {
    const initialTemperature = this.formatThickeningScheduleValue(this.testSettings.initialTemperature, '°F');
    const initialPressure = this.formatThickeningScheduleValue(this.testSettings.initialPressure, ' psi');
    const finalTemperature = this.formatThickeningScheduleValue(this.testSettings.bhct, '°F');
    const temperatureRampTime = this.formatThickeningScheduleValue(this.testSettings.temperatureRampTime, ' min');
    const finalPressure = this.formatThickeningScheduleValue(this.testSettings.finalPressure, ' psi');
    const pressureRampTime = this.formatThickeningScheduleValue(this.testSettings.pressureRampTime, ' min');

    const baseSummary = `Start at ${initialTemperature} and ${initialPressure}. Ramp temperature to ${finalTemperature} in ${temperatureRampTime}. Ramp pressure to ${finalPressure} in ${pressureRampTime}.`;

    if (this.thickening.isHesitation && this.thickening.isShutdown) {
      const stopMotor = this.formatThickeningScheduleValue(this.thickening.stopMotor, ' min');
      const shutdownDuration = this.thickening.stopMotor != null && this.thickening.startMotor != null
        ? this.formatThickeningScheduleValue(this.thickening.startMotor - this.thickening.stopMotor, ' min')
        : '--';
      const startMotor = this.formatThickeningScheduleValue(this.thickening.startMotor, ' min');
      const conditioningTime = this.formatThickeningScheduleValue(this.thickening.conditioningTime, ' min');
      const gradient = this.formatThickeningScheduleValue(this.thickening.gradient, '°F/min', 2);
      const hesitationBhst = this.formatThickeningScheduleValue(this.thickening.bhst ?? this.testSettings.bhst, '°F');
      return `${baseSummary} Hold pressure for duration. Shutdown motor after ${stopMotor} for ${shutdownDuration} then start motor back up at ${startMotor}. Hold temperature for ${conditioningTime} then begin hesitation cycles while increasing temperature at ${gradient} until reaching ${hesitationBhst}.`;
    }

    if (this.thickening.isHesitation) {
      const conditioningTime = this.formatThickeningScheduleValue(this.thickening.conditioningTime, ' min');
      const gradient = this.formatThickeningScheduleValue(this.thickening.gradient, '°F/min', 2);
      const hesitationBhst = this.formatThickeningScheduleValue(this.thickening.bhst ?? this.testSettings.bhst, '°F');
      return `${baseSummary} Hold pressure for duration. Hold temperature for ${conditioningTime} then begin hesitation cycles while increasing temperature at ${gradient} until reaching ${hesitationBhst}.`;
    }

    if (this.thickening.isShutdown) {
      const stopMotor = this.formatThickeningScheduleValue(this.thickening.stopMotor, ' min');
      const shutdownDuration = this.thickening.stopMotor != null && this.thickening.startMotor != null
        ? this.formatThickeningScheduleValue(this.thickening.startMotor - this.thickening.stopMotor, ' min')
        : '--';
      const startMotor = this.formatThickeningScheduleValue(this.thickening.startMotor, ' min');
      return `${baseSummary} Hold temperature and pressure for duration. Shutdown motor after ${stopMotor} for ${shutdownDuration} then start motor back up at ${startMotor}. Then run for duration.`;
    }

    return `${baseSummary} Hold temperature and pressure for duration of the test.`;
  }

  getThickeningTemperatureSchedule(): Array<{ action: string; duration: string; target: string; }> {
    return [
      { action: 'Step', duration: '--', target: this.formatScheduleTarget(this.testSettings.initialTemperature, 'F') },
      { action: 'Ramp', duration: this.formatScheduleDuration(this.testSettings.temperatureRampTime), target: this.formatScheduleTarget(this.thickening.bhst ?? this.testSettings.bhst, 'F') },
      { action: 'End', duration: 'Cont. Dwell', target: '--' }
    ];
  }

  getThickeningPressureSchedule(): Array<{ action: string; duration: string; target: string; }> {
    return [
      { action: 'Step', duration: '--', target: this.formatScheduleTarget(this.testSettings.initialPressure, 'psi') },
      { action: 'Ramp', duration: this.formatScheduleDuration(this.testSettings.pressureRampTime), target: this.formatScheduleTarget(this.testSettings.finalPressure, 'psi') },
      { action: 'End', duration: 'Cont. Dwell', target: '--' }
    ];
  }

  getThickeningMotorSchedule(): Array<{ action: string; duration: string; }> {
    if (this.thickening.isHesitation && this.thickening.isShutdown) {
      return [
        { action: 'On', duration: this.formatScheduleDuration(this.thickening.stopMotor) },
        {
          action: 'Off',
          duration: this.thickening.stopMotor != null && this.thickening.startMotor != null
            ? `${this.thickening.startMotor - this.thickening.stopMotor} min`
            : '--'
        },
        { action: 'On', duration: this.formatScheduleDuration(this.thickening.conditioningTime) },
        { action: 'Cycle', duration: 'Infinite' }
      ];
    }

    if (this.thickening.isHesitation) {
      const onDuration = this.testSettings.temperatureRampTime != null && this.thickening.conditioningTime != null
        ? `${sumFiniteNumbers([this.testSettings.temperatureRampTime, this.thickening.conditioningTime])} min`
        : '--';
      return [
        { action: 'On', duration: onDuration },
        { action: 'Cycle', duration: 'Infinite' }
      ];
    }

    if (this.thickening.isShutdown) {
      return [
        { action: 'On', duration: this.formatScheduleDuration(this.thickening.stopMotor) },
        {
          action: 'Off',
          duration: this.thickening.stopMotor != null && this.thickening.startMotor != null
            ? `${this.thickening.startMotor - this.thickening.stopMotor} min`
            : '--'
        },
        { action: 'On', duration: 'Infinite' }
      ];
    }

    return [
      { action: 'On', duration: 'Infinite' }
    ];
  }

  private getTestOutputScheduleDefaults(): TestOutputScheduleDefaults {
    return {
      finalTemperature: this.testSettings.bhct,
      finalPressure: this.testSettings.finalPressure,
      temperatureRampTime: this.testSettings.temperatureRampTime
    };
  }

  recordThickeningOutput(): void {
    this.openTestOutputDialog({
      mode: 'record',
      title: 'Thickening Time',
      slurryId: this.slurryId,
      testInputMasterId: this.thickeningTestInputMasterId,
      batches: this.batches,
      scheduleDefaults: this.getTestOutputScheduleDefaults(),
      slurryStatusId: this.statusId,
      isParentProjectCompleted: this.isParentProjectCompleted
    });
  }

  reviewThickeningOutput(output: BatchTestBriefDto | null = this.thickeningPrimaryOutput): void {
    if (!output) {
      return;
    }

    this.openTestOutputDialog({
      mode: 'review',
      title: 'Thickening Time',
      output,
      slurryId: this.slurryId,
      testInputMasterId: this.thickeningTestInputMasterId,
      batches: this.batches,
      scheduleDefaults: this.getTestOutputScheduleDefaults(),
      slurryStatusId: this.statusId,
      isParentProjectCompleted: this.isParentProjectCompleted
    });
  }

  reviewTestOutput(outputs: BatchTestBriefDto[] | null | undefined): void {
    this.openTestOutput(outputs, 'review');
  }

  recordTestOutput(outputs: BatchTestBriefDto[] | null | undefined): void {
    this.openTestOutput(outputs, 'record');
  }

  openTestBatch(outputs: BatchTestBriefDto[] | null | undefined): void {
    this.openTestOutput(outputs, 'batch');
  }

  handleOutputAction(context: SlurryOutputActionContext): void {
    this.openTestOutput(context.outputs, context.action);
  }

  private openGenericTestOutputDialog(
    mode: 'review' | 'record',
    outputs: BatchTestBriefDto[] | null | undefined,
    kind: GenericTestOutputKind,
    title: string,
    testInputMasterId: number | null,
    metadata?: GenericTestOutputMetadata
  ): void {
    const output = this.getLatestOutput(outputs);
    this.openTestOutputDialog({
      mode,
      output,
      slurryId: this.slurryId,
      testInputMasterId,
      batches: this.batches,
      kind,
      title,
      staticGelEquipmentTypeId: metadata?.staticGelEquipmentTypeId ?? null,
      compressiveStrengthTypeId: metadata?.compressiveStrengthTypeId ?? null,
      foamedStabilityTypeId: metadata?.foamedStabilityTypeId ?? null,
      rheologyIsSurface: metadata?.rheologyIsSurface,
      scheduleDefaults: this.getTestOutputScheduleDefaults(),
      testInputTemperature: metadata?.testInputTemperature ?? null,
      testInputPressure: metadata?.testInputPressure ?? null,
      slurryStatusId: this.statusId,
      isParentProjectCompleted: this.isParentProjectCompleted
    });
  }

  private openTestOutputDialog(data: ThickeningOutputDialogData): void {
    const dialogRef = this.dialog.open(ThickeningOutputDialogComponent, {
      width: '64vw',
      maxWidth: '95vw',
      disableClose: false,
      data
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.refresh) {
        this.refreshSlurryAfterMutation();
      }
    });
  }

  private resolveOutputDialogContext(outputs: BatchTestBriefDto[] | null | undefined): { kind: GenericTestOutputKind; title: string; testInputMasterId: number | null; metadata?: GenericTestOutputMetadata; } | null {
    const output = this.getLatestOutput(outputs);
    if (!output) {
      return null;
    }

    const testInputMasterId = output.testInputMasterId;

    if (this.thickeningTestInputMasterId === testInputMasterId) {
      return { kind: 'thickening', title: 'Thickening Time', testInputMasterId };
    }

    const fluidLossMatch = this.fluidLossTests.find(x => x.testInputMasterId === testInputMasterId);
    if (fluidLossMatch) {
      return {
        kind: 'fluidLoss',
        title: 'Fluid Loss',
        testInputMasterId,
        metadata: { testInputTemperature: fluidLossMatch.temperature }
      };
    }

    const freeFluidMatch = this.freeFluidTests.find(x => x.testInputMasterId === testInputMasterId);
    if (freeFluidMatch) {
      return {
        kind: 'freeFluid',
        title: 'Free Fluid',
        testInputMasterId,
        metadata: { testInputTemperature: freeFluidMatch.temperature }
      };
    }

    const rheologyMatch = this.rheologyTests.find(x => x.testInputMasterId === testInputMasterId);
    if (rheologyMatch) {
      return {
        kind: 'rheology',
        title: 'Rheology',
        testInputMasterId,
        metadata: {
          rheologyIsSurface: rheologyMatch.rheologyTypeId === 1,
          testInputTemperature: rheologyMatch.temperature
        }
      };
    }

    const staticGelMatch = this.staticGelTests.find(x => x.testInputMasterId === testInputMasterId);
    if (staticGelMatch) {
      return {
        kind: staticGelMatch.criticalStaticGelStrengthTypeId === 2 ? 'staticGelMacs' : 'staticGelSgsa',
        title: 'Critical Static Gel Strength',
        testInputMasterId,
        metadata: {
          staticGelEquipmentTypeId: staticGelMatch.criticalStaticGelStrengthTypeId,
          testInputTemperature: staticGelMatch.temperature,
          testInputPressure: staticGelMatch.finalHydrostaticPressure
        }
      };
    }

    const compressiveMatch = this.compStrTests.find(x => x.testInputMasterId === testInputMasterId);
    if (compressiveMatch) {
      return {
        kind: compressiveMatch.compressiveStrengthTypeId === 2 ? 'compressiveStrengthCrush' : 'compressiveStrengthUca',
        title: 'Compressive Strength',
        testInputMasterId,
        metadata: {
          compressiveStrengthTypeId: compressiveMatch.compressiveStrengthTypeId,
          testInputTemperature: compressiveMatch.temperature,
          testInputPressure: compressiveMatch.pressure
        }
      };
    }

    const foamStabilityMatch = this.foamStabilityTests.find(x => x.testInputMasterId === testInputMasterId);
    if (foamStabilityMatch) {
      return {
        kind: 'foamStability',
        title: 'Foam Stability',
        testInputMasterId,
        metadata: { foamedStabilityTypeId: foamStabilityMatch.foamedStabilityTypeId }
      };
    }

    const foamCompressiveMatch = this.foamCompStrTests.find(x => x.testInputMasterId === testInputMasterId);
    if (foamCompressiveMatch) {
      return {
        kind: foamCompressiveMatch.compressiveStrengthTypeId === 2 ? 'compressiveStrengthCrush' : 'compressiveStrengthUca',
        title: 'Foam Compressive Strength',
        testInputMasterId,
        metadata: {
          compressiveStrengthTypeId: foamCompressiveMatch.compressiveStrengthTypeId,
          testInputTemperature: foamCompressiveMatch.temperature,
          testInputPressure: foamCompressiveMatch.pressure
        }
      };
    }

    if (this.getCombinedOutputs(this.hphtTests.flatMap(x => x.readings)).some(x => x.testInputMasterId === testInputMasterId)) {
      return { kind: 'hphtRheology', title: 'HP/HT Rheology', testInputMasterId };
    }

    const otherConfigs: Array<{ title: string; rows: OtherTestRow[]; }> = [
      { title: 'Acid Solubility', rows: this.otherAcidSolubilityRows },
      { title: 'API Sedimentation', rows: this.otherApiSedimentationRows },
      { title: 'Expansion', rows: this.otherExpansionRows },
      { title: 'Pressurized Mud Scale', rows: this.otherPressurizedMudScaleRows },
      { title: 'Pycnometer', rows: this.otherPycnometerRows },
      { title: 'Shear Bond', rows: this.shearBondRows },
      { title: 'Splitting Tensile Strength', rows: this.otherSplittingTsRows },
      { title: 'Triaxial Confined Compressive Strength', rows: this.otherTriaxilConfinedCsRows },
      { title: 'Unconfined Compressive Strength', rows: this.otherUnconfinedCsRows },
      { title: 'Basic Crush', rows: this.otherBasicCrushRows }
    ];

    const otherMatch = otherConfigs.find(config => config.rows.some(row => row.testInputMasterId === testInputMasterId));
    if (otherMatch) {
      return { kind: 'other', title: otherMatch.title, testInputMasterId };
    }

    return { kind: 'other', title: 'Test Output', testInputMasterId };
  }

  getTestPrimaryOutput(outputs: BatchTestBriefDto[] | null | undefined): BatchTestBriefDto | null {
    return this.getLatestOutput(outputs);
  }

  openTestOutput(outputs: BatchTestBriefDto[] | null | undefined, action: SlurryOutputActionContext['action'] = 'batch'): void {
    const output = this.getLatestOutput(outputs);
    if (!output) {
      return;
    }

    const context = this.resolveOutputDialogContext(outputs);

    if (context && context.kind === 'thickening') {
      if (action === 'review') {
        this.reviewThickeningOutput();
        return;
      }

      if (action === 'record') {
        this.recordThickeningOutput();
        return;
      }
    }

    if (action === 'review') {
      if (context) {
        this.openGenericTestOutputDialog('review', outputs, context.kind, context.title, context.testInputMasterId, context.metadata);
        return;
      }
    }

    if (action === 'record') {
      if (context) {
        this.openGenericTestOutputDialog('record', outputs, context.kind, context.title, context.testInputMasterId, context.metadata);
        return;
      }
    }

    this.setTab('batches');
    this.openBatchDetails(output.slurryBatchId);
  }

  openBatchDetailsFromTestTab(batchId: number): void {
    this.setTab('batches');
    this.openBatchDetails(batchId);
  }

  openBatchDetails(batchId: number): void {
    void this.router.navigate(
      buildSlurryBatchDetailsCommands(this.navigationContext, this.slurryId, batchId),
      { state: this.getSlurryNavigationState() });
  }

  closeBatchDetails(): void {
    if (this.embedded) {
      this.clearSelectedBatch();
      return;
    }

    void this.router.navigate(
      buildSlurryDetailsCommands(this.navigationContext, this.slurryId),
      { state: this.getSlurryNavigationState() });
  }

  private getSlurryNavigationState(): {
    projectId: number | null;
    slurryUfid: string;
    projectName: string;
    projectType: number;
    redesign?: { comments?: string };
  } {
    return {
      projectId: this.projectId,
      slurryUfid: this.ufid,
      projectName: this.projectName ?? '',
      projectType: this.projectType ?? 1,
      redesign: this.notes1 ? { comments: this.notes1 } : undefined
    };
  }

  getTestAttachmentCount(testInputMasterId: number | null | undefined): number {
    if (!this.isPersistedTestInputMaster(testInputMasterId)) {
      return 0;
    }

    return this.testAttachmentCounts[testInputMasterId] ?? 0;
  }

  getTestAttachmentsLabel(testInputMasterId: number | null | undefined): string {
    return `View Attachments (${this.getTestAttachmentCount(testInputMasterId)})`;
  }

  getHpHtGroupAttachmentsLabel(group: HpHtTestRow): string {
    const count = this.getHpHtGroupTestInputMasterIds(group)
      .reduce((total, testInputMasterId) => total + this.getTestAttachmentCount(testInputMasterId), 0);
    return `View Attachments (${count})`;
  }

  canOpenTestAttachments(testInputMasterId: number | null | undefined): boolean {
    return this.slurryId > 0 && this.isPersistedTestInputMaster(testInputMasterId);
  }

  openTestAttachments(testInputMasterId: number | null | undefined, title = 'Test', testInputMasterIds?: number[]): void {
    if (!this.canOpenTestAttachments(testInputMasterId)) {
      return;
    }

    const dialogRef = this.dialog.open(TestAttachmentsDialogComponent, {
      width: '900px',
      maxHeight: '90vh',
      data: {
        testInputMasterId,
        title,
        testInputMasterIds,
        slurryId: this.slurryId
      }
    });

    dialogRef.afterClosed().subscribe(() => this.refreshTestAttachmentCounts());
  }

  private getRheologyDefaultTemperature(rheologyTypeId: number, bhct: number | null = this.testSettings.bhct): number | null {
    return rheologyTypeId === 1 ? this.surfaceRheologyDefaultTemperature : bhct;
  }

  private createDefaultHpHtReading(): HpHtReadingRow {
    return {
      temperature: null,
      pressure: null,
      totalTime: null,
      testInputMasterId: null,
      outputs: []
    };
  }

  private createDefaultHpHt(hpHtRheologyTypeId = 1): HpHtTestRow {
    return {
      hpHtRheologyTypeId,
      notes: '',
      isMuted: false,
      readings: [this.createDefaultHpHtReading()]
    };
  }

  private createDefaultRheology(rheologyTypeId = 1): RheologyRow {
    return {
      rheologyTypeId,
      bobSleeveTypeId: 1,
      temperature: this.getRheologyDefaultTemperature(rheologyTypeId),
      notes: '',
      isMuted: false,
      testInputMasterId: null,
      outputs: []
    };
  }

  onRheologyTypeChanged(row: RheologyRow): void {
    if (row.temperature == null ||
        row.temperature === this.surfaceRheologyDefaultTemperature ||
        row.temperature === this.testSettings.bhct) {
      row.temperature = this.getRheologyDefaultTemperature(row.rheologyTypeId);
    }
  }

  isCompressiveStrengthUca(row: CompStrRow): boolean {
    return row.compressiveStrengthTypeId === 1;
  }

  isCompressiveStrengthCrush(row: CompStrRow): boolean {
    return row.compressiveStrengthTypeId === 2;
  }

  isCompressiveStrengthFieldMissing(
    row: CompStrRow,
    field: 'type' | 'crushType' | 'rampTime' | 'pressure' | 'temperature' | 'lengthOfTest'
  ): boolean {
    switch (field) {
      case 'type':
        return !row.compressiveStrengthTypeId || !row.compressiveStrengthTestTypeId;
      case 'crushType':
        return this.isCompressiveStrengthCrush(row) && row.crushTestTypeId == null;
      case 'rampTime':
        return this.isCompressiveStrengthUca(row) && row.temperatureRampTime == null;
      case 'pressure':
        return row.pressure == null;
      case 'temperature':
        return row.temperature == null;
      case 'lengthOfTest':
        return this.isCompressiveStrengthCrush(row) && row.lengthOfTest == null;
    }
  }

  private getCompressiveStrengthMissingFields(row: CompStrRow): string[] {
    const fields: string[] = [];
    if (this.isCompressiveStrengthFieldMissing(row, 'type')) fields.push('Type');
    if (this.isCompressiveStrengthFieldMissing(row, 'crushType')) fields.push('Crush Type');
    if (this.isCompressiveStrengthFieldMissing(row, 'rampTime')) fields.push('Temperature Ramp Time');
    if (this.isCompressiveStrengthFieldMissing(row, 'pressure')) fields.push('Pressure');
    if (this.isCompressiveStrengthFieldMissing(row, 'temperature')) fields.push('Temperature');
    if (this.isCompressiveStrengthFieldMissing(row, 'lengthOfTest')) fields.push('Length of Test');
    return fields;
  }

  private navigateToFirstInvalidTest(): void {
    const validationError = this.getTestsValidationError();
    this.activeTab = validationError?.startsWith('Foam Tests') ? 'foam' : 'props';
  }

  private createDefaultCompStr(compressiveStrengthTypeId = 1, compressiveStrengthTestTypeId = 1): CompStrRow {
    const row: CompStrRow = {
      compressiveStrengthTypeId,
      compressiveStrengthTestTypeId,
      crushTestTypeId: null,
      contamination: false,
      temperatureRampTime: null,
      temperature: this.testSettings.bhst,
      pressure: null,
      lengthOfTest: null,
      notes: '',
      isMuted: false,
      testInputMasterId: null,
      outputs: []
    };
    this.applyCompressiveStrengthTypeDefaults(row);
    return row;
  }

  private createDefaultFoamStability(foamedStabilityTypeId = 1): FoamStabilityRow {
    return {
      foamedStabilityTypeId,
      foamedDensity: this.testSettings.foamDensity,
      notes: '',
      isMuted: false,
      testInputMasterId: null,
      outputs: []
    };
  }

  private createDefaultOtherTestRow(): OtherTestRow {
    return {
      notes: '',
      isMuted: false,
      testInputMasterId: null,
      outputs: []
    };
  }

  private createDefaultShearBondRow(): OtherTestRow {
    return {
      cureTimeHrs: null,
      notes: '',
      isMuted: false,
      testInputMasterId: null,
      outputs: []
    };
  }

  applyCompressiveStrengthTypeDefaults(row: CompStrRow): void {
    if (row.temperature == null) {
      row.temperature = this.testSettings.bhst;
    }

    if (this.isCompressiveStrengthUca(row)) {
      if (row.temperatureRampTime == null) {
        row.temperatureRampTime = this.compressiveStrengthDefaultRampTime;
      }
    } else {
      row.temperatureRampTime = null;
    }

    if (this.isCompressiveStrengthCrush(row)) {
      if (row.crushTestTypeId == null) {
        row.crushTestTypeId = 1;
      }
      if (row.lengthOfTest == null) {
        row.lengthOfTest = this.compressiveStrengthDefaultLengthOfTest;
      }
    } else {
      row.crushTestTypeId = null;
      row.lengthOfTest = null;
    }
  }

  onCompStrTypeChanged(row: CompStrRow): void {
    this.applyCompressiveStrengthTypeDefaults(row);
  }

  private applyHesitationDefaults(): void {
    if (!this.thickening.isHesitation) {
      return;
    }

    if (this.thickening.conditioningTime == null) {
      this.thickening.conditioningTime = this.hesitationDefaultConditioningTime;
    }
    if (this.thickening.gradient == null) {
      this.thickening.gradient = this.hesitationDefaultGradient;
    }
  }

  private applyShutdownDefaults(): void {
    if (!this.thickening.isShutdown) {
      return;
    }

    if (this.thickening.stopMotor == null) {
      this.thickening.stopMotor = this.shutdownDefaultMotorTime;
    }
    if (this.thickening.startMotor == null) {
      this.thickening.startMotor = this.shutdownDefaultMotorTime;
    }
  }

  private applySrtDefaults(): void {
    if (!this.thickening.hasSrt) {
      return;
    }

    if (this.thickening.srt == null) {
      this.thickening.srt = this.srtDefaultTime;
    }
  }

  onThickeningSelectedChanged(nextSelected: boolean): void {
    this.thickeningSelected = nextSelected;
    if (!nextSelected) {
      return;
    }

    if (this.thickening.bhst == null) {
      this.thickening.bhst = this.testSettings.bhst;
    }
    this.applyHesitationDefaults();
    this.applyShutdownDefaults();
    this.applySrtDefaults();
  }

  onThickeningHesitationChanged(isHesitation: boolean): void {
    this.thickening.isHesitation = isHesitation;
    this.applyHesitationDefaults();
  }

  onThickeningShutdownChanged(isShutdown: boolean): void {
    this.thickening.isShutdown = isShutdown;
    this.applyShutdownDefaults();
  }

  onThickeningSrtChanged(hasSrt: boolean): void {
    this.thickening.hasSrt = hasSrt;
    this.applySrtDefaults();
  }

  onRheologySelectedChanged(nextSelected: boolean): void {
    this.rheologySelected = nextSelected;
    if (nextSelected) {
      if (this.rheologyTests.length === 0) { this.addRheology(); }
      return;
    }
    this.rheologyTests = [];
  }

  onHphtSelectedChanged(nextSelected: boolean): void {
    this.hphtSelected = nextSelected;
    if (nextSelected) {
      if (this.hphtTests.length === 0) { this.addHpHt(); }
      return;
    }
    this.hphtTests = [];
  }

  onFluidLossSelectedChanged(nextSelected: boolean): void {
    this.fluidLossSelected = nextSelected;
    if (nextSelected) {
      if (this.fluidLossTests.length === 0) { this.addFluidLoss(); }
      return;
    }
    this.fluidLossTests = [];
  }

  onFreeFluidSelectedChanged(nextSelected: boolean): void {
    this.freeFluidSelected = nextSelected;
    if (nextSelected) {
      if (this.freeFluidTests.length === 0) { this.addFreeFluid(); }
      return;
    }
    this.freeFluidTests = [];
  }

  onStaticGelSelectedChanged(nextSelected: boolean): void {
    this.staticGelSelected = nextSelected;
    if (nextSelected) {
      if (this.staticGelTests.length === 0) { this.addStaticGel(); }
      return;
    }
    this.staticGelTests = [];
  }

  onCompStrSelectedChanged(nextSelected: boolean): void {
    this.compStrSelected = nextSelected;
    if (nextSelected) {
      if (this.compStrTests.length === 0) { this.addCompStr(); }
      return;
    }
    this.compStrTests = [];
  }

  // Test collections
  rheologyTests:   RheologyRow[]    = [];
  hphtTests:       HpHtTestRow[]    = [];
  fluidLossTests:  FluidLossRow[]   = [];
  freeFluidTests:  FreeFluidRow[]   = [];
  staticGelTests:  StaticGelRow[]   = [];
  compStrTests:    CompStrRow[]     = [];
  foamCompStrTests: CompStrRow[]    = [];
  foamStabilityTests: FoamStabilityRow[] = [];

  otherApiSedimentationRows: OtherTestRow[] = [];
  otherExpansionRows: OtherTestRow[] = [];
  otherPressurizedMudScaleRows: OtherTestRow[] = [];
  otherPycnometerRows: OtherTestRow[] = [];
  otherAcidSolubilityRows: OtherTestRow[] = [];
  otherSplittingTsRows: OtherTestRow[] = [];
  otherUnconfinedCsRows: OtherTestRow[] = [];
  otherTriaxilConfinedCsRows: OtherTestRow[] = [];
  otherBasicCrushRows: OtherTestRow[] = [];
  shearBondRows: OtherTestRow[] = [];

  // ── Batches tab state ──
  batches: SlurryBatchDto[] = [];
  batchesLoaded = false;
  batchMessage = '';
  batchToleranceSpecified = false;
  batchMixAsFoam = false;
  createBatchNotes = '';
  selectedBatchId: number | null = null;
  selectedBatch: SlurryBatchDto | null = null;
  batchDetailsMessage = '';
  selectedFoamerMaterialIds: number[] = [];
  selectedBatchTestInputMasterIds: number[] = [];
  private readonly batchDetailsLegacyTestOrder = [
    'Thickening Time',
    'Fluid Loss (Static)',
    'Free Fluid',
    'Rheology (Downhole)',
    'Rheology (Surface)',
    'Compressive Strength (UCA)'
  ];
  private readonly existingBatchesLegacyTestOrder = [
    'Thickening Time',
    'Fluid Loss',
    'Free Fluid',
    'Rheology',
    'HP/HT Rheology',
    'Static Gel Strength',
    'Compressive Strength',
    'Foam Stability',
    'Foam Compressive Strength',
    'Acid Solubility',
    'API Sedimentation',
    'Expansion',
    'Pressurized Mud Scale',
    'Pycnometer',
    'Shear Bond',
    'Splitting TS',
    'Triaxial Confined CS',
    'Unconfined CS',
    'Basic Crush'
  ];
  selectedBatchBlendMeasurement = false;
  batchHeader = {
    notes: '',
    batchLabVolume: null as number | null,
    foamDensity: null as number | null,
    measuredBlendWeight: null as number | null,
    measurementTolerance: null as number | null,
    excessFoamPercent: null as number | null,
    capturedBlendCisProcontainerId: '',
    replacePreferredBlendCisProcontainerId: false,
    blendContainerComments: '',
    blendWeightComments: '',
    measuredMudCisProcontainer: ''
  };
  measuredMaterials: MeasuredMaterialDto[] = [];
  measuredMaterialForm = {
    slurryMaterialId: null as number | null,
    measuredAmount: null as number | null,
    overrideAmount: null as number | null,
    caputuredCisProcontainerId: '',
    isFoamer: false,
    containerComments: '',
    weightComments: '',
    preferredCisProcontainerId: '',
    replacePreferredCisProcontainerId: false
  };
  measuredMaterialMessage = '';
  measuredFoam: MeasuredFoamDto | null = null;
  measuredFoamForm = {
    measuredWeight: null as number | null,
    cupWeight: null as number | null,
    cupVolume: null as number | null,
    weightComments: ''
  };
  measuredFoamMessage = '';
  measuredMud: MeasuredMudDto | null = null;
  measuredMudForm = {
    measuredMudCisProcontainer: '',
    mudContainerComments: '',
    preferredMudCisProcontainer: '',
    replacePreferredMudCisProcontainer: false
  };
  measuredMudMessage = '';
  measurementDialogOpen = false;
  measurementSessionDirty = false;
  batchSubmitInProgress = false;
  private persistedBatchMaterialsLocked = false;
  private batchMeasurementsLoading = false;
  private batchMeasurementsLoadError = '';
  private batchSelectionGeneration = 0;
  private batchMeasurementsLoadGeneration = 0;
  private batchLabVolumeBeforeMixAsFoam: number | null = null;

  @HostListener('window:beforeunload', ['$event'])
  protectMeasurementSessionOnUnload(event: BeforeUnloadEvent): void {
    if (!this.measurementSessionDirty) { return; }
    event.preventDefault();
    event.returnValue = '';
  }
  selectedTestMasterIds: number[] = [];
  allTestMasterIds: number[] = [];
  allTestInputs: Array<{ testInputMasterId: number; kind: string; displayLabel: string; hasOutputs: boolean; outputs: BatchTestBriefDto[]; }> = [];
  private printableBatchTestsById = new Map<number, PrintableBatchTestEntry>();
  singleTestMessage = '';
  selectedSingleTestTypeId: number | null = null;
  singleTestThickening = this.createDefaultThickening();
  singleTestRheology = this.createDefaultRheology();
  singleTestHpHt = this.createDefaultHpHt();
  singleTestFluidLoss: FluidLossRow = { fluidLossTypeId: 1, temperature: this.testSettings.bhct, notes: '', isMuted: false, testInputMasterId: null, outputs: [] };
  singleTestFreeFluid: FreeFluidRow = { angle: 45, temperature: this.testSettings.bhct, notes: '', isMuted: false, testInputMasterId: null, outputs: [] };
  singleTestStaticGel: StaticGelRow = { criticalStaticGelStrengthTypeId: 1, jpt: null, finalHydrostaticPressure: null, temperature: this.testSettings.bhct, csgs: 0, notes: '', isMuted: false, testInputMasterId: null, outputs: [] };
  singleTestCompStr = this.createDefaultCompStr(1, 1);
  singleTestFoamStability = this.createDefaultFoamStability();
  singleTestFoamCompStr = this.createDefaultCompStr(1, 2);
  singleTestOther = this.createDefaultOtherTestRow();
  private readonly notesOnlySingleTestTypeIds = new Set<number>([10, 11, 12, 13, 14, 15, 16, 17, 18, 19]);
  private readonly allSingleTestTypeOptions: SingleTestTypeOption[] = [
    { id: 1, label: 'Thickening Time', tab: 'props' },
    { id: 3, label: 'Free Fluid', tab: 'props' },
    { id: 2, label: 'Fluid Loss', tab: 'props' },
    { id: 4, label: 'Rheology', tab: 'props' },
    { id: 5, label: 'HP/HT Rheology', tab: 'props' },
    { id: 6, label: 'Static Gel Strength', tab: 'props' },
    { id: 20, label: 'Compressive Strength', tab: 'props' },
    { id: 8, label: 'Foam Stability', tab: 'foam' },
    { id: 22, label: 'Foam Compressive Strength', tab: 'foam' },
    { id: 15, label: 'Acid Solubility', tab: 'other' },
    { id: 12, label: 'API Sedimentation', tab: 'other' },
    { id: 13, label: 'Expansion', tab: 'other' },
    { id: 10, label: 'Pressurized Mud Scale', tab: 'other' },
    { id: 11, label: 'Pycnometer', tab: 'other' },
    { id: 14, label: 'Shear Bond', tab: 'other' },
    { id: 16, label: 'Basic Crush', tab: 'other' },
    { id: 17, label: 'Splitting TS', tab: 'other' },
    { id: 18, label: 'Unconfined CS', tab: 'other' },
    { id: 19, label: 'Triaxial Confined CS', tab: 'other' }
  ];

  get singleTestTypeOptions(): SingleTestTypeOption[] {
    return this.allSingleTestTypeOptions.filter(opt =>
      opt.id !== 1 || !this.thickeningSelected);
  }

  isSingleTestNotesOnlyType(testTypeId: number | null): boolean {
    return testTypeId != null && this.notesOnlySingleTestTypeIds.has(testTypeId);
  }

  toggleTestForBatch(masterId: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedTestMasterIds.includes(masterId)) { this.selectedTestMasterIds.push(masterId); }
    } else {
      this.selectedTestMasterIds = this.selectedTestMasterIds.filter(x => x !== masterId);
    }
  }

  getBatchTestSelectionBlockedReason(): string {
    return this.selectedTestMasterIds.length > 0 ? '' : 'Select at least one test before printing selected tests.';
  }

  getBatchTestDisplayLabel(testInputMasterId: number): string {
    return this.allTestInputs.find(x => x.testInputMasterId === testInputMasterId)?.displayLabel
      ?? `Test Input #${testInputMasterId}`;
  }

  getBatchTestsSummary(batch: SlurryBatchDto): string {
    const labels = batch.tests
      .slice()
      .sort((left, right) => this.getExistingBatchesLegacyTestOrder(left.testInputMasterId) - this.getExistingBatchesLegacyTestOrder(right.testInputMasterId))
      .map(test => this.getBatchTestDisplayLabel(test.testInputMasterId))
      .filter(label => label.length > 0);

    return labels.length > 0 ? labels.join('; ') : '--';
  }

  private getExistingBatchesLegacyTestOrder(testInputMasterId: number): number {
    const input = this.allTestInputs.find(test => test.testInputMasterId === testInputMasterId);
    const kindIndex = input ? this.existingBatchesLegacyTestOrder.indexOf(input.kind) : -1;
    const inputIndex = this.allTestInputs.findIndex(test => test.testInputMasterId === testInputMasterId);

    return (kindIndex >= 0 ? kindIndex : this.existingBatchesLegacyTestOrder.length) * 10_000
      + (inputIndex >= 0 ? inputIndex : testInputMasterId);
  }

  getSelectedBatchDisplayNumber(): string {
    if (this.selectedBatch?.batchNumber?.trim()) {
      return this.selectedBatch.batchNumber.trim();
    }

    return this.selectedBatchId != null ? `Batch #${this.selectedBatchId}` : 'Batch';
  }

  getSelectedBatchTests(): Array<{ testInputMasterId: number; displayLabel: string; selected: boolean; locked: boolean; statusLabel: string; }> {
    if (!this.selectedBatch) {
      return [];
    }

    const currentOutputsByTestId = new Map(this.selectedBatch.tests.map(test => [test.testInputMasterId, test]));
    const availableRows = this.allTestInputs.map(test => this.buildSelectedBatchTestRow(test.testInputMasterId, currentOutputsByTestId.get(test.testInputMasterId)));
    const missingSelectedRows = this.selectedBatch.tests
      .filter(test => !this.allTestInputs.some(input => input.testInputMasterId === test.testInputMasterId))
      .map(test => this.buildSelectedBatchTestRow(test.testInputMasterId, test));

    return this.sortSelectedBatchTests([...availableRows, ...missingSelectedRows]);
  }

  toggleSelectedBatchTest(testInputMasterId: number, checked: boolean): void {
    if (this.isSelectedBatchTestLocked(testInputMasterId)) {
      return;
    }

    if (checked) {
      if (!this.selectedBatchTestInputMasterIds.includes(testInputMasterId)) {
        this.selectedBatchTestInputMasterIds = [...this.selectedBatchTestInputMasterIds, testInputMasterId];
      }
      return;
    }

    this.selectedBatchTestInputMasterIds = this.selectedBatchTestInputMasterIds.filter(id => id !== testInputMasterId);
  }

  printAllBatchTests(): void {
    if (this.allTestInputs.length === 0) {
      return;
    }

    this.printBatchTestSheet('Print All Tests', this.collectPrintableBatchTests(false), false);
  }

  printSelectedBatchTests(): void {
    if (this.selectedTestMasterIds.length === 0) {
      return;
    }

    this.printBatchTestSheet('Print Selected Tests', this.collectPrintableBatchTests(true), false);
  }

  getHpHtGroupTestInputMasterId(group: HpHtTestRow): number | null {
    return this.getHpHtGroupTestInputMasterIds(group)[0] ?? null;
  }

  getHpHtGroupTestInputMasterIds(group: HpHtTestRow): number[] {
    return Array.from(new Set(group.readings
      .map(reading => reading.testInputMasterId)
      .filter((testInputMasterId): testInputMasterId is number => this.isPersistedTestInputMaster(testInputMasterId))));
  }

  private getOptionLabel(options: ReadonlyArray<{ id: number; label: string; }>, id?: number | null): string {
    if (id == null) {
      return '';
    }

    return options.find(option => option.id === id)?.label ?? '';
  }

  private buildBatchTestDisplayLabel(input: {
    kind: string;
    rheologyTypeId?: number | null;
    hpHtRheologyTypeId?: number | null;
    fluidLossTypeId?: number | null;
    criticalStaticGelStrengthTypeId?: number | null;
    compressiveStrengthTypeId?: number | null;
    foamedStabilityTypeId?: number | null;
  }, nextIndexByBaseLabel: Map<string, number>): string {
    let baseLabel = input.kind;
    let counterKey = baseLabel;

    if (input.kind === 'Rheology') {
      const rheologyTypeLabel = this.getOptionLabel(this.rheologyTypeOptions, input.rheologyTypeId);
      baseLabel = rheologyTypeLabel ? `Rheology (${rheologyTypeLabel})` : 'Rheology';
      counterKey = baseLabel;
    } else if (input.kind === 'HP/HT Rheology') {
      const equipmentLabel = this.getOptionLabel(this.hphtEquipmentTypeOptions, input.hpHtRheologyTypeId);
      baseLabel = equipmentLabel ? `HPHT Rheology (${equipmentLabel})` : 'HPHT Rheology';
      // Legacy uses one counter for both Chann models, while Fann 77 has its own.
      counterKey = input.hpHtRheologyTypeId === 1 ? baseLabel : 'HPHT Rheology (Chann)';
    } else if (input.kind === 'Fluid Loss') {
      const fluidLossTypeLabel = this.getOptionLabel(this.fluidLossTypeOptions, input.fluidLossTypeId);
      baseLabel = fluidLossTypeLabel ? `Fluid Loss (${fluidLossTypeLabel})` : 'Fluid Loss';
      counterKey = baseLabel;
    } else if (input.kind === 'Static Gel Strength') {
      const staticGelLabel = input.criticalStaticGelStrengthTypeId === 2 ? 'MACS' : 'SGSA';
      baseLabel = `Static Gel Strength (${staticGelLabel})`;
      counterKey = baseLabel;
    } else if (input.kind === 'Compressive Strength') {
      const compressiveTypeLabel = this.getOptionLabel(this.compressiveTypeOptions, input.compressiveStrengthTypeId);
      baseLabel = compressiveTypeLabel ? `Compressive Strength (${compressiveTypeLabel})` : 'Compressive Strength';
      counterKey = baseLabel;
    } else if (input.kind === 'Foam Compressive Strength') {
      const compressiveTypeLabel = this.getOptionLabel(this.compressiveTypeOptions, input.compressiveStrengthTypeId);
      baseLabel = compressiveTypeLabel ? `Foam Compressive Strength (${compressiveTypeLabel})` : 'Foam Compressive Strength';
      counterKey = baseLabel;
    } else if (input.kind === 'Foam Stability') {
      const foamStabilityTypeLabel = this.getOptionLabel(this.foamStabilityTypeOptions, input.foamedStabilityTypeId);
      const legacyFoamStabilityTypeLabel = foamStabilityTypeLabel === 'Set Cement' ? 'Cement' : foamStabilityTypeLabel;
      baseLabel = legacyFoamStabilityTypeLabel ? `Foam Stability (${legacyFoamStabilityTypeLabel})` : 'Foam Stability';
      counterKey = baseLabel;
    } else if (input.kind === 'API Sedimentation') {
      baseLabel = 'Api Sedimentation';
    } else if (input.kind === 'Pycnometer') {
      baseLabel = 'Pyconometer';
    }

    if (baseLabel === 'Thickening Time') {
      return baseLabel;
    }

    const nextIndex = (nextIndexByBaseLabel.get(counterKey) ?? 0) + 1;
    nextIndexByBaseLabel.set(counterKey, nextIndex);
    return `${baseLabel} # ${nextIndex}`;
  }

  private formatPrintableValue(value: unknown, emptyLabel = '--'): string {
    if (value == null) {
      return emptyLabel;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : emptyLabel;
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return String(value);
  }

  private toPrintableFields(fields: Array<{ label: string; value: unknown; emptyLabel?: string; }>): PrintableBatchTestField[] {
    return fields.map(field => ({
      label: field.label,
      value: this.formatPrintableValue(field.value, field.emptyLabel)
    }));
  }

  private appendPrintableNotesBlock(blocks: PrintableBatchTestBlock[], notes: string | null | undefined): void {
    const trimmed = notes?.trim();
    if (!trimmed) {
      return;
    }

    blocks.push({
      title: 'Notes',
      fields: [{ label: 'Text', value: trimmed }]
    });
  }

  private clonePrintableBlocks(blocks: PrintableBatchTestBlock[] | undefined): PrintableBatchTestBlock[] {
    return (blocks ?? []).map(block => ({
      title: block.title,
      fields: block.fields.map(field => ({ ...field }))
    }));
  }

  private buildPrintableBatchTestEntries(dto: SlurryTestInputDto, nextIndexByBaseLabel: Map<string, number>): PrintableBatchTestEntry[] {
    const entriesByKind = new Map<string, PrintableBatchTestEntry[]>();
    const pushEntry = (
      input: {
        testInputMasterId?: number | null;
        outputs?: BatchTestBriefDto[] | null;
        kind: string;
        rheologyTypeId?: number | null;
        hpHtRheologyTypeId?: number | null;
        fluidLossTypeId?: number | null;
        criticalStaticGelStrengthTypeId?: number | null;
        compressiveStrengthTypeId?: number | null;
        foamedStabilityTypeId?: number | null;
      },
      blocks: PrintableBatchTestBlock[],
      referenceBlocks?: PrintableBatchTestBlock[]
    ): void => {
      if (typeof input.testInputMasterId !== 'number' || input.testInputMasterId <= 0) {
        return;
      }

      const entry: PrintableBatchTestEntry = {
        testInputMasterId: input.testInputMasterId,
        kind: input.kind,
        displayLabel: this.buildBatchTestDisplayLabel(input, nextIndexByBaseLabel),
        outputs: input.outputs ?? [],
        blocks,
        referenceBlocks
      };
      const kindEntries = entriesByKind.get(input.kind) ?? [];
      kindEntries.push(entry);
      entriesByKind.set(input.kind, kindEntries);
    };

    for (const input of dto.rheologyInputs ?? []) {
      const blocks: PrintableBatchTestBlock[] = [{
        title: 'Basic Info',
        fields: this.toPrintableFields([
          { label: 'Rheology Type', value: this.getOptionLabel(this.rheologyTypeOptions, input.rheologyTypeId) },
          { label: 'Bob / Sleeve', value: this.getOptionLabel(this.bobSleeveTypeOptions, input.bobSleeveTypeId) },
          { label: 'Temperature (F)', value: input.temperature }
        ])
      }];
      this.appendPrintableNotesBlock(blocks, input.notes);
      pushEntry({ ...input, kind: 'Rheology' }, blocks);
    }

    for (const group of dto.hpHtRheologyTests ?? []) {
      for (const reading of group.readings ?? []) {
        const blocks: PrintableBatchTestBlock[] = [{
          title: 'Basic Info',
          fields: this.toPrintableFields([
            { label: 'Equipment', value: this.getOptionLabel(this.hphtEquipmentTypeOptions, group.hpHtRheologyTypeId) },
            { label: 'Temperature (F)', value: reading.temperature },
            { label: 'Pressure (psi)', value: reading.pressure },
            { label: 'Total Time (min)', value: reading.totalTime }
          ])
        }];
        this.appendPrintableNotesBlock(blocks, group.notes);
        pushEntry({ ...reading, kind: 'HP/HT Rheology', hpHtRheologyTypeId: group.hpHtRheologyTypeId }, blocks);
      }
    }

    for (const input of dto.fluidLossInputs ?? []) {
      const blocks: PrintableBatchTestBlock[] = [{
        title: 'Basic Info',
        fields: this.toPrintableFields([
          { label: 'Fluid Loss Type', value: this.getOptionLabel(this.fluidLossTypeOptions, input.fluidLossTypeId) },
          { label: 'Temperature (F)', value: input.temperature }
        ])
      }];
      this.appendPrintableNotesBlock(blocks, input.notes);
      pushEntry({ ...input, kind: 'Fluid Loss' }, blocks);
    }

    for (const input of dto.freeFluidInputs ?? []) {
      const blocks: PrintableBatchTestBlock[] = [{
        title: 'Basic Info',
        fields: this.toPrintableFields([
          { label: 'Angle', value: input.angle },
          { label: 'Temperature (F)', value: input.temperature }
        ])
      }];
      this.appendPrintableNotesBlock(blocks, input.notes);
      pushEntry({ ...input, kind: 'Free Fluid' }, blocks);
    }

    for (const input of dto.staticGelInputs ?? []) {
      const blocks: PrintableBatchTestBlock[] = [
        {
          title: 'Basic Info',
          fields: this.toPrintableFields([
            { label: 'Equipment', value: this.getOptionLabel(this.staticGelEquipmentTypeOptions, input.criticalStaticGelStrengthTypeId) },
            { label: 'JPT', value: input.jpt },
            { label: 'Final Hydrostatic Pressure', value: input.finalHydrostaticPressure },
            { label: 'Temperature (F)', value: input.temperature },
            { label: 'CSGS', value: input.csgs }
          ])
        },
        {
          title: 'Shared Settings',
          fields: this.toPrintableFields([
            { label: 'Initial Temperature (F)', value: this.testSettings.initialTemperature },
            { label: 'BHCT (F)', value: this.testSettings.bhct },
            { label: 'Initial Pressure (psi)', value: this.testSettings.initialPressure },
            { label: 'Final Pressure (psi)', value: this.testSettings.finalPressure }
          ])
        }
      ];
      this.appendPrintableNotesBlock(blocks, input.notes);
      pushEntry({ ...input, kind: 'Static Gel Strength' }, blocks);
    }

    for (const input of dto.compressiveStrengthInputs ?? []) {
      const blocks: PrintableBatchTestBlock[] = [{
        title: 'Basic Info',
        fields: this.toPrintableFields([
          { label: 'Test Type', value: this.getOptionLabel(this.compressiveTypeOptions, input.compressiveStrengthTypeId) },
          { label: 'Contamination', value: input.contamination },
          { label: 'Crush Type', value: this.getOptionLabel(this.crushTypeOptions, input.crushTestTypeId) },
          { label: 'Temperature Ramp Time (h)', value: input.temperatureRampTime },
          { label: 'Temperature (F)', value: input.temperature },
          { label: 'Pressure (psi)', value: input.pressure },
          { label: 'Length of Test (h)', value: input.lengthOfTest }
        ])
      }];
      this.appendPrintableNotesBlock(blocks, input.notes);
      pushEntry({ ...input, kind: 'Compressive Strength' }, blocks);
    }

    for (const input of dto.foamCompressiveStrengthInputs ?? []) {
      const blocks: PrintableBatchTestBlock[] = [{
        title: 'Basic Info',
        fields: this.toPrintableFields([
          { label: 'Test Type', value: this.getOptionLabel(this.compressiveTypeOptions, input.compressiveStrengthTypeId) },
          { label: 'Contamination', value: input.contamination },
          { label: 'Crush Type', value: this.getOptionLabel(this.crushTypeOptions, input.crushTestTypeId) },
          { label: 'Temperature Ramp Time (h)', value: input.temperatureRampTime },
          { label: 'Temperature (F)', value: input.temperature },
          { label: 'Pressure (psi)', value: input.pressure },
          { label: 'Length of Test (h)', value: input.lengthOfTest }
        ])
      }];
      this.appendPrintableNotesBlock(blocks, input.notes);
      pushEntry({ ...input, kind: 'Foam Compressive Strength' }, blocks);
    }

    for (const input of dto.foamStabilityInputs ?? []) {
      const blocks: PrintableBatchTestBlock[] = [{
        title: 'Basic Info',
        fields: this.toPrintableFields([
          { label: 'Foam Stability Type', value: this.getOptionLabel(this.foamStabilityTypeOptions, input.foamedStabilityTypeId) },
          { label: 'Foamed Density', value: input.foamedDensity }
        ])
      }];
      this.appendPrintableNotesBlock(blocks, input.notes);
      pushEntry({ ...input, kind: 'Foam Stability' }, blocks);
    }

    const pushNoteOnlyEntry = (
      kind: string,
      inputs: Array<{ testInputMasterId?: number | null; outputs?: BatchTestBriefDto[] | null; notes?: string | null; cureTimeHrs?: number | null; }> | null | undefined
    ): void => {
      for (const input of inputs ?? []) {
        const basicInfoFields = this.toPrintableFields([
          { label: 'Cure Time (hrs)', value: input.cureTimeHrs }
        ]).filter(field => field.value !== '--');
        const blocks: PrintableBatchTestBlock[] = [];
        if (basicInfoFields.length > 0) {
          blocks.push({
            title: 'Basic Info',
            fields: basicInfoFields
          });
        }
        this.appendPrintableNotesBlock(blocks, input.notes);
        pushEntry(
          { ...input, kind },
          blocks.length > 0
            ? blocks
            : [{ title: 'Notes', fields: [{ label: 'Text', value: this.formatPrintableValue(input.notes) }] }]
        );
      }
    };

    pushNoteOnlyEntry('API Sedimentation', dto.apiSedimentationInputs);
    pushNoteOnlyEntry('Expansion', dto.expansionInputs);
    pushNoteOnlyEntry('Pressurized Mud Scale', dto.pressurizedMudScaleInputs);
    pushNoteOnlyEntry('Pycnometer', dto.pycnometerInputs);
    pushNoteOnlyEntry('Shear Bond', dto.shearBondInputs);
    pushNoteOnlyEntry('Acid Solubility', dto.acidSolubilityInputs);
    pushNoteOnlyEntry('Splitting TS', dto.splittingTsInputs);
    pushNoteOnlyEntry('Unconfined CS', dto.unconfinedCsInputs);
    pushNoteOnlyEntry('Triaxial Confined CS', dto.triaxilConfinedCsInputs);
    pushNoteOnlyEntry('Basic Crush', dto.basicCrushTestInputs);

    if (dto.thickeningInput) {
      const input = dto.thickeningInput;
      const blocks: PrintableBatchTestBlock[] = [
        {
          title: 'Basic Info',
          fields: this.toPrintableFields([
            { label: 'Target Thickening Time From', value: this.targetThickeningTimeFrom },
            { label: 'Target Thickening Time To', value: this.targetThickeningTimeTo },
            { label: 'Contamination', value: input.contamination },
            { label: 'BHST', value: input.bhst }
          ])
        },
        {
          title: 'Shared Settings',
          fields: this.toPrintableFields([
            { label: 'Initial Temperature (F)', value: this.testSettings.initialTemperature },
            { label: 'BHCT (F)', value: this.testSettings.bhct },
            { label: 'Temperature Ramp Time (min)', value: this.testSettings.temperatureRampTime },
            { label: 'Initial Pressure (psi)', value: this.testSettings.initialPressure },
            { label: 'Pressure Ramp Time (min)', value: this.testSettings.pressureRampTime },
            { label: 'Final Pressure (psi)', value: this.testSettings.finalPressure }
          ])
        }
      ];

      if (input.isHesitation) {
        blocks.push({
          title: 'Hesitation SQ',
          fields: this.toPrintableFields([
            { label: 'Conditioning Time (min)', value: input.conditioningTime },
            { label: 'Gradient', value: input.gradient },
            { label: 'BHST', value: input.bhst }
          ])
        });
      }

      if (input.isShutdown) {
        blocks.push({
          title: 'Shutdown',
          fields: this.toPrintableFields([
            { label: 'Stop Motor (min)', value: input.stopMotor },
            { label: 'Start Motor (min)', value: input.startMotor }
          ])
        });
      }

      if (input.hasSrt) {
        blocks.push({
          title: 'SRT',
          fields: this.toPrintableFields([
            { label: 'SRT', value: input.srt }
          ])
        });
      }

      this.appendPrintableNotesBlock(blocks, input.notes);

      const referenceBlocks: PrintableBatchTestBlock[] = [{
        title: 'Basic Info',
        fields: this.toPrintableFields([
          { label: 'Target Thickening Time From', value: this.targetThickeningTimeFrom },
          { label: 'Target Thickening Time To', value: this.targetThickeningTimeTo },
          { label: 'BHST', value: input.bhst }
        ])
      }];
      this.appendPrintableNotesBlock(referenceBlocks, input.notes);

      pushEntry({ ...input, kind: 'Thickening Time' }, blocks, referenceBlocks);
    }

    const legacyOrder = [
      'Thickening Time',
      'Free Fluid',
      'Fluid Loss',
      'Rheology',
      'HP/HT Rheology',
      'Static Gel Strength',
      'Compressive Strength',
      'Foam Stability',
      'Foam Compressive Strength',
      'Acid Solubility',
      'API Sedimentation',
      'Expansion',
      'Pressurized Mud Scale',
      'Pycnometer',
      'Shear Bond',
      'Splitting TS',
      'Triaxial Confined CS',
      'Unconfined CS',
      'Basic Crush'
    ];

    const orderedEntries: PrintableBatchTestEntry[] = [];
    for (const kind of legacyOrder) {
      orderedEntries.push(...(entriesByKind.get(kind) ?? []));
      entriesByKind.delete(kind);
    }

    for (const kindEntries of entriesByKind.values()) {
      orderedEntries.push(...kindEntries);
    }

    return orderedEntries;
  }

  private collectPrintableBatchTests(selectedOnly: boolean): Array<PrintableBatchTestEntry & { isReference: boolean; }> {
    const selectedIds = new Set(this.selectedTestMasterIds);
    const printable: Array<PrintableBatchTestEntry & { isReference: boolean; }> = [];

    for (const test of this.allTestInputs) {
      const entry = this.printableBatchTestsById.get(test.testInputMasterId);
      if (!entry) {
        continue;
      }

      if (!selectedOnly || selectedIds.has(test.testInputMasterId)) {
        printable.push({
          ...entry,
          blocks: this.clonePrintableBlocks(entry.blocks),
          referenceBlocks: this.clonePrintableBlocks(entry.referenceBlocks),
          isReference: false
        });
        continue;
      }

      if (entry.kind === 'Thickening Time') {
        printable.push({
          ...entry,
          blocks: this.clonePrintableBlocks(entry.referenceBlocks?.length ? entry.referenceBlocks : entry.blocks),
          referenceBlocks: this.clonePrintableBlocks(entry.referenceBlocks),
          isReference: true
        });
      }
    }

    return printable;
  }

  private collectPrintableSelectedBatchTests(): Array<PrintableBatchTestEntry & { isReference: boolean; }> {
    if (!this.selectedBatch) {
      return [];
    }

    return this.getSelectedBatchTests().reduce<Array<PrintableBatchTestEntry & { isReference: boolean; }>>((acc, test) => {
        const entry = this.printableBatchTestsById.get(test.testInputMasterId);
        if (!entry) {
          return acc;
        }

        if (test.selected) {
          acc.push({
            ...entry,
            blocks: this.clonePrintableBlocks(entry.blocks),
            referenceBlocks: this.clonePrintableBlocks(entry.referenceBlocks),
            isReference: false
          });
          return acc;
        }

        if (entry.kind !== 'Thickening Time') {
          return acc;
        }

        acc.push({
          ...entry,
          blocks: this.clonePrintableBlocks(entry.referenceBlocks?.length ? entry.referenceBlocks : entry.blocks),
          referenceBlocks: this.clonePrintableBlocks(entry.referenceBlocks),
          isReference: true
        });
        return acc;
      }, []);
  }

  private resolveBaseMaterialName(materialId: string, options: MaterialLookup[]): string {
    const numericId = Number(materialId);
    return options.find(option => option.id === numericId)?.name ?? (materialId ? `Material #${materialId}` : '--');
  }

  private getPrintableDryMaterials(): Array<{ section: string; sectionType: string; name: string; cisPro: string; percent: number | null; weight: number | null; volume: number | null; labWeight: number | null; afterBlend: boolean; }> {
    const materials: Array<{ section: string; sectionType: string; name: string; cisPro: string; percent: number | null; weight: number | null; volume: number | null; labWeight: number | null; afterBlend: boolean; }> = [];

    if (this.blendBaseInitial.material) {
      materials.push({
        section: 'Cement Base',
        sectionType: 'CementBase',
        name: this.resolveBaseMaterialName(this.blendBaseInitial.material, this.blendBaseMaterials),
        cisPro: this.blendBaseInitial.cisPRO,
        percent: this.blendBaseInitial.percent,
        weight: this.blendBaseInitial.weightLb,
        volume: this.blendBaseInitial.volumeGal,
        labWeight: this.blendBaseInitial.labWeightG,
        afterBlend: false
      });
    }

    for (const row of this.blendBasePozRows) {
      materials.push({
        section: 'Poz Base',
        sectionType: 'PozBase',
        name: this.resolveBaseMaterialName(row.material, this.blendBaseMaterials),
        cisPro: row.cisPRO,
        percent: row.percent,
        weight: row.weightLb,
        volume: row.volumeGal,
        labWeight: row.labWeightG,
        afterBlend: false
      });
    }

    for (const row of this.blendAdditives) {
      materials.push({
        section: 'Blend Additive',
        sectionType: 'BlendAdditive',
        name: row.name || (row.additive ? `Material #${row.additive}` : '--'),
        cisPro: row.cisPRO,
        percent: row.percent,
        weight: row.weightLb,
        volume: row.volumeGal,
        labWeight: row.labWeightG,
        afterBlend: false
      });
    }

    if (this.isBlendTest) {
      materials.push({
        section: 'Blend Total',
        sectionType: 'BlendAdditive',
        name: 'Total',
        cisPro: this.blendCisProContainer,
        percent: null,
        weight: this.getPrimaryDryTotalWeight(),
        volume: this.getPrimaryDryTotalVolume(),
        labWeight: sumFiniteNumbers([
          this.blendBaseInitial.labWeightG,
          ...this.blendBasePozRows.map(row => row.labWeightG),
          ...this.blendAdditives.map(row => row.labWeightG)
        ]),
        afterBlend: false
      });
    }

    for (const row of this.additives) {
      materials.push({
        section: 'Additive',
        sectionType: 'Additive',
        name: row.name || (row.additive ? `Material #${row.additive}` : '--'),
        cisPro: row.cisPRO,
        percent: row.percent,
        weight: row.weightLb,
        volume: row.volumeGal,
        labWeight: row.labWeightG,
        afterBlend: true
      });
    }

    return materials;
  }

  private getPrintableLiquidMaterials(): Array<{ section: string; sectionType: string; name: string; cisPro: string; specificGravity: number | null; weight: number | null; volume: number | null; labWeight: number | null; }> {
    const materials: Array<{ section: string; sectionType: string; name: string; cisPro: string; specificGravity: number | null; weight: number | null; volume: number | null; labWeight: number | null; }> = [];

    if (this.liquidMaterials[0]?.liquid) {
      materials.push({
        section: 'Liquid Base',
        sectionType: 'LiquidBase',
        name: this.resolveBaseMaterialName(this.liquidMaterials[0].liquid, this.liquidBaseMaterials),
        cisPro: this.liquidMaterials[0].cisPRO,
        specificGravity: this.liquidMaterials[0].specificGravity,
        weight: this.liquidMaterials[0].weightLb,
        volume: this.liquidMaterials[0].volumeGal,
        labWeight: this.liquidMaterials[0].labWeightG
      });
    }

    for (let index = 1; index < this.liquidMaterials.length; index++) {
      const row = this.liquidMaterials[index];
      if (!row.liquid) {
        continue;
      }

      materials.push({
        section: 'Liquid Material',
        sectionType: 'LiquidBase',
        name: this.resolveBaseMaterialName(row.liquid, this.liquidBaseMaterials),
        cisPro: row.cisPRO,
        specificGravity: row.specificGravity,
        weight: row.weightLb,
        volume: row.volumeGal,
        labWeight: row.labWeightG
      });
    }

    for (const row of this.liquidAdditives) {
      materials.push({
        section: 'Liquid Additive',
        sectionType: 'LiquidAdditive',
        name: row.name || (row.additive ? `Material #${row.additive}` : '--'),
        cisPro: row.cisPRO,
        specificGravity: row.specificGravity,
        weight: row.weightLb,
        volume: row.volumeGal,
        labWeight: row.labWeightG
      });
    }

    for (const option of this.getFoamerBatchMaterialOptions()) {
      const sourceRow = this.liquidAdditives.find(row => row.additive === String(option.materialId))
        ?? this.liquidAdditives.find(row => row.name === option.materialName);

      materials.push({
        section: 'Foamer',
        sectionType: option.sectionType,
        name: option.materialName || sourceRow?.name || `Material #${option.materialId}`,
        cisPro: sourceRow?.cisPRO ?? option.preferredCisProcontainerId ?? '',
        specificGravity: sourceRow?.specificGravity ?? null,
        weight: sourceRow?.weightLb ?? null,
        volume: sourceRow?.volumeGal ?? null,
        labWeight: this.getMaterialTargetAmountForOption(option, true)
      });
    }

    return materials;
  }

  private openPrintWindow(width: number, height: number): Window | null {
    return window.open(window.location.href, '_blank', `width=${width},height=${height}`);
  }

  private writePrintWindow(popup: Window, html: string): void {
    let printTriggered = false;
    const triggerPrint = () => {
      if (printTriggered || popup.closed) {
        return;
      }

      printTriggered = true;
      popup.focus();
      popup.print();
    };

    popup.addEventListener('load', triggerPrint, { once: true });
    popup.document.open();
    popup.document.write(html);
    popup.document.close();
    window.setTimeout(triggerPrint, 300);
  }

  private loadBatches(): void {
    if (this.slurryId <= 0) { return; }
    this.slurryService.getBatches(this.slurryId).subscribe({
      next: (b) => {
        this.batches = b;
        this.batchesLoaded = true;
        if (this.selectedBatchId != null && !b.some(x => x.slurryBatchId === this.selectedBatchId)) {
          this.clearSelectedBatch();
        }
      },
      error: (err) => console.error('Failed to load batches:', err)
    });
  }

  selectBatch(batchId: number): void {
    if (this.slurryId <= 0) { return; }
    if (batchId !== this.selectedBatchId
      && this.measurementSessionDirty
      && !window.confirm('Discard measurements captured in this session and open another batch?')) {
      return;
    }
    const selectionGeneration = ++this.batchSelectionGeneration;
    this.selectedBatchId = batchId;
    this.batchDetailsMessage = '';
    this.measuredMaterialMessage = '';
    this.measuredFoamMessage = '';
    this.measuredMudMessage = '';
    this.clearMeasurementSessionDrafts();
    this.batchMeasurementsLoading = true;
    this.batchMeasurementsLoadError = '';
    this.persistedBatchMaterialsLocked = true;

    this.slurryService.getBatch(this.slurryId, batchId).subscribe({
      next: (batch) => {
        if (this.selectedBatchId !== batchId || this.batchSelectionGeneration !== selectionGeneration) { return; }
        this.applySelectedBatch(batch);
        this.loadSelectedBatchMeasurements(batchId);
      },
      error: (err) => {
        if (this.selectedBatchId !== batchId || this.batchSelectionGeneration !== selectionGeneration) { return; }
        console.error('Failed to load batch details:', err);
        this.batchDetailsMessage = this.getApiErrorMessage(err, 'Failed to load batch details.');
        this.clearSelectedBatch();
      }
    });
  }

  saveBatchHeader(): void {
    if (!this.canSaveBatchHeader() || this.selectedBatchId == null) { return; }
    const request: UpdateBatchRequestDto = {
      notes: this.batchHeader.notes || undefined,
      batchLabVolume: this.batchHeader.batchLabVolume ?? undefined,
      foamDensity: this.batchMixAsFoam ? (this.batchHeader.foamDensity ?? undefined) : undefined,
      measurementTolerance: this.batchToleranceSpecified ? (this.batchHeader.measurementTolerance ?? this.defaultBatchMeasurementTolerance) : undefined,
      excessFoamPercent: this.batchMixAsFoam ? (this.batchHeader.excessFoamPercent ?? undefined) : undefined,
      selectedFoamerMaterialIds: this.batchMixAsFoam ? [...this.selectedFoamerMaterialIds] : [],
      selectedTestInputMasterIds: [...this.selectedBatchTestInputMasterIds]
    };

    this.batchDetailsMessage = '';
    this.slurryService.updateBatch(this.slurryId, this.selectedBatchId, request).subscribe({
      next: (updated) => {
        const blendDraft = this.measurementSessionDirty ? {
          measuredBlendWeight: this.batchHeader.measuredBlendWeight,
          capturedBlendCisProcontainerId: this.batchHeader.capturedBlendCisProcontainerId,
          replacePreferredBlendCisProcontainerId: this.batchHeader.replacePreferredBlendCisProcontainerId,
          blendContainerComments: this.batchHeader.blendContainerComments,
          blendWeightComments: this.batchHeader.blendWeightComments,
          measuredMudCisProcontainer: this.batchHeader.measuredMudCisProcontainer
        } : null;
        this.applySelectedBatch(updated);
        if (blendDraft) {
          Object.assign(this.batchHeader, blendDraft);
        }
        this.batchDetailsMessage = 'Batch header saved.';
        this.loadBatches();
      },
      error: (err) => {
        console.error('Failed to update batch header:', err);
        this.batchDetailsMessage = this.getApiErrorMessage(err, 'Failed to update batch header.');
      }
    });
  }

  submitSelectedBatch(): void {
    if (!this.canSubmitSelectedBatch() || this.selectedBatchId == null) { return; }
    const batchId = this.selectedBatchId;
    const selectionGeneration = this.batchSelectionGeneration;
    const request = this.buildSubmitBatchRequest();
    this.batchSubmitInProgress = true;
    this.batchDetailsMessage = 'Submitting mix...';
    this.slurryService.submitBatch(this.slurryId, batchId, request).pipe(
      finalize(() => this.batchSubmitInProgress = false)
    ).subscribe({
      next: (result) => {
        if (this.selectedBatchId !== batchId || this.batchSelectionGeneration !== selectionGeneration) {
          this.loadBatches();
          return;
        }
        this.clearMeasurementSessionDrafts();
        this.applySelectedBatch(result.batch);
        this.loadSelectedBatchMeasurements(result.slurryBatchId);
        this.batchDetailsMessage = 'Mix submitted. Measurements saved.';
        this.loadBatches();
      },
      error: (err) => {
        if (this.selectedBatchId !== batchId || this.batchSelectionGeneration !== selectionGeneration) { return; }
        console.error('Failed to submit batch:', err);
        this.batchDetailsMessage = this.getApiErrorMessage(err, 'Failed to submit batch.');
      }
    });
  }

  cancelMeasurementSession(): void {
    if (this.selectedBatchId == null || this.measurementDialogOpen) { return; }
    if (!window.confirm('Cancel all measurements captured in this session? Saved batch data will not be changed.')) { return; }
    const batchId = this.selectedBatchId;
    this.slurryService.getBatch(this.slurryId, batchId).subscribe({
      next: batch => {
        this.clearMeasurementSessionDrafts();
        this.applySelectedBatch(batch);
        this.loadSelectedBatchMeasurements(batchId);
        this.batchDetailsMessage = 'Measurement session cancelled. No measurements were saved.';
      },
      error: err => {
        console.error('Failed to restore batch after cancelling measurements:', err);
        this.batchDetailsMessage = this.getApiErrorMessage(err, 'Failed to restore the saved batch state.');
      }
    });
  }

  private buildSubmitBatchRequest(): SubmitBatchRequestDto {
    return {
      batch: {
        notes: this.batchHeader.notes || undefined,
        batchLabVolume: this.batchHeader.batchLabVolume ?? undefined,
        foamDensity: this.batchMixAsFoam ? (this.batchHeader.foamDensity ?? undefined) : undefined,
        measuredBlendWeight: this.batchHeader.measuredBlendWeight ?? undefined,
        measurementTolerance: this.batchToleranceSpecified ? (this.batchHeader.measurementTolerance ?? this.defaultBatchMeasurementTolerance) : undefined,
        excessFoamPercent: this.batchMixAsFoam ? (this.batchHeader.excessFoamPercent ?? undefined) : undefined,
        capturedBlendCisProcontainerId: this.batchHeader.capturedBlendCisProcontainerId || undefined,
        replacePreferredBlendCisProcontainerId: this.batchHeader.replacePreferredBlendCisProcontainerId,
        blendContainerComments: this.batchHeader.blendContainerComments || undefined,
        blendWeightComments: this.batchHeader.blendWeightComments || undefined,
        measuredMudCisProcontainer: this.measuredMud?.measuredMudCisProcontainer || undefined,
        selectedFoamerMaterialIds: this.batchMixAsFoam ? [...this.selectedFoamerMaterialIds] : [],
        selectedTestInputMasterIds: [...this.selectedBatchTestInputMasterIds]
      },
      measuredMaterials: this.measuredMaterials
        .filter(material => {
          const option = this.batchMaterialOptions.find(candidate => candidate.slurryMaterialId === material.slurryMaterialId);
          return option != null
            && this.isRequiredBatchMaterialOption(option)
            && !this.isReadOnlyBlendComponent(option);
        })
        .map(material => ({
        slurryMaterialId: material.slurryMaterialId,
        measuredAmount: material.measuredAmount ?? undefined,
        overrideAmount: material.overrideAmount ?? undefined,
        caputuredCisProcontainerId: material.caputuredCisProcontainerId || undefined,
        isFoamer: material.isFoamer,
        containerComments: material.containerComments || undefined,
        weightComments: material.weightComments || undefined,
        preferredCisProcontainerId: material.preferredCisProcontainerId || undefined,
        replacePreferredCisProcontainerId: material.replacePreferredCisProcontainerId ?? false
      })),
      measuredFoam: this.measuredFoam ? {
        measuredWeight: this.measuredFoam.measuredWeight ?? undefined,
        cupWeight: this.measuredFoam.cupWeight ?? undefined,
        cupVolume: this.measuredFoam.cupVolume ?? undefined,
        weightComments: this.measuredFoam.weightComments || undefined
      } : undefined,
      measuredMud: this.measuredMud?.measuredMudCisProcontainer ? {
        measuredMudCisProcontainer: this.measuredMud.measuredMudCisProcontainer,
        mudContainerComments: this.measuredMud.mudContainerComments || undefined,
        preferredMudCisProcontainer: this.measuredMud.preferredMudCisProcontainer || undefined,
        replacePreferredMudCisProcontainer: this.measuredMud.replacePreferredMudCisProcontainer ?? false
      } : undefined
    };
  }

  private applyMaterialMeasurementDraft(request: UpsertMeasuredMaterialRequestDto): void {
    const previous = this.getMeasuredMaterialRecord(request.slurryMaterialId);
    const draft: MeasuredMaterialDto = {
      measuredMaterialId: previous?.measuredMaterialId ?? 0,
      slurryBatchId: this.selectedBatchId as number,
      slurryMaterialId: request.slurryMaterialId,
      measuredAmount: request.measuredAmount ?? null,
      overrideAmount: request.overrideAmount ?? null,
      caputuredCisProcontainerId: request.caputuredCisProcontainerId ?? '',
      caputuredCisProcontainerDeleted: previous?.caputuredCisProcontainerDeleted ?? false,
      isFoamer: request.isFoamer,
      containerComments: request.containerComments ?? null,
      weightComments: request.weightComments ?? null,
      preferredCisProcontainerId: request.preferredCisProcontainerId ?? null,
      preferredCisProcontainerDeleted: previous?.preferredCisProcontainerDeleted ?? false,
      replacePreferredCisProcontainerId: request.replacePreferredCisProcontainerId ?? false
    };
    this.measuredMaterials = [...this.measuredMaterials.filter(item => item.slurryMaterialId !== request.slurryMaterialId), draft];
    this.measurementSessionDirty = true;
    this.measuredMaterialMessage = 'Measurement captured locally. Submit Mix will save it.';
  }

  private applyBlendMeasurementDraft(request: UpdateBatchRequestDto): void {
    this.batchHeader.measuredBlendWeight = request.measuredBlendWeight ?? null;
    this.batchHeader.capturedBlendCisProcontainerId = request.capturedBlendCisProcontainerId ?? '';
    this.batchHeader.blendContainerComments = request.blendContainerComments ?? '';
    this.batchHeader.blendWeightComments = request.blendWeightComments ?? '';
    this.batchHeader.replacePreferredBlendCisProcontainerId = request.replacePreferredBlendCisProcontainerId ?? false;
    this.measurementSessionDirty = true;
    this.measuredMaterialMessage = 'Blend measurement captured locally. Submit Mix will save it.';
  }

  private applyMudMeasurementDraft(request: UpsertMeasuredMudRequestDto): void {
    this.measuredMud = {
      slurryBatchId: this.selectedBatchId as number,
      measuredMudCisProcontainer: request.measuredMudCisProcontainer ?? null,
      mudContainerComments: request.mudContainerComments ?? null,
      preferredMudCisProcontainer: request.preferredMudCisProcontainer ?? null,
      preferredMudCisProcontainerDeleted: this.measuredMud?.preferredMudCisProcontainerDeleted ?? false,
      measuredMudCisProcontainerDeleted: this.measuredMud?.measuredMudCisProcontainerDeleted ?? false,
      replacePreferredMudCisProcontainer: request.replacePreferredMudCisProcontainer ?? false
    };
    this.measuredMudForm = {
      measuredMudCisProcontainer: request.measuredMudCisProcontainer ?? '',
      mudContainerComments: request.mudContainerComments ?? '',
      preferredMudCisProcontainer: request.preferredMudCisProcontainer ?? '',
      replacePreferredMudCisProcontainer: request.replacePreferredMudCisProcontainer ?? false
    };
    this.measurementSessionDirty = true;
    this.measuredMudMessage = 'Mud measurement captured locally. Submit Mix will save it.';
  }

  private applyFoamMeasurementDraft(request: UpsertMeasuredFoamRequestDto): void {
    this.measuredFoam = {
      measuredFoamId: this.measuredFoam?.measuredFoamId ?? 0,
      slurryBatchId: this.selectedBatchId as number,
      measuredWeight: request.measuredWeight ?? null,
      cupWeight: request.cupWeight ?? null,
      cupVolume: request.cupVolume ?? null,
      weightComments: request.weightComments ?? null
    };
    this.measuredFoamForm = {
      measuredWeight: request.measuredWeight ?? null,
      cupWeight: request.cupWeight ?? null,
      cupVolume: request.cupVolume ?? null,
      weightComments: request.weightComments ?? ''
    };
    this.measurementSessionDirty = true;
    this.measuredFoamMessage = 'Foam measurement captured locally. Submit Mix will save it.';
  }

  private clearMeasurementSessionDrafts(): void {
    this.measuredMaterials = [];
    this.measuredFoam = null;
    this.measuredMud = null;
    this.measurementSessionDirty = false;
  }

  redoSelectedBatch(): void {
    if (!this.canRedoSelectedBatch() || this.selectedBatchId == null) { return; }
    if (!this.confirmBatchReset('Redo Batch', 'This will clear all captured measurements for the batch and reopen it.')) {
      return;
    }
    this.batchDetailsMessage = '';
    this.slurryService.redoBatch(this.slurryId, this.selectedBatchId).subscribe({
      next: (result) => {
        this.applySelectedBatch(result.batch);
        this.batchDetailsMessage = `Batch reopened. Updated outputs: ${result.updatedOutputsCount}.`;
        this.loadSelectedBatchMeasurements(this.selectedBatchId as number);
        this.loadBatches();
      },
      error: (err) => {
        console.error('Failed to redo batch:', err);
        this.batchDetailsMessage = this.getApiErrorMessage(err, 'Failed to redo batch.');
      }
    });
  }

  redoDryMix(): void {
    if (!this.canRedoDryMix() || !this.confirmBatchReset('Start Over - Dry', 'This will clear captured dry-mix measurements for the selected batch.')) {
      return;
    }

    const dryMaterialIds = new Set(this.getDryMeasuredMaterials().map(material => material.slurryMaterialId));
    this.measuredMaterials = this.measuredMaterials.filter(material => !dryMaterialIds.has(material.slurryMaterialId));
    if (this.selectedBatch?.isBlendTest === true || this.isBlendTest) {
      this.batchHeader.measuredBlendWeight = null;
      this.batchHeader.capturedBlendCisProcontainerId = '';
      this.batchHeader.replacePreferredBlendCisProcontainerId = false;
    }

    if (this.isBatchMeasurementSectionActive('dry')) {
      this.onMeasuredMaterialSelectionChange();
    }

    this.measurementSessionDirty = this.hasAnyMeasurementSessionDraft();
    this.measuredMaterialMessage = '';
    this.batchDetailsMessage = 'Dry mix measurements cleared locally.';
  }

  redoLiquidMix(): void {
    if (!this.canRedoLiquidMix() || !this.confirmBatchReset('Start Over - Liquid', 'This will clear captured liquid-mix measurements for the selected batch.')) {
      return;
    }

    const liquidMaterialIds = new Set(this.getLiquidMeasuredMaterials().map(material => material.slurryMaterialId));
    this.measuredMaterials = this.measuredMaterials.filter(material => !liquidMaterialIds.has(material.slurryMaterialId));

    if (this.isBatchMeasurementSectionActive('liquid')) {
      this.onMeasuredMaterialSelectionChange();
    }

    this.measurementSessionDirty = this.hasAnyMeasurementSessionDraft();
    this.measuredMaterialMessage = '';
    this.batchDetailsMessage = 'Liquid mix measurements cleared locally.';
  }

  redoFoamMix(): void {
    if (!this.canRedoFoamMix() || !this.confirmBatchReset('Start Over - Foam', 'This will clear captured foam measurements for the selected batch.')) {
      return;
    }
    this.redoSelectedBatchSection(
      () => this.slurryService.redoFoamMix(this.slurryId, this.selectedBatchId as number),
      'Foam measurements cleared.'
    );
  }

  redoMudMix(): void {
    if (!this.canRedoMudMix() || !this.confirmBatchReset('Start Over - Mud', 'This will clear the captured mud measurement for the selected batch.')) {
      return;
    }
    this.redoSelectedBatchSection(
      () => this.slurryService.redoMudMix(this.slurryId, this.selectedBatchId as number),
      'Mud measurements cleared.'
    );
  }

  saveMeasuredMaterial(): void {
    if (!this.canSaveMeasuredMaterial() || this.selectedBatchId == null || this.measuredMaterialForm.slurryMaterialId == null) {
      return;
    }

    const request: UpsertMeasuredMaterialRequestDto = {
      slurryMaterialId: this.measuredMaterialForm.slurryMaterialId,
      measuredAmount: this.measuredMaterialForm.measuredAmount ?? undefined,
      overrideAmount: this.measuredMaterialForm.overrideAmount ?? undefined,
      caputuredCisProcontainerId: this.measuredMaterialForm.caputuredCisProcontainerId || undefined,
      isFoamer: this.batchMixAsFoam && this.measuredMaterialForm.isFoamer,
      containerComments: this.measuredMaterialForm.containerComments || undefined,
      weightComments: this.measuredMaterialForm.weightComments || undefined,
      preferredCisProcontainerId: this.measuredMaterialForm.preferredCisProcontainerId || undefined,
      replacePreferredCisProcontainerId: this.measuredMaterialForm.replacePreferredCisProcontainerId
    };

    this.applyMaterialMeasurementDraft(request);
  }

  saveMeasuredFoam(): void {
    if (!this.canSaveMeasuredFoam() || this.selectedBatchId == null) { return; }

    const request: UpsertMeasuredFoamRequestDto = {
      measuredWeight: this.measuredFoamForm.measuredWeight ?? undefined,
      cupWeight: this.measuredFoamForm.cupWeight ?? undefined,
      cupVolume: this.measuredFoamForm.cupVolume ?? undefined,
      weightComments: this.measuredFoamForm.weightComments || undefined
    };

    this.applyFoamMeasurementDraft(request);
  }

  saveMeasuredMud(): void {
    if (!this.canSaveMeasuredMud() || this.selectedBatchId == null) { return; }

    const request: UpsertMeasuredMudRequestDto = {
      measuredMudCisProcontainer: this.measuredMudForm.measuredMudCisProcontainer || undefined,
      mudContainerComments: this.measuredMudForm.mudContainerComments || undefined,
      preferredMudCisProcontainer: this.measuredMudForm.preferredMudCisProcontainer || undefined,
      replacePreferredMudCisProcontainer: this.measuredMudForm.replacePreferredMudCisProcontainer
    };

    this.applyMudMeasurementDraft(request);
  }

  canEditSelectedBatch(): boolean {
    if (this.batchMeasurementsLoading || this.batchMeasurementsLoadError) {
      return false;
    }

    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && this.selectedBatchId != null && (this.statusId === 3 || this.statusId === 4);
    if (!this.getCapabilityAllowed(c => c.editBatchExecution, fallback)) {
      return false;
    }
    return this.selectedBatchId != null && this.getSelectedBatchEditableOutputCount() > 0;
  }

  canEditSelectedBatchSetup(): boolean {
    return this.canEditSelectedBatch() && !this.hasLockedBatchSetupMeasurements();
  }

  canEditSelectedBatchTests(): boolean {
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && this.selectedBatchId != null && (this.statusId === 3 || this.statusId === 4);
    if (!this.getCapabilityAllowed(c => c.editBatchExecution, fallback)) {
      return false;
    }

    if (this.selectedBatchId == null || this.isParentProjectCompleted) {
      return false;
    }

    // Legacy BatchTestsEdit remains available after measurements are captured.
    // Individual tests with progressed outputs are still protected by isSelectedBatchTestLocked().
    return this.statusId === 3 || this.statusId === 4;
  }

  canSubmitSelectedBatch(): boolean {
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && this.selectedBatchId != null && (this.statusId === 3 || this.statusId === 4);
    return this.getCapabilityAllowed(c => c.editBatchExecution, fallback)
      && this.selectedBatchId != null
      && (this.statusId === 3 || this.statusId === 4)
      && !this.batchSubmitInProgress
      && !this.batchMeasurementsLoading
      && !this.batchMeasurementsLoadError
      && !this.persistedBatchMaterialsLocked
      && this.areAllBatchMaterialsMeasured()
      && this.isMixAsFoamSelectionReadyForSubmit()
      && this.isMeasuredFoamReadyForSubmit()
      && (!this.canShowMudMeasurement() || this.isMeasuredMudReadyForSubmit());
  }

  canRedoSelectedBatch(): boolean {
    return this.canEditSelectedBatch();
  }

  canRedoDryMix(): boolean {
    return this.canRedoSelectedBatch() && !this.persistedBatchMaterialsLocked;
  }

  canRedoLiquidMix(): boolean {
    return this.canRedoSelectedBatch() && !this.persistedBatchMaterialsLocked;
  }

  canRedoFoamMix(): boolean {
    return this.canRedoSelectedBatch() && (this.getFoamerMeasuredMaterials().length > 0 || this.hasMeasuredFoamWeight() || this.hasMeasuredFoamCup() || !!this.batchHeader.capturedBlendCisProcontainerId || this.batchHeader.measuredBlendWeight != null);
  }

  canRedoMudMix(): boolean {
    return this.canShowMudMeasurement() && this.canRedoSelectedBatch() && this.hasMeasuredMudCapture();
  }

  canCreateBatch(): boolean {
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && (this.statusId === 2 || this.statusId === 3 || this.statusId === 7);
    return this.getCapabilityAllowed(c => c.createBatch, fallback);
  }

  getSelectedMeasuredMaterial(): MeasuredMaterialDto | null {
    const selectedId = this.measuredMaterialForm.slurryMaterialId;
    if (selectedId == null) {
      return null;
    }
    return this.measuredMaterials.find(x => x.slurryMaterialId === selectedId) ?? null;
  }

  canSaveMeasuredMaterial(): boolean {
    return this.canEditSelectedBatch()
      && this.selectedBatchId != null
      && this.measuredMaterialForm.slurryMaterialId != null
      && this.isMeasuredMaterialFormValid();
  }

  getMeasuredMaterialLockReason(): string {
    if (!this.canEditSelectedBatch()) {
      return this.getBatchEditLockReason();
    }
    if (this.measuredMaterialForm.isFoamer && !this.batchMixAsFoam) {
      return 'Enable Mix as Foam before saving foamer measurements.';
    }
    return this.getSelectedMeasuredMaterial()
      ? 'Measured values are locked after capture. Comment fields remain editable until the batch is submitted.'
      : '';
  }

  hasMeasuredFoamWeight(): boolean {
    return this.measuredFoam?.measuredWeight != null;
  }

  hasMeasuredFoamCup(): boolean {
    return this.measuredFoam?.cupWeight != null || this.measuredFoam?.cupVolume != null;
  }

  canEditMeasuredFoamWeight(): boolean {
    return this.batchMixAsFoam && this.canEditSelectedBatch() && !this.hasMeasuredFoamWeight();
  }

  canEditMeasuredFoamCup(): boolean {
    return this.batchMixAsFoam && this.canEditSelectedBatch() && !this.hasMeasuredFoamCup();
  }

  canEditMeasuredFoamComments(): boolean {
    return this.batchMixAsFoam && this.canEditSelectedBatch();
  }

  canSaveMeasuredFoam(): boolean {
    return this.canSaveMeasuredFoamWeight() || this.canSaveMeasuredFoamCup();
  }

  canSaveMeasuredFoamWeight(): boolean {
    return this.batchMixAsFoam
      && this.canEditMeasuredFoamComments()
      && this.isMeasuredFoamFormValid()
      && (this.measuredFoamForm.measuredWeight != null || this.hasMeasuredFoamWeight());
  }

  canSaveMeasuredFoamCup(): boolean {
    return this.batchMixAsFoam
      && this.canEditMeasuredFoamComments()
      && this.isMeasuredFoamFormValid()
      && ((this.measuredFoamForm.cupWeight != null && this.measuredFoamForm.cupVolume != null) || this.hasMeasuredFoamCup());
  }

  getMeasuredFoamLockReason(): string {
    if (!this.batchMixAsFoam) {
      return 'Enable Mix as Foam to capture foam measurements.';
    }
    if (!this.canEditSelectedBatch()) {
      return this.getBatchEditLockReason();
    }
    if (this.hasMeasuredFoamWeight() && this.hasMeasuredFoamCup()) {
      return 'Measured foam values are locked after capture. Weight comments remain editable until the batch is submitted.';
    }
    if (this.hasMeasuredFoamWeight()) {
      return 'Measured foam weight is already captured. Only comments and the remaining foam cup fields stay open until submit.';
    }
    if (this.hasMeasuredFoamCup()) {
      return 'Measured foam cup values are already captured. Only comments and the remaining foam weight fields stay open until submit.';
    }
    return '';
  }

  hasMeasuredMudCapture(): boolean {
    return !!this.measuredMud?.measuredMudCisProcontainer;
  }

  canSaveMeasuredMud(): boolean {
    return this.canShowMudMeasurement()
      && this.canEditSelectedBatch()
      && this.selectedBatchId != null
      && this.isMeasuredMudFormValid();
  }

  getMeasuredMudLockReason(): string {
    if (!this.canShowMudMeasurement()) {
      return 'Mud measurement is not available for the selected batch tests.';
    }

    if (!this.canEditSelectedBatch()) {
      return this.getBatchEditLockReason();
    }
    return this.hasMeasuredMudCapture()
      ? 'Measured mud values are locked after capture. Container comments remain editable until the batch is submitted.'
      : '';
  }

  canShowMudMeasurement(): boolean {
    return this.selectedBatch?.mudMeasurementAccessible === true;
  }

  getBatchEditLockReason(): string {
    if (this.selectedBatchId == null) {
      return 'Select a batch to edit execution fields.';
    }
    if (this.batchMeasurementsLoading) {
      return 'Batch execution is disabled while saved measurements are loading.';
    }
    if (this.batchMeasurementsLoadError) {
      return this.batchMeasurementsLoadError;
    }
    const metadataReason = this.getCapabilityReason(c => c.editBatchExecution);
    if (metadataReason) {
      return metadataReason;
    }
    if (this.isParentProjectCompleted) {
      return 'Batch execution is read-only because the parent project is completed.';
    }
    if (!(this.statusId === 3 || this.statusId === 4)) {
      return 'Batch execution is locked for the current slurry status.';
    }
    if (this.hasLockedBatchSetupMeasurements()) {
      return 'Batch setup is locked because measurements already exist. Use Redo Batch to clear captured values before changing batch settings.';
    }
    if (this.selectedBatch && this.getSelectedBatchEditableOutputCount() === 0) {
      return 'Measurement capture is locked because no outputs are currently editable (In Progress, Rejected, Need Retake).';
    }
    return '';
  }

  isSelectedBatchTestLocked(testInputMasterId: number): boolean {
    if (!this.canEditSelectedBatchTests()) {
      return true;
    }

    const currentTest = this.selectedBatch?.tests.find(test => test.testInputMasterId === testInputMasterId);
    return currentTest != null && (currentTest.status > 1 || this.statusId === 4);
  }

  getSelectedBatchTestStatusLabel(testInputMasterId: number): string {
    const currentTest = this.selectedBatch?.tests.find(test => test.testInputMasterId === testInputMasterId);
    return currentTest ? this.batchTestStatusLabel(currentTest.status) : '';
  }

  onMeasuredMaterialSelectionChange(): void {
    const selectedId = this.measuredMaterialForm.slurryMaterialId;
    if (selectedId == null) {
      return;
    }

    const existing = this.measuredMaterials.find(x => x.slurryMaterialId === selectedId);
    if (!existing) {
      const option = this.batchMaterialOptions.find(x => x.slurryMaterialId === selectedId);
      this.measuredMaterialForm.measuredAmount = null;
      this.measuredMaterialForm.overrideAmount = null;
      this.measuredMaterialForm.caputuredCisProcontainerId = '';
      this.measuredMaterialForm.isFoamer = this.batchMixAsFoam && this.selectedFoamerMaterialIds.includes(selectedId);
      this.measuredMaterialForm.containerComments = '';
      this.measuredMaterialForm.weightComments = '';
      this.measuredMaterialForm.preferredCisProcontainerId = option?.preferredCisProcontainerId ?? '';
      this.measuredMaterialForm.replacePreferredCisProcontainerId = false;
      return;
    }

    this.measuredMaterialForm.measuredAmount = existing.measuredAmount ?? null;
    this.measuredMaterialForm.overrideAmount = existing.overrideAmount ?? null;
    this.measuredMaterialForm.caputuredCisProcontainerId = existing.caputuredCisProcontainerId ?? '';
    this.measuredMaterialForm.isFoamer = this.batchMixAsFoam && existing.isFoamer;
    this.measuredMaterialForm.containerComments = existing.containerComments ?? '';
    this.measuredMaterialForm.weightComments = existing.weightComments ?? '';
    this.measuredMaterialForm.preferredCisProcontainerId = existing.preferredCisProcontainerId ?? '';
    this.measuredMaterialForm.replacePreferredCisProcontainerId = existing.replacePreferredCisProcontainerId ?? false;
  }

  get batchMaterialOptions(): BatchMaterialOptionDto[] {
    return (this.selectedBatch?.materialOptions ?? []).slice();
  }

  get effectiveBatchMeasurementTolerance(): number {
    return this.batchToleranceSpecified
      ? this.batchHeader.measurementTolerance ?? this.defaultBatchMeasurementTolerance
      : this.defaultBatchMeasurementTolerance;
  }

  hasCustomBatchMeasurementTolerance(): boolean {
    return this.batchToleranceSpecified;
  }

  onBatchToleranceToggle(enabled: boolean): void {
    this.batchToleranceSpecified = enabled;
    this.batchHeader.measurementTolerance = enabled
      ? this.batchHeader.measurementTolerance ?? this.defaultBatchMeasurementTolerance
      : null;
  }

  canToggleBatchMixAsFoam(): boolean {
    return this.canEditSelectedBatchSetup()
      && this.getFoamerMaterialOptions().length > 0
      && !this.hasAnyMeasuredBatchMaterials();
  }

  getBatchMixAsFoamBlockedReason(): string {
    if (this.canToggleBatchMixAsFoam()) {
      return '';
    }
    if (!this.canEditSelectedBatch()) {
      return this.getBatchEditLockReason();
    }
    if (this.getFoamerMaterialOptions().length === 0) {
      return 'Mix as foam is unavailable because the batch has no liquid additive materials that can be used as foamers.';
    }
    if (this.hasAnyMeasuredBatchMaterials()) {
      return 'Mix as foam cannot be changed after material measurements are captured. Use the redo actions to clear the captured values first.';
    }
    return '';
  }

  onBatchMixAsFoamToggle(enabled: boolean): void {
    if (enabled === this.batchMixAsFoam) {
      return;
    }

    if (enabled) {
      this.batchLabVolumeBeforeMixAsFoam = this.batchHeader.batchLabVolume;
      this.batchMixAsFoam = true;
      this.batchHeader.batchLabVolume = 1170;
      this.batchHeader.excessFoamPercent = this.batchHeader.excessFoamPercent ?? this.defaultExcessFoamPercent;
    } else {
      this.batchMixAsFoam = false;
      this.batchHeader.foamDensity = null;
      this.batchHeader.excessFoamPercent = null;
      this.batchHeader.batchLabVolume = this.batchLabVolumeBeforeMixAsFoam ?? this.selectedBatch?.targetLabVolume ?? this.batchHeader.batchLabVolume;
      this.measuredFoamForm = {
        measuredWeight: null,
        cupWeight: null,
        cupVolume: null,
        weightComments: ''
      };
    }

    this.onMeasuredMaterialSelectionChange();
  }

  getFoamerMaterialOptions(): BatchMaterialOptionDto[] {
    return this.batchMaterialOptions.filter(option =>
      option.sectionType === 'LiquidAdditive'
      && this.isRequiredBatchMaterialOption(option));
  }

  canChangeBatchFoamerSelection(): boolean {
    return this.canToggleBatchMixAsFoam() && this.batchMixAsFoam;
  }

  isFoamerCandidateSelected(slurryMaterialId: number): boolean {
    return this.selectedFoamerMaterialIds.includes(slurryMaterialId);
  }

  onFoamerCandidateToggle(slurryMaterialId: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedFoamerMaterialIds.includes(slurryMaterialId)) {
        this.selectedFoamerMaterialIds = [...this.selectedFoamerMaterialIds, slurryMaterialId];
      }
    } else {
      this.selectedFoamerMaterialIds = this.selectedFoamerMaterialIds.filter(id => id !== slurryMaterialId);
    }

    if (this.measuredMaterialForm.slurryMaterialId === slurryMaterialId) {
      this.measuredMaterialForm.isFoamer = checked;
    }
  }

  shouldShowFoamerFluidLossWarning(): boolean {
    return this.batchMaterialOptions.some(option => option.isFluidLossTestWithFoamNeeded);
  }

  isMixAsFoamSelectionReadyForSubmit(): boolean {
    return !this.batchMixAsFoam
      || this.getFoamerBatchMaterialOptions().length === 0
      || this.measuredMaterials.some(material => material.isFoamer);
  }

  getSelectedMaterialOption(): BatchMaterialOptionDto | null {
    const selectedId = this.measuredMaterialForm.slurryMaterialId;
    return selectedId == null
      ? null
      : this.batchMaterialOptions.find(x => x.slurryMaterialId === selectedId) ?? null;
  }

  getSelectedMaterialTargetAmount(): number | null {
    const option = this.getSelectedMaterialOption();
    return option ? this.getMaterialTargetAmountForOption(option, this.measuredMaterialForm.isFoamer) : null;
  }

  isMeasuredMaterialContainerAcceptable(
    capturedContainer = this.measuredMaterialForm.caputuredCisProcontainerId,
    preferredContainer = this.measuredMaterialForm.preferredCisProcontainerId): boolean {
    return this.isContainerAcceptable(preferredContainer, capturedContainer);
  }

  isMeasuredMaterialWeightAcceptable(): boolean {
    const target = this.getSelectedMaterialTargetAmount();
    const measuredAmount = this.measuredMaterialForm.measuredAmount;
    if (target == null || measuredAmount == null) {
      return true;
    }

    return Math.abs(measuredAmount - target) <= this.effectiveBatchMeasurementTolerance;
  }

  isMeasuredMaterialFormValid(): boolean {
    if (this.measuredMaterialForm.measuredAmount == null || !this.measuredMaterialForm.caputuredCisProcontainerId.trim()) {
      return false;
    }

    return true;
  }

  areAllBatchMaterialsMeasured(): boolean {
    if (this.shouldShowDryBlendTotalRow()) {
      const blendContainerAcceptable = this.isContainerAcceptable(
        this.getDryBlendTotalPreferredContainer(),
        this.batchHeader.capturedBlendCisProcontainerId);
      const blendReady = (this.batchHeader.measuredBlendWeight != null
          || !!this.batchHeader.capturedBlendCisProcontainerId.trim())
        && (blendContainerAcceptable || !!this.batchHeader.blendContainerComments.trim())
        && (this.isMeasuredBlendWeightAcceptable() || !!this.batchHeader.blendWeightComments.trim());
      if (!blendReady) {
        return false;
      }
    }

    return this.batchMaterialOptions
      .filter(option => this.isRequiredBatchMaterialOption(option))
      .every(option => {
        if (this.shouldShowDryBlendTotalRow()
          && !option.afterBlend
          && !this.isLiquidBatchMaterialOption(option)
          && !this.isFoamerBatchMaterialOption(option)) {
          return true;
        }

        const measured = this.measuredMaterials.find(x => x.slurryMaterialId === option.slurryMaterialId);
        if (!measured) {
          return false;
        }

        const containerAcceptable = this.isContainerAcceptable(
          measured.preferredCisProcontainerId ?? option.preferredCisProcontainerId ?? '',
          measured.caputuredCisProcontainerId ?? '');
        const weightAcceptable = this.isMeasuredMaterialWeightAcceptableForRecord(measured, option);

        return (containerAcceptable || !!measured.containerComments?.trim())
          && (weightAcceptable || !!measured.weightComments?.trim());
      });
  }

  private getMissingBatchMeasurementCommentReason(): string {
    if (this.shouldShowDryBlendTotalRow()) {
      if (!this.isContainerAcceptable(this.getDryBlendTotalPreferredContainer(), this.batchHeader.capturedBlendCisProcontainerId)
        && !this.batchHeader.blendContainerComments.trim()) {
        return 'Enter a container comment for Blend Total before submitting the batch.';
      }
      if (!this.isMeasuredBlendWeightAcceptable() && !this.batchHeader.blendWeightComments.trim()) {
        return 'Enter a weight comment for Blend Total before submitting the batch.';
      }
    }

    for (const option of this.batchMaterialOptions.filter(candidate => this.isRequiredBatchMaterialOption(candidate))) {
      if (this.isReadOnlyBlendComponent(option)) {
        continue;
      }

      const measured = this.measuredMaterials.find(candidate => candidate.slurryMaterialId === option.slurryMaterialId);
      if (!measured) {
        continue;
      }

      if (!this.isContainerAcceptable(
        measured.preferredCisProcontainerId ?? option.preferredCisProcontainerId ?? '',
        measured.caputuredCisProcontainerId ?? '')
        && !measured.containerComments?.trim()) {
        return `Enter a container comment for ${option.materialName} before submitting the batch.`;
      }
      if (!this.isMeasuredMaterialWeightAcceptableForRecord(measured, option)
        && !measured.weightComments?.trim()) {
        return `Enter a weight comment for ${option.materialName} before submitting the batch.`;
      }
    }

    if (this.batchMixAsFoam && this.measuredFoam
      && !this.isMeasuredFoamWeightAcceptable()
      && !this.measuredFoam.weightComments?.trim()) {
      return 'Enter a weight comment for Foam before submitting the batch.';
    }

    if (this.measuredMud?.measuredMudCisProcontainer
      && !this.isContainerAcceptable(this.measuredMud.preferredMudCisProcontainer ?? '', this.measuredMud.measuredMudCisProcontainer)
      && !this.measuredMud.mudContainerComments?.trim()) {
      return 'Enter a container comment for Mud before submitting the batch.';
    }

    return '';
  }

  getExpectedFoamWeight(): number | null {
    if (this.batchHeader.batchLabVolume == null || this.batchHeader.foamDensity == null) {
      return null;
    }

    return this.batchHeader.batchLabVolume * this.batchHeader.foamDensity * this.lbPerGalInGPerMl;
  }

  formatBatchFixed3(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
      return '--';
    }

    return value.toFixed(3);
  }

  formatBatchFixed4(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
      return '--';
    }

    return value.toFixed(4);
  }

  isMeasuredFoamWeightAcceptable(): boolean {
    const expectedWeight = this.getExpectedFoamWeight();
    if (expectedWeight == null || this.measuredFoamForm.measuredWeight == null) {
      return true;
    }

    return Math.abs(this.measuredFoamForm.measuredWeight - expectedWeight) <= this.effectiveBatchMeasurementTolerance;
  }

  isMeasuredFoamFormValid(): boolean {
    const hasCupWeight = this.measuredFoamForm.cupWeight != null;
    const hasCupVolume = this.measuredFoamForm.cupVolume != null;
    if (hasCupWeight !== hasCupVolume) {
      return false;
    }
    return true;
  }

  isMeasuredFoamReadyForSubmit(): boolean {
    if (!this.batchMixAsFoam) {
      return true;
    }

    if (!this.measuredFoam) {
      return false;
    }

    const hasCupWeight = this.measuredFoam.cupWeight != null;
    const hasCupVolume = this.measuredFoam.cupVolume != null;
    if (!hasCupWeight || !hasCupVolume || (this.measuredFoam.cupVolume ?? 0) <= 0) {
      return false;
    }

    const expectedWeight = this.getExpectedFoamWeight();
    if (this.measuredFoam.measuredWeight == null) {
      return false;
    }

    if (expectedWeight == null) {
      return true;
    }

    return Math.abs(this.measuredFoam.measuredWeight - expectedWeight) <= this.effectiveBatchMeasurementTolerance
      || !!this.measuredFoam.weightComments?.trim();
  }

  isMeasuredMudFormValid(): boolean {
    return !!this.measuredMudForm.measuredMudCisProcontainer.trim();
  }

  isMeasuredMudReadyForSubmit(): boolean {
    if (!this.measuredMud?.measuredMudCisProcontainer) {
      return true;
    }

    return this.isContainerAcceptable(this.measuredMud.preferredMudCisProcontainer ?? '', this.measuredMud.measuredMudCisProcontainer)
      || !!this.measuredMud.mudContainerComments?.trim();
  }

  hasMeasuredBatchMaterialContainerComments(option: BatchMaterialOptionDto): boolean {
    return !!this.getMeasuredMaterialRecord(option.slurryMaterialId)?.containerComments?.trim();
  }

  hasMeasuredBatchMaterialWeightComments(option: BatchMaterialOptionDto): boolean {
    return !!this.getMeasuredMaterialRecord(option.slurryMaterialId)?.weightComments?.trim();
  }

  getMeasuredFoamResultDensity(): number | null {
    const cupWeight = this.measuredFoamForm.cupWeight;
    const cupVolume = this.measuredFoamForm.cupVolume;
    if (cupWeight == null || cupVolume == null || cupVolume === 0) {
      return null;
    }

    return cupWeight / cupVolume / this.lbPerGalInGPerMl;
  }

  isMeasuredMudContainerAcceptable(): boolean {
    return this.isContainerAcceptable(
      this.measuredMudForm.preferredMudCisProcontainer,
      this.measuredMudForm.measuredMudCisProcontainer);
  }

  hasMeasuredMudContainerComments(): boolean {
    return !!this.measuredMudForm.mudContainerComments.trim();
  }

  private isMeasuredMaterialWeightAcceptableForRecord(measured: MeasuredMaterialDto, option: BatchMaterialOptionDto): boolean {
    const target = this.getMaterialTargetAmountForOption(option);
    if (target == null || measured.measuredAmount == null) {
      return true;
    }

    return Math.abs(measured.measuredAmount - target) <= this.effectiveBatchMeasurementTolerance;
  }

  private getMaterialTargetAmountForOption(option: BatchMaterialOptionDto, isFoamer = false): number | null {
    if (option.calculatedLabWeight == null) {
      return null;
    }

    if (this.batchMixAsFoam) {
      return this.getFoamMixTargetAmount(option, isFoamer);
    }

    const targetLabVolume = this.selectedBatch?.targetLabVolume;
    const batchLabVolume = this.batchHeader.batchLabVolume;
    if (targetLabVolume && batchLabVolume && targetLabVolume > 0) {
      return option.calculatedLabWeight * batchLabVolume / targetLabVolume;
    }

    return option.calculatedLabWeight;
  }

  private getFoamMixTargetAmount(option: BatchMaterialOptionDto, isFoamer: boolean): number | null {
    if (option.calculatedLabWeight == null || this.batchHeader.batchLabVolume == null || this.batchHeader.foamDensity == null) {
      return null;
    }

    const totalCalculatedWeight = sumFiniteNumbers(
      this.batchMaterialOptions.map(material => material.calculatedLabWeight)
    );
    if (totalCalculatedWeight <= 0) {
      return null;
    }

    const baseRatio = this.batchHeader.batchLabVolume * this.batchHeader.foamDensity * this.lbPerGalInGPerMl / totalCalculatedWeight;
    const excessMultiplier = isFoamer
      ? 1
      : 1 + ((this.batchHeader.excessFoamPercent ?? this.defaultExcessFoamPercent) / 100);

    return option.calculatedLabWeight * baseRatio * excessMultiplier;
  }

  isContainerAcceptable(preferredContainer: string, capturedContainer: string): boolean {
    if (!preferredContainer.trim() || !capturedContainer.trim()) {
      return true;
    }

    return preferredContainer
      .split(',')
      .map(value => value.trim())
      .filter(Boolean)
      .includes(capturedContainer);
  }

  getBatchMaterialName(slurryMaterialId: number): string {
    const option = this.batchMaterialOptions.find(x => x.slurryMaterialId === slurryMaterialId);
    return option ? option.materialName : `Material #${slurryMaterialId}`;
  }

  getDryBatchMaterialOptions(): BatchMaterialOptionDto[] {
    return this.batchMaterialOptions.filter(option =>
      !this.isLiquidBatchMaterialOption(option)
      && !this.isFoamerBatchMaterialOption(option)
      && this.shouldShowDryBatchMaterialOption(option));
  }

  getDryBatchBlendMaterialOptions(): BatchMaterialOptionDto[] {
    return this.getDryBatchMaterialOptions().filter(option => !option.afterBlend);
  }

  getDryBatchAfterBlendMaterialOptions(): BatchMaterialOptionDto[] {
    return this.getDryBatchMaterialOptions().filter(option => option.afterBlend);
  }

  shouldShowDryBlendTotalRow(): boolean {
    return (this.selectedBatch?.isBlendTest === true || this.isBlendTest) && this.getDryBlendBaseMaterialOptions().length > 0;
  }

  getDryBlendTotalTargetAmount(): number | null {
    if (!this.shouldShowDryBlendTotalRow()) {
      return null;
    }

    const total = sumFiniteNumbers(
      this.getDryBlendBaseMaterialOptions()
        .map(option => this.getBatchMaterialTargetAmount(option.slurryMaterialId))
    );

    return total > 0 ? total : 0;
  }

  getDryBlendTotalPreferredContainer(): string {
    return this.blendCisProContainer?.trim() ?? '';
  }

  getLiquidBatchMaterialOptions(): BatchMaterialOptionDto[] {
    return this.batchMaterialOptions.filter(option =>
      this.isRequiredBatchMaterialOption(option)
      && this.isLiquidBatchMaterialOption(option)
      && !this.isFoamerBatchMaterialOption(option));
  }

  getFoamerBatchMaterialOptions(): BatchMaterialOptionDto[] {
    return this.batchMaterialOptions.filter(option =>
      this.isRequiredBatchMaterialOption(option)
      && this.isFoamerBatchMaterialOption(option));
  }

  getMeasuredMaterialRecord(slurryMaterialId: number): MeasuredMaterialDto | null {
    return this.measuredMaterials.find(x => x.slurryMaterialId === slurryMaterialId) ?? null;
  }

  selectBatchMaterial(slurryMaterialId: number): void {
    this.selectedBatchBlendMeasurement = false;
    this.measuredMaterialForm.slurryMaterialId = slurryMaterialId;
    this.onMeasuredMaterialSelectionChange();
    this.measuredMaterialMessage = '';
  }

  selectBatchBlendMeasurement(): void {
    this.selectedBatchBlendMeasurement = true;
    this.measuredMaterialForm.slurryMaterialId = null;
    this.measuredMaterialMessage = '';
  }

  openBatchMaterialComments(slurryMaterialId: number, commentKind: 'weight' | 'container'): void {
    this.openBatchMeasureDialog(slurryMaterialId, 'comments', commentKind);
  }

  openBatchMeasureDialog(
    slurryMaterialId: number,
    mode: 'measure' | 'comments' = 'measure',
    commentKind?: 'weight' | 'container'
  ): void {
    if (this.selectedBatchId == null || this.measurementDialogOpen) {
      return;
    }

    const option = this.batchMaterialOptions.find(x => x.slurryMaterialId === slurryMaterialId);
    if (!option || this.isReadOnlyBlendComponent(option)) {
      return;
    }

    const existing = this.getMeasuredMaterialRecord(slurryMaterialId);
    const isFoamer = this.batchMixAsFoam && this.selectedFoamerMaterialIds.includes(slurryMaterialId);
    const canEditCoreValues = this.canMeasureBatchMaterial(slurryMaterialId);
    const canEditComments = this.canEditSelectedBatch();

    let lockReason = '';
    if (!canEditComments) {
      lockReason = this.getBatchEditLockReason();
    } else if (existing) {
      lockReason = 'Measured values are locked after capture. Comment fields remain editable until the batch is submitted.';
    }

    const data: BatchMeasureMaterialDialogData = {
      measurementKind: 'material',
      mode,
      commentKind,
      slurryMaterialId,
      title: option.materialName,
      targetAmount: this.getBatchMaterialTargetAmount(slurryMaterialId),
      tolerance: this.effectiveBatchMeasurementTolerance,
      preferredCisProcontainerId: this.getBatchMaterialPreferredContainer(option),
      preferredContainerDeleted: this.isBatchMaterialPreferredContainerDeleted(option),
      capturedContainerDeleted: existing?.caputuredCisProcontainerDeleted ?? false,
      canEditCoreValues,
      canEditComments,
      saveLabel: existing ? 'Save Comments' : 'Save Measured Material',
      lockReason,
      slurryId: this.slurryId,
      batchId: this.selectedBatchId,
      batchMixAsFoam: this.batchMixAsFoam,
      isFoamer,
      measuredAmount: existing?.measuredAmount ?? null,
      overrideAmount: existing?.overrideAmount ?? null,
      caputuredCisProcontainerId: existing?.caputuredCisProcontainerId ?? '',
      containerComments: existing?.containerComments ?? '',
      weightComments: existing?.weightComments ?? '',
      replacePreferredCisProcontainerId: existing?.replacePreferredCisProcontainerId ?? false,
      batchUpdateRequest: {},
      showFluidLossFoamerWarning: isFoamer && option.isFluidLossTestWithFoamNeeded
    };

    this.measurementDialogOpen = true;
    const ref = this.dialog.open(BatchMeasureMaterialDialogComponent, {
      width: mode === 'comments' ? '680px' : '1008px',
      maxWidth: '95vw',
      panelClass: 'legacy-batch-measure-dialog',
      disableClose: false,
      data
    });

    ref.afterClosed().subscribe((result?: BatchMeasureMaterialDialogResult) => {
      this.measurementDialogOpen = false;
      if (!result?.saved || !result.material || this.selectedBatchId == null) {
        return;
      }
      this.applyMaterialMeasurementDraft(result.material);
      if (!result.createdMeasurement) { return; }
      const options = this.getBatchMeasurementSequence();
      const currentIndex = options.findIndex(o => o.slurryMaterialId === slurryMaterialId);
      for (let i = currentIndex + 1; i < options.length; i++) {
        if (!this.getMeasuredMaterialRecord(options[i].slurryMaterialId)) {
          this.openBatchMeasureDialog(options[i].slurryMaterialId);
          return;
        }
      }
    });
  }

  private getBatchMeasurementSequence(): BatchMaterialOptionDto[] {
    const dry = this.shouldShowDryBlendTotalRow()
      ? this.getDryBatchAfterBlendMaterialOptions()
      : this.getDryBatchMaterialOptions();
    return [
      ...dry,
      ...this.getLiquidBatchMaterialOptions(),
      ...this.getFoamerBatchMaterialOptions()
    ];
  }

  openBatchBlendMeasureDialog(
    mode: 'measure' | 'comments' = 'measure',
    commentKind?: 'weight' | 'container'
  ): void {
    if (this.measurementDialogOpen
      || this.selectedBatchId == null
      || (mode === 'measure' && !this.canOpenBatchBlendMeasurement())
      || (mode === 'comments' && !this.hasPersistedBlendMeasurement())) {
      return;
    }

    const data: BatchMeasureMaterialDialogData = {
      measurementKind: 'blend',
      mode,
      commentKind,
      slurryMaterialId: null,
      title: 'Total',
      targetAmount: this.getDryBlendTotalTargetAmount(),
      tolerance: this.effectiveBatchMeasurementTolerance,
      preferredCisProcontainerId: this.getDryBlendTotalPreferredContainer(),
      preferredContainerDeleted: this.isBatchBlendPreferredContainerDeleted(),
      capturedContainerDeleted: this.isBatchBlendCapturedContainerDeleted(),
      canEditCoreValues: mode === 'measure',
      canEditComments: this.canEditSelectedBatch(),
      saveLabel: 'Save Measured Blend',
      lockReason: '',
      slurryId: this.slurryId,
      batchId: this.selectedBatchId,
      batchMixAsFoam: this.batchMixAsFoam,
      isFoamer: false,
      measuredAmount: this.batchHeader.measuredBlendWeight,
      overrideAmount: null,
      caputuredCisProcontainerId: this.batchHeader.capturedBlendCisProcontainerId,
      containerComments: this.batchHeader.blendContainerComments,
      weightComments: this.batchHeader.blendWeightComments,
      replacePreferredCisProcontainerId: this.batchHeader.replacePreferredBlendCisProcontainerId,
      batchUpdateRequest: {
        notes: this.batchHeader.notes || undefined,
        batchLabVolume: this.batchHeader.batchLabVolume ?? undefined,
        foamDensity: this.batchMixAsFoam ? (this.batchHeader.foamDensity ?? undefined) : undefined,
        measurementTolerance: this.batchToleranceSpecified ? (this.batchHeader.measurementTolerance ?? this.defaultBatchMeasurementTolerance) : undefined,
        excessFoamPercent: this.batchMixAsFoam ? (this.batchHeader.excessFoamPercent ?? undefined) : undefined,
        measuredMudCisProcontainer: this.batchHeader.measuredMudCisProcontainer || undefined,
        selectedFoamerMaterialIds: this.batchMixAsFoam ? [...this.selectedFoamerMaterialIds] : [],
        selectedTestInputMasterIds: [...this.selectedBatchTestInputMasterIds]
      }
    };

    this.measurementDialogOpen = true;
    const ref = this.dialog.open(BatchMeasureMaterialDialogComponent, {
      width: mode === 'comments' ? '680px' : '1008px',
      maxWidth: '95vw',
      panelClass: 'legacy-batch-measure-dialog',
      disableClose: false,
      data
    });

    ref.afterClosed().subscribe((result?: BatchMeasureMaterialDialogResult) => {
      this.measurementDialogOpen = false;
      if (!result?.saved || !result.blend || this.selectedBatchId == null) {
        return;
      }
      this.applyBlendMeasurementDraft(result.blend);
      const next = [
        ...this.getDryBatchAfterBlendMaterialOptions(),
        ...this.getLiquidBatchMaterialOptions(),
        ...this.getFoamerBatchMaterialOptions()
      ].find(option => !this.getMeasuredMaterialRecord(option.slurryMaterialId));
      if (result.createdMeasurement && next) {
        this.openBatchMeasureDialog(next.slurryMaterialId);
      }
    });
  }

  openBatchMudMeasureDialog(mode: 'measure' | 'comments' = 'measure'): void {
    if (this.measurementDialogOpen || this.selectedBatchId == null || !this.canShowMudMeasurement()
      || (mode === 'measure' && !this.canEditSelectedBatch())) {
      return;
    }

    const canEditComments = this.canEditSelectedBatch();

    const data: BatchMeasureMaterialDialogData = {
      measurementKind: 'mud',
      mode,
      slurryMaterialId: null,
      title: 'Mud',
      targetAmount: null,
      tolerance: this.effectiveBatchMeasurementTolerance,
      preferredCisProcontainerId: this.measuredMudForm.preferredMudCisProcontainer,
      preferredContainerDeleted: this.isMeasuredMudPreferredContainerDeleted(),
      capturedContainerDeleted: this.isMeasuredMudCapturedContainerDeleted(),
      canEditCoreValues: mode === 'measure' && canEditComments,
      canEditComments,
      saveLabel: mode === 'measure' ? 'Measure Mud' : 'Save Mud Comments',
      lockReason: mode === 'comments' ? this.getMeasuredMudLockReason() : '',
      slurryId: this.slurryId,
      batchId: this.selectedBatchId,
      batchMixAsFoam: this.batchMixAsFoam,
      isFoamer: false,
      measuredAmount: null,
      overrideAmount: null,
      caputuredCisProcontainerId: this.measuredMudForm.measuredMudCisProcontainer,
      containerComments: this.measuredMudForm.mudContainerComments,
      weightComments: '',
      replacePreferredCisProcontainerId: this.measuredMudForm.replacePreferredMudCisProcontainer,
      batchUpdateRequest: {}
    };

    this.openAuxiliaryMeasurementDialog(data);
  }

  openBatchFoamMeasureDialog(mode: 'measure' | 'comments' = 'measure'): void {
    if (this.measurementDialogOpen || this.selectedBatchId == null || !this.batchMixAsFoam
      || (mode === 'measure' && !this.canEditSelectedBatch())) {
      return;
    }

    const canEditComments = this.canEditSelectedBatch();

    const data: BatchMeasureMaterialDialogData = {
      measurementKind: 'foam',
      mode,
      slurryMaterialId: null,
      title: 'Foam',
      targetAmount: this.getExpectedFoamWeight(),
      tolerance: this.effectiveBatchMeasurementTolerance,
      preferredCisProcontainerId: '',
      preferredContainerDeleted: false,
      capturedContainerDeleted: false,
      canEditCoreValues: mode === 'measure' && canEditComments,
      canEditComments,
      saveLabel: mode === 'measure' ? 'Measure Foam' : 'Save Foam Comments',
      lockReason: mode === 'comments' ? this.getMeasuredFoamLockReason() : '',
      slurryId: this.slurryId,
      batchId: this.selectedBatchId,
      batchMixAsFoam: true,
      isFoamer: false,
      measuredAmount: this.measuredFoamForm.measuredWeight,
      overrideAmount: null,
      caputuredCisProcontainerId: '',
      containerComments: '',
      weightComments: this.measuredFoamForm.weightComments,
      replacePreferredCisProcontainerId: false,
      batchUpdateRequest: {},
      existingCupWeight: this.measuredFoamForm.cupWeight,
      existingCupVolume: this.measuredFoamForm.cupVolume
    };

    this.openAuxiliaryMeasurementDialog(data);
  }

  openBatchFoamCupMeasureDialog(): void {
    if (this.measurementDialogOpen || this.selectedBatchId == null || !this.batchMixAsFoam
      || !this.canEditSelectedBatch()) {
      return;
    }

    const data: BatchMeasureMaterialDialogData = {
      measurementKind: 'foamCup',
      mode: 'measure',
      slurryMaterialId: null,
      title: 'Foam Cup',
      targetAmount: null,
      tolerance: this.effectiveBatchMeasurementTolerance,
      preferredCisProcontainerId: '',
      preferredContainerDeleted: false,
      capturedContainerDeleted: false,
      canEditCoreValues: true,
      canEditComments: false,
      saveLabel: 'Measure Foam Cup',
      lockReason: '',
      slurryId: this.slurryId,
      batchId: this.selectedBatchId,
      batchMixAsFoam: true,
      isFoamer: false,
      measuredAmount: this.measuredFoamForm.cupWeight,
      overrideAmount: null,
      caputuredCisProcontainerId: '',
      containerComments: '',
      weightComments: this.measuredFoamForm.weightComments,
      replacePreferredCisProcontainerId: false,
      batchUpdateRequest: {},
      cupVolume: this.measuredFoamForm.cupVolume,
      existingFoamWeight: this.measuredFoamForm.measuredWeight
    };

    this.openAuxiliaryMeasurementDialog(data);
  }

  private openAuxiliaryMeasurementDialog(data: BatchMeasureMaterialDialogData): void {
    this.measurementDialogOpen = true;
    const ref = this.dialog.open(BatchMeasureMaterialDialogComponent, {
      width: data.mode === 'comments' ? '680px' : '1008px',
      maxWidth: '95vw',
      panelClass: 'legacy-batch-measure-dialog',
      disableClose: false,
      data
    });
    ref.afterClosed().subscribe((result?: BatchMeasureMaterialDialogResult) => {
      this.measurementDialogOpen = false;
      if (result?.mud) {
        this.applyMudMeasurementDraft(result.mud);
      }
      if (result?.foam) {
        this.applyFoamMeasurementDraft(result.foam);
      }
    });
  }

  isBatchMaterialSelected(slurryMaterialId: number): boolean {
    return this.measuredMaterialForm.slurryMaterialId === slurryMaterialId;
  }

  isBatchBlendMeasurementActive(): boolean {
    return this.selectedBatchBlendMeasurement;
  }

  canOpenBatchBlendMeasurement(): boolean {
    return this.canEditSelectedBatch()
      && this.shouldShowDryBlendTotalRow();
  }

  canEditSelectedBatchBlendCoreValues(): boolean {
    return this.selectedBatchBlendMeasurement && this.canOpenBatchBlendMeasurement();
  }

  isMeasuredBlendWeightAcceptable(): boolean {
    const target = this.getDryBlendTotalTargetAmount();
    const measuredWeight = this.batchHeader.measuredBlendWeight;
    if (target == null || measuredWeight == null) {
      return true;
    }

    return Math.abs(measuredWeight - target) <= this.effectiveBatchMeasurementTolerance;
  }

  canMeasureBatchMaterial(slurryMaterialId: number): boolean {
    const option = this.batchMaterialOptions.find(candidate => candidate.slurryMaterialId === slurryMaterialId);
    return this.canEditSelectedBatch() && option != null && !this.isReadOnlyBlendComponent(option);
  }

  isReadOnlyBlendComponent(option: BatchMaterialOptionDto): boolean {
    return this.shouldShowDryBlendTotalRow()
      && !option.afterBlend
      && !this.isLiquidBatchMaterialOption(option)
      && !this.isFoamerBatchMaterialOption(option);
  }

  canOpenBatchMaterialMeasurement(slurryMaterialId: number): boolean {
    return this.canMeasureBatchMaterial(slurryMaterialId);
  }

  showBatchMeasurementsPendingAlert(): boolean {
    return this.selectedBatchId != null
      && !this.batchMeasurementsLoading
      && !this.batchMeasurementsLoadError
      && !this.persistedBatchMaterialsLocked
      && this.canEditSelectedBatch();
  }

  canEditSelectedMeasuredMaterialCoreValues(): boolean {
    const selectedId = this.measuredMaterialForm.slurryMaterialId;
    return selectedId != null && this.canMeasureBatchMaterial(selectedId);
  }

  canEditSelectedMeasuredMaterialComments(): boolean {
    return this.canEditSelectedBatch() && this.measuredMaterialForm.slurryMaterialId != null;
  }

  getSelectedBatchMeasurementSaveLabel(): string {
    return this.getSelectedMeasuredMaterial() ? 'Save Comments' : 'Save Measured Material';
  }

  getMeasuredMudActionLabel(): string {
    return this.hasMeasuredMudCapture() ? 'Remeasure Mud' : 'Measure Mud';
  }

  getMeasuredFoamWeightActionLabel(): string {
    return this.hasMeasuredFoamWeight() ? 'Remeasure Foam' : 'Measure Foam';
  }

  getMeasuredFoamCupActionLabel(): string {
    return this.hasMeasuredFoamCup() ? 'Remeasure Foam Cup' : 'Measure Foam Cup';
  }

  getBatchMaterialTargetAmount(slurryMaterialId: number): number | null {
    const option = this.batchMaterialOptions.find(x => x.slurryMaterialId === slurryMaterialId);
    if (!option) {
      return null;
    }

    return this.getMaterialTargetAmountForOption(option, this.selectedFoamerMaterialIds.includes(slurryMaterialId));
  }

  getBatchMaterialPreferredContainer(option: BatchMaterialOptionDto): string {
    return this.getMeasuredMaterialRecord(option.slurryMaterialId)?.preferredCisProcontainerId
      ?? option.preferredCisProcontainerId
      ?? '';
  }

  isBatchMaterialPreferredContainerDeleted(option: BatchMaterialOptionDto): boolean {
    return this.getMeasuredMaterialRecord(option.slurryMaterialId)?.preferredCisProcontainerDeleted
      ?? option.preferredCisProcontainerDeleted
      ?? false;
  }

  isBatchMaterialCapturedContainerDeleted(option: BatchMaterialOptionDto): boolean {
    return this.getMeasuredMaterialRecord(option.slurryMaterialId)?.caputuredCisProcontainerDeleted ?? false;
  }

  isMeasuredBatchMaterialContainerAcceptable(option: BatchMaterialOptionDto): boolean {
    const measured = this.getMeasuredMaterialRecord(option.slurryMaterialId);
    if (!measured) {
      return true;
    }

    return this.isContainerAcceptable(
      measured.preferredCisProcontainerId ?? option.preferredCisProcontainerId ?? '',
      measured.caputuredCisProcontainerId ?? '');
  }

  isMeasuredBatchMaterialWeightAcceptable(option: BatchMaterialOptionDto): boolean {
    const measured = this.getMeasuredMaterialRecord(option.slurryMaterialId);
    return !measured || this.isMeasuredMaterialWeightAcceptableForRecord(measured, option);
  }

  isBatchBlendPreferredContainerDeleted(): boolean {
    return this.selectedBatch?.preferredBlendCisProcontainerDeleted === true;
  }

  isBatchBlendCapturedContainerDeleted(): boolean {
    return this.selectedBatch?.capturedBlendCisProcontainerDeleted === true;
  }

  isSelectedMeasuredMaterialPreferredContainerDeleted(): boolean {
    const selectedId = this.measuredMaterialForm.slurryMaterialId;
    if (selectedId == null) {
      return false;
    }

    const option = this.batchMaterialOptions.find(x => x.slurryMaterialId === selectedId);
    return option ? this.isBatchMaterialPreferredContainerDeleted(option) : false;
  }

  isSelectedMeasuredMaterialCapturedContainerDeleted(): boolean {
    const selectedId = this.measuredMaterialForm.slurryMaterialId;
    if (selectedId == null) {
      return false;
    }

    const option = this.batchMaterialOptions.find(x => x.slurryMaterialId === selectedId);
    return option ? this.isBatchMaterialCapturedContainerDeleted(option) : false;
  }

  isMeasuredMudPreferredContainerDeleted(): boolean {
    return this.measuredMud?.preferredMudCisProcontainerDeleted === true;
  }

  isMeasuredMudCapturedContainerDeleted(): boolean {
    return this.measuredMud?.measuredMudCisProcontainerDeleted === true;
  }

  isBatchMeasurementSectionActive(section: 'dry' | 'liquid' | 'foamer'): boolean {
    if (this.selectedBatchBlendMeasurement) {
      return false;
    }

    const selectedId = this.measuredMaterialForm.slurryMaterialId;
    if (selectedId == null) {
      return false;
    }

    const option = this.batchMaterialOptions.find(x => x.slurryMaterialId === selectedId);
    if (!option) {
      return false;
    }

    if (section === 'dry') {
      return !this.isLiquidBatchMaterialOption(option) && !this.isFoamerBatchMaterialOption(option);
    }

    if (section === 'liquid') {
      return this.isLiquidBatchMaterialOption(option) && !this.isFoamerBatchMaterialOption(option);
    }

    return this.isFoamerBatchMaterialOption(option);
  }

  getSelectedBatchMeasurementOption(): BatchMaterialOptionDto | null {
    const selectedId = this.measuredMaterialForm.slurryMaterialId;
    return selectedId == null
      ? null
      : this.batchMaterialOptions.find(x => x.slurryMaterialId === selectedId) ?? null;
  }

  getSelectedBatchMeasurementTitle(): string {
    const option = this.getSelectedBatchMeasurementOption();
    if (!option) {
      return 'Measure Material';
    }

    return `Measure ${option.materialName}`;
  }

  getSelectedBatchMeasurementActionLabel(): string {
    return this.getSelectedMeasuredMaterial() ? 'Measured' : 'Measure';
  }

  private isLiquidBatchMaterialOption(option: BatchMaterialOptionDto): boolean {
    return option.sectionType === 'LiquidAdditive' || option.sectionType === 'LiquidBase';
  }

  private isFoamerBatchMaterialOption(option: BatchMaterialOptionDto): boolean {
    return this.batchMixAsFoam && this.selectedFoamerMaterialIds.includes(option.slurryMaterialId);
  }

  getSelectedBatchStatusBreakdown(): Array<{ label: string; count: number; }> {
    if (!this.selectedBatch) {
      return [];
    }

    const buckets = new Map<string, number>();
    for (const t of this.selectedBatch.tests) {
      const label = this.batchTestStatusLabel(t.status);
      buckets.set(label, (buckets.get(label) ?? 0) + 1);
    }

    return Array.from(buckets.entries()).map(([label, count]) => ({ label, count }));
  }

  private printBatchTestSheet(
    title: string,
    tests: Array<PrintableBatchTestEntry & { isReference: boolean; }>,
    batchSpecificPrint = false
  ): void {
    const popup = this.openPrintWindow(900, 700);
    if (!popup) {
      this.batchMessage = 'Print window was blocked by the browser.';
      return;
    }

    const escape = (value: unknown): string => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  const renderHeaderValue = (value: unknown): string => {
    const formatted = this.formatPrintableValue(value, '');
    return formatted.length > 0 ? escape(formatted) : '&nbsp;';
  };
  const renderFieldItems = (
    fields: PrintableBatchTestField[],
    widthClass: 'field-item-wide' | 'field-item-compact' = 'field-item-compact'
  ): string => fields.map(field => `
    <div class="field-item ${widthClass}">
      <span class="field-caption">${escape(field.label)}</span>
      <span class="field-value">${escape(field.value)}</span>
    </div>`).join('');
  const formatFixed = (value: unknown, digits: number): string => {
    if (value == null || value === '') {
      return '--';
    }

    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) {
      return '--';
    }

    return parsed.toFixed(digits);
  };
  const formatUnitValue = (value: unknown, digits: number, unit: string): string => {
    const formatted = formatFixed(value, digits);
    return formatted === '--' ? '--' : `${formatted}${unit}`;
  };
  const formatIntegerUnit = (value: unknown, unit: string): string => {
    if (value == null || value === '') {
      return '--';
    }

    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) {
      return '--';
    }

    return `${Math.round(parsed)}${unit}`;
  };
  const formatDateValue = (value: string | null | undefined): string => {
    if (!value) {
      return '--';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '--';
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }).format(date);
  };
  const renderScheduleTable = (
    scheduleTitle: string,
    columns: string[],
    rows: Array<{ action: string; duration: string; target?: string; }>
  ): string => `
    <div class="schedule-box">
      <div class="schedule-box-title">${escape(scheduleTitle)}</div>
      <table class="schedule-table">
        <thead>
          <tr>${columns.map(column => `<th>${escape(column)}</th>`).join('')}</tr>
        </thead>
        <tbody>
          ${rows.map(row => `
            <tr>
              <td>${escape(row.action)}</td>
              <td>${escape(row.duration)}</td>
              ${columns.length > 2 ? `<td>${escape(row.target ?? '--')}</td>` : ''}
            </tr>`).join('')}
        </tbody>
      </table>
    </div>`;

  const isBatchSpecific = batchSpecificPrint;
  const slurryPropertyKinds = ['Thickening Time', 'Fluid Loss', 'Rheology', 'Static Gel Strength', 'Compressive Strength'];
  const foamKinds = ['Foam Stability'];
  const hasSlurryPropertyTests = tests.some(test => slurryPropertyKinds.includes(test.kind));
  const hasFoamTests = tests.some(test => foamKinds.includes(test.kind));
  const formatMaterialPercent = (value: unknown): string => {
    if (value == null || value === '') {
      return '-';
    }

    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) {
      return '-';
    }

    return `${parsed.toFixed(3)}%`;
  };
  const formatGalPerSk = (value: unknown, digits = 2): string => {
    const formatted = formatFixed(value, digits);
    return formatted === '--' ? '--' : `${formatted}gal/sk`;
  };
  const mixInfoFields = this.toPrintableFields([
    { label: 'Density:', value: formatUnitValue(this.slurryDensityPpg, 2, 'ppg') },
    { label: 'Sack Weight:', value: formatUnitValue(this.equivalentSackLb, 2, 'lb') },
    { label: 'Sent To Lab:', value: formatDateValue(this.sentToLabDate) }
  ]).filter(field => field.value !== '--');
  const infoFields = this.toPrintableFields([
    ...(hasFoamTests ? [
      { label: 'Base Density:', value: formatUnitValue(this.testSettings.baseDensity, 2, 'ppg') },
      { label: 'Foamed Density:', value: formatUnitValue(this.testSettings.foamDensity, 2, 'ppg') }
    ] : []),
    ...(hasSlurryPropertyTests ? [
      { label: 'Initial Temperature:', value: formatIntegerUnit(this.testSettings.initialTemperature, 'F') },
      { label: 'Initial Pressure:', value: formatIntegerUnit(this.testSettings.initialPressure, 'psi') },
      { label: 'Final Temperature/BHCT:', value: formatIntegerUnit(this.testSettings.bhct, 'F') },
      { label: 'Temperature Ramp Time:', value: formatIntegerUnit(this.testSettings.temperatureRampTime, 'min') },
      { label: 'Pressure Ramp Time:', value: formatIntegerUnit(this.testSettings.pressureRampTime, 'min') },
      { label: 'Final Pressure:', value: formatIntegerUnit(this.testSettings.finalPressure, 'psi') },
      { label: 'BHST:', value: formatIntegerUnit(this.testSettings.bhst, 'F') }
    ] : [])
  ]).filter(field => field.value !== '--');
  const materialLocationQueues = new Map<string, string[]>();
  for (const material of this.loadedSlurryMaterialDetails) {
    const sectionType = material.sectionType ?? '';
    const queue = materialLocationQueues.get(sectionType) ?? [];
    queue.push(material.containerLocation ?? material.preferredCisProContainerLocation ?? '');
    materialLocationQueues.set(sectionType, queue);
  }
  const nextMaterialLocation = (sectionType: string): string => {
    const queue = materialLocationQueues.get(sectionType);
    return queue && queue.length > 0 ? queue.shift() ?? '' : '';
  };

  const batchOptionsBySection = new Map<string, BatchMaterialOptionDto[]>();
  for (const option of this.batchMaterialOptions) {
    const queue = batchOptionsBySection.get(option.sectionType) ?? [];
    queue.push(option);
    batchOptionsBySection.set(option.sectionType, queue);
  }
  const nextBatchOption = (sectionType: string): BatchMaterialOptionDto | null => {
    const queue = batchOptionsBySection.get(sectionType);
    return queue && queue.length > 0 ? queue.shift() ?? null : null;
  };
  const measuredByMaterialId = new Map(this.measuredMaterials.map(material => [material.slurryMaterialId, material]));
  const formatMeasuredAmount = (material: MeasuredMaterialDto | undefined, digits: number): string =>
    material ? formatFixed(material.overrideAmount ?? material.measuredAmount, digits) : '--';

  const dryMaterials = this.getPrintableDryMaterials().map(material => {
    const option = isBatchSpecific ? nextBatchOption(material.sectionType) : null;
    const measurement = option ? measuredByMaterialId.get(option.slurryMaterialId) : undefined;
    return {
      ...material,
      preferredContainer: option?.preferredCisProcontainerId ?? material.cisPro,
      containerLocation: material.name === 'Total'
        ? this.preferredBlendCisProContainerLocation
        : nextMaterialLocation(material.sectionType),
      capturedContainer: measurement?.caputuredCisProcontainerId ?? '--',
      measuredWeight: formatMeasuredAmount(measurement, isBatchSpecific ? 3 : 2),
      requiresFoamWarning: option?.isFluidLossTestWithFoamNeeded ?? false
    };
  });
  const liquidMaterials = this.getPrintableLiquidMaterials().map(material => {
    const option = isBatchSpecific ? nextBatchOption(material.sectionType) : null;
    const measurement = option ? measuredByMaterialId.get(option.slurryMaterialId) : undefined;
    return {
      ...material,
      preferredContainer: option?.preferredCisProcontainerId ?? material.cisPro,
      containerLocation: nextMaterialLocation(material.sectionType),
      capturedContainer: measurement?.caputuredCisProcontainerId ?? '--',
      measuredWeight: formatMeasuredAmount(measurement, isBatchSpecific ? 3 : 2),
      requiresFoamWarning: option?.isFluidLossTestWithFoamNeeded ?? false
    };
  }).sort((left, right) => left.name.localeCompare(right.name));

  const dryMaterialRows = (isBatchSpecific ? dryMaterials.map(material => `
    <tr class="${material.requiresFoamWarning ? 'warning-material-row' : ''}">
      <td class="flag-cell">${material.requiresFoamWarning ? '*' : ''}</td>
      <td>${escape(material.name)}</td>
      <td class="numeric-cell">${escape(formatMaterialPercent(material.percent))}</td>
      <td class="numeric-cell">${escape(formatFixed(material.weight, 3))}</td>
      <td>${escape(material.preferredContainer)}</td>
      <td>${escape(material.capturedContainer)}</td>
      <td class="numeric-cell">${escape(formatFixed(material.labWeight, 3))}</td>
      <td class="numeric-cell">${escape(material.measuredWeight)}</td>
    </tr>`).join('') : dryMaterials.map(material => `
    <tr class="${material.requiresFoamWarning ? 'warning-material-row' : ''}">
      <td class="flag-cell">${material.requiresFoamWarning ? '*' : ''}</td>
      <td>${escape(material.name)}</td>
      <td class="numeric-cell">${escape(formatMaterialPercent(material.percent))}</td>
      <td class="numeric-cell">${escape(formatFixed(material.weight, 2))}</td>
      <td>${escape(material.preferredContainer)}</td>
      <td class="location-cell">${escape(material.containerLocation || '--')}</td>
      <td class="numeric-cell">${escape(formatFixed(material.labWeight, 2))}</td>
    </tr>`).join(''))
    || `<tr><td colspan="${isBatchSpecific ? 8 : 7}">No dry materials</td></tr>`;
  const liquidMaterialRows = (isBatchSpecific ? liquidMaterials.map(material => `
    <tr class="${material.requiresFoamWarning ? 'warning-material-row' : ''}">
      <td class="flag-cell">${material.requiresFoamWarning ? '*' : ''}</td>
      <td>${escape(material.name)}</td>
      <td class="numeric-cell">${escape(formatGalPerSk(material.volume, 3))}</td>
      <td>${escape(material.preferredContainer)}</td>
      <td>${escape(material.capturedContainer)}</td>
      <td class="numeric-cell">${escape(formatFixed(material.labWeight, 3))}</td>
      <td class="numeric-cell">${escape(material.measuredWeight)}</td>
    </tr>`).join('') : liquidMaterials.map(material => `
    <tr class="${material.requiresFoamWarning ? 'warning-material-row' : ''}">
      <td class="flag-cell">${material.requiresFoamWarning ? '*' : ''}</td>
      <td>${escape(material.name)}</td>
      <td class="numeric-cell">${escape(formatGalPerSk(material.volume))}</td>
      <td>${escape(material.preferredContainer)}</td>
      <td class="location-cell">${escape(material.containerLocation || '--')}</td>
      <td class="numeric-cell">${escape(formatFixed(material.labWeight, 2))}</td>
    </tr>`).join(''))
    || `<tr><td colspan="${isBatchSpecific ? 7 : 6}">No liquid materials</td></tr>`;

  const thickeningScheduleHtml = (isReference: boolean): string => `
    <div class="thickening-layout">
      <div class="schedule-summary-row">
        <span class="field-caption">${isReference ? 'Thickening Time Schedule:' : 'Schedule:'}</span>
        <span class="plain-value">${escape(this.getThickeningScheduleSummary())}</span>
      </div>
      <div class="schedule-grid">
        ${renderScheduleTable('Temperature', ['Action', 'Duration', 'Target'], this.getThickeningTemperatureSchedule())}
        ${renderScheduleTable('Pressure', ['Action', 'Duration', 'Target'], this.getThickeningPressureSchedule())}
        ${renderScheduleTable('Motor', ['Action', 'Duration'], this.getThickeningMotorSchedule())}
      </div>
      ${isReference ? '' : `
        <div class="schedule-target-row">
          <div class="target-item"><span class="field-caption">Target Thickening Time From (hh:min):</span><span class="field-value-inline">${escape(this.targetThickeningTimeFrom || '--')}</span></div>
          <div class="target-item"><span class="field-caption">Target Thickening Time To (hh:min):</span><span class="field-value-inline">${escape(this.targetThickeningTimeTo || '--')}</span></div>
        </div>`}
    </div>`;

  const getFieldValue = (
    test: PrintableBatchTestEntry & { isReference: boolean; },
    label: string,
    blockTitle?: string
  ): string => {
    const normalizedLabel = label.trim().toLowerCase();
    for (const block of test.blocks) {
      if (blockTitle && (block.title ?? '') !== blockTitle) {
        continue;
      }

      const match = block.fields.find(field => field.label.trim().toLowerCase() === normalizedLabel);
      if (match) {
        return match.value;
      }
    }

    return '--';
  };
  const getNotesValue = (test: PrintableBatchTestEntry & { isReference: boolean; }): string =>
    test.blocks.find(block => (block.title ?? '') === 'Notes')?.fields[0]?.value ?? '--';
  const renderMetaFields = (items: Array<{ label: string; value: string; }>): string => {
    const populated = items.filter(item => item.value && item.value !== '--');
    if (populated.length === 0) {
      return '';
    }

    return `
      <div class="legacy-meta-row">
        ${populated.map(item => `
          <div class="legacy-meta-item">
            <span class="plain-caption">${escape(item.label)}</span>
            <span class="field-value-inline">${escape(item.value)}</span>
          </div>`).join('')}
      </div>`;
  };
  const renderNotesBand = (value: string, warningText = ''): string => `
    <div class="notes-band">
      <span class="notes-caption">Notes:</span>
      ${warningText ? `<span class="warning-inline">${escape(warningText)}</span>` : ''}
      <span class="notes-value">${escape(value || '--')}</span>
    </div>`;
  const renderLegacyGrid = (
    tableClass: string,
    headerCells: string,
    bodyRows: string
  ): string => `
    <table class="legacy-grid ${tableClass}">
      <thead><tr>${headerCells}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>`;
  const renderInputCell = (colspan = 1, extraClass = ''): string =>
    `<td class="legacy-input-cell${extraClass ? ` ${extraClass}` : ''}"${colspan > 1 ? ` colspan="${colspan}"` : ''}></td>`;
  const renderCommentCell = (colspan = 1): string =>
    `<td class="legacy-input-cell legacy-comment-cell"${colspan > 1 ? ` colspan="${colspan}"` : ''}></td>`;
  const renderCaptionCell = (label: string, colspan = 1): string =>
    `<td class="legacy-caption-cell"${colspan > 1 ? ` colspan="${colspan}"` : ''}>${escape(label)}</td>`;
  const renderBlankCell = (colspan = 1): string =>
    `<td${colspan > 1 ? ` colspan="${colspan}"` : ''}></td>`;
  const renderFluidLossSection = (test: PrintableBatchTestEntry & { isReference: boolean; }): string => `
    ${renderMetaFields([
      { label: 'Test Type:', value: getFieldValue(test, 'Fluid Loss Type', 'Basic Info') },
      { label: 'Temperature:', value: getFieldValue(test, 'Temperature (F)', 'Basic Info') }
    ])}
    ${renderNotesBand(getNotesValue(test), this.shouldShowFoamerFluidLossWarning() ? '* DO NOT RUN FLUID LOSS TEST WITH FOAMERS' : '')}
    ${renderLegacyGrid(
      'fluid-loss-grid',
      [
        'Temp (°F)',
        '1/2 minute Filtrate Vol. (mls)',
        '1 minute Filtrate Vol. (mls)',
        '5 minute Filtrate Vol. (mls)',
        '10 minute Filtrate Vol. (mls)',
        '15 minute Filtrate Vol. (mls)',
        '20 minute Filtrate Vol. (mls)',
        '30 minute Filtrate Vol. (mls)',
        'Total Filtrate Vol. (mls)',
        'Total Test Time (mins)',
        'Fluid Loss Calculated (mls)'
      ].map(text => `<th>${escape(text)}</th>`).join(''),
      `
        <tr>${new Array(11).fill('').map(() => renderInputCell()).join('')}</tr>
        <tr>${renderCaptionCell('Started By')}${renderInputCell(2)}${renderCaptionCell('Test Date')}${renderInputCell(2)}${renderBlankCell(5)}</tr>
        <tr>${renderCaptionCell('Stopped By')}${renderInputCell(2)}${renderCaptionCell('Equip. No.')}${renderInputCell(2)}${renderBlankCell(5)}</tr>
        <tr>${renderCaptionCell('Comments')}${renderCommentCell(10)}</tr>`
    )}`;
  const renderFreeFluidSection = (test: PrintableBatchTestEntry & { isReference: boolean; }): string => `
    ${renderMetaFields([
      { label: 'Temperature:', value: getFieldValue(test, 'Temperature (F)', 'Basic Info') },
      { label: 'Angle:', value: getFieldValue(test, 'Angle', 'Basic Info') }
    ])}
    ${renderNotesBand(getNotesValue(test))}
    ${renderLegacyGrid(
      'free-fluid-grid',
      [
        '<th>Temp (°F)</th>',
        '<th>mls FF</th>',
        '<th>% FF</th>',
        '<th>Angle</th>',
        '<th colspan="4">Comments (channel, mottle, etc)</th>'
      ].join(''),
      `
        <tr>${renderInputCell()}${renderInputCell()}${renderInputCell()}${renderInputCell()}${renderCommentCell(4)}</tr>
        <tr>${renderCaptionCell('Started By')}${renderInputCell()}${renderCaptionCell('Stopped By')}${renderInputCell()}${renderCaptionCell('Start Time')}${renderInputCell()}${renderCaptionCell('Test Date')}${renderInputCell()}</tr>`
    )}`;
  const renderRheologySection = (test: PrintableBatchTestEntry & { isReference: boolean; }): string => {
    const isSurface = getFieldValue(test, 'Rheology Type', 'Basic Info').toLowerCase().includes('surface');
    return `
      ${renderMetaFields([
        { label: 'Bob / Sleeve:', value: getFieldValue(test, 'Bob / Sleeve', 'Basic Info') },
        { label: 'Temperature:', value: getFieldValue(test, 'Temperature (F)', 'Basic Info') }
      ])}
      ${renderNotesBand(getNotesValue(test))}
      ${renderLegacyGrid(
        isSurface ? 'surface-rheology-grid' : 'downhole-rheology-grid',
        [
          '<th>Temp (°F)</th>',
          '<th>Spring Factor</th>',
          '<th>600</th>',
          '<th>300</th>',
          '<th>200</th>',
          '<th>100</th>',
          '<th>60</th>',
          '<th>30</th>',
          '<th>20</th>',
          '<th>10</th>',
          '<th>6</th>',
          '<th>3</th>'
        ].join(''),
        `
          <tr>${new Array(12).fill('').map(() => renderInputCell()).join('')}</tr>
          <tr>${new Array(12).fill('').map(() => renderInputCell()).join('')}</tr>
          <tr>
            ${renderCaptionCell('Started By')}${renderInputCell()}${renderCaptionCell('Stopped By')}${renderInputCell()}
            ${renderCaptionCell('Test Date')}${renderInputCell()}${renderCaptionCell('Equip. No.')}${renderInputCell()}
            ${isSurface ? `${renderCaptionCell('10 sec.')}${renderInputCell()}${renderCaptionCell('10 min.')}${renderInputCell()}` : renderBlankCell(4)}
          </tr>
          ${isSurface ? `<tr>${renderCaptionCell('Comments')}${renderCommentCell(11)}</tr>` : ''}
          ${isSurface ? '' : `<tr>${renderCaptionCell('Comments')}${renderCommentCell(11)}</tr>`}`
      )}`;
  };
  const renderHpHtRheologySection = (test: PrintableBatchTestEntry & { isReference: boolean; }): string => `
    ${renderMetaFields([
      { label: 'Equipment:', value: getFieldValue(test, 'Equipment', 'Basic Info') },
      { label: 'Temperature:', value: getFieldValue(test, 'Temperature (F)', 'Basic Info') },
      { label: 'Pressure:', value: getFieldValue(test, 'Pressure (psi)', 'Basic Info') },
      { label: 'Total Time:', value: getFieldValue(test, 'Total Time (min)', 'Basic Info') }
    ])}
    ${renderNotesBand(getNotesValue(test))}
    ${renderLegacyGrid(
      'downhole-rheology-grid',
      [
        '<th>Temp (°F)</th>',
        '<th>Spring Factor</th>',
        '<th>600</th>',
        '<th>300</th>',
        '<th>200</th>',
        '<th>100</th>',
        '<th>60</th>',
        '<th>30</th>',
        '<th>20</th>',
        '<th>10</th>',
        '<th>6</th>',
        '<th>3</th>'
      ].join(''),
      `
        <tr>${new Array(12).fill('').map(() => renderInputCell()).join('')}</tr>
        <tr>${new Array(12).fill('').map(() => renderInputCell()).join('')}</tr>
        <tr>${renderCaptionCell('Started By')}${renderInputCell()}${renderCaptionCell('Stopped By')}${renderInputCell()}${renderCaptionCell('Test Date')}${renderInputCell()}${renderCaptionCell('Equip. No.')}${renderInputCell()}${renderBlankCell(4)}</tr>
        <tr>${renderCaptionCell('Comments')}${renderCommentCell(11)}</tr>`
    )}`;
  const renderStaticGelSection = (test: PrintableBatchTestEntry & { isReference: boolean; }): string => `
    ${renderMetaFields([
      { label: 'JPT:', value: getFieldValue(test, 'JPT', 'Basic Info') },
      { label: 'BHP:', value: getFieldValue(test, 'Final Hydrostatic Pressure', 'Basic Info') },
      { label: 'Temperature:', value: getFieldValue(test, 'Temperature (F)', 'Basic Info') }
    ])}
    ${renderNotesBand(getNotesValue(test))}
    ${renderLegacyGrid(
      'static-gel-grid',
      [
        '<th>Temp (°F)</th>',
        '<th>Pressure (psi)</th>',
        '<th>JPT (hh:mm)</th>',
        '<th colspan="2">Time to 100lb/100ft2</th>',
        '<th colspan="2">Time to 500lb/100ft2</th>',
        `<th colspan="2">${escape(getFieldValue(test, 'CSGS', 'Basic Info') !== '--' ? 'Time to CSGS' : 'Time to CSGS')}</th>`
      ].join(''),
      `
        <tr>${renderInputCell()}${renderInputCell()}${renderInputCell()}${renderInputCell(2)}${renderInputCell(2)}${renderInputCell(2)}</tr>
        <tr>${renderCaptionCell('Started By')}${renderInputCell()}${renderCaptionCell('Start Time')}${renderInputCell()}${renderCaptionCell('Start Date')}${renderInputCell()}${renderBlankCell(3)}</tr>
        <tr>${renderCaptionCell('Stopped By')}${renderInputCell()}${renderCaptionCell('Stop Time')}${renderInputCell()}${renderCaptionCell('Stop Date')}${renderInputCell()}${renderCaptionCell('Equip. No.')}${renderInputCell()}</tr>
        <tr>${renderCaptionCell('Comments')}${renderCommentCell(8)}</tr>`
    )}`;
  const renderCompressiveSection = (test: PrintableBatchTestEntry & { isReference: boolean; }): string => `
    ${renderMetaFields([
      { label: 'Temperature Ramp Time:', value: getFieldValue(test, 'Temperature Ramp Time (h)', 'Basic Info') },
      { label: 'Temperature:', value: getFieldValue(test, 'Temperature (F)', 'Basic Info') },
      { label: 'Pressure:', value: getFieldValue(test, 'Pressure (psi)', 'Basic Info') }
    ])}
    ${renderNotesBand(getNotesValue(test))}
    ${renderLegacyGrid(
      'compressive-grid',
      [
        '<th>Cond. Time (mins)</th>',
        '<th>Cond. Temp (°F)</th>',
        '<th>Test Temp (°F)</th>',
        '<th>Pressure (psi)</th>',
        '<th>100 psi (hh:mm)</th>',
        '<th>500 psi (hh:mm)</th>',
        '<th>12 hr (psi)</th>',
        '<th>24 hr (psi)</th>',
        '<th>48 hr (psi)</th>',
        '<th>End (psi)</th>',
        '<th>End Time (hrs)</th>'
      ].join(''),
      `
        <tr>${new Array(11).fill('').map(() => renderInputCell()).join('')}</tr>
        <tr>${renderBlankCell(2)}${renderCaptionCell('Started By')}${renderInputCell()}${renderCaptionCell('Start Time')}${renderInputCell()}${renderCaptionCell('Start Date')}${renderInputCell()}${renderBlankCell(3)}</tr>
        <tr>${renderBlankCell(2)}${renderCaptionCell('Stopped By')}${renderInputCell()}${renderCaptionCell('Stop Time')}${renderInputCell()}${renderCaptionCell('Stop Date')}${renderInputCell()}${renderCaptionCell('Equip. No.')}${renderInputCell()}</tr>
        <tr>${renderCaptionCell('Comments')}${renderCommentCell(10)}</tr>`
    )}`;
  const renderFoamStabilitySection = (test: PrintableBatchTestEntry & { isReference: boolean; }): string => `
    ${renderMetaFields([
      { label: 'Foamed Density:', value: getFieldValue(test, 'Foamed Density', 'Basic Info') },
      { label: 'Test Type:', value: getFieldValue(test, 'Foam Stability Type', 'Basic Info') }
    ])}
    ${renderNotesBand(getNotesValue(test))}
    ${renderLegacyGrid(
      'foam-stability-grid',
      [
        '<th>Foamed Density (ppg)</th>',
        '<th>Temp (°F)</th>',
        '<th>Stable(Y/N)</th>',
        '<th colspan="4">Comments (segregation, channeling, bubble breakout, etc.)</th>'
      ].join(''),
      `
        <tr>${renderInputCell()}${renderInputCell()}${renderInputCell()}${renderCommentCell(4)}</tr>
        <tr>${renderBlankCell(2)}${renderCaptionCell('Started By')}${renderInputCell()}${renderCaptionCell('Start Time')}${renderInputCell()}${renderBlankCell()}</tr>
        <tr>${renderBlankCell(2)}${renderCaptionCell('Stopped By')}${renderInputCell()}${renderBlankCell(3)}</tr>`
    )}`;

  const testSections = tests.map(test => {
    const visibleBlocks = isBatchSpecific && test.kind === 'Thickening Time'
      ? test.blocks.map(block => ({
          ...block,
          fields: (block.title ?? '') === 'Basic Info'
            ? block.fields.filter(field => {
                const normalizedLabel = field.label.trim().toLowerCase();
                return normalizedLabel !== 'target thickening time from'
                  && normalizedLabel !== 'target thickening time to';
              })
            : block.fields
        })).filter(block => block.fields.length > 0)
      : test.blocks;
    const genericBlocks = visibleBlocks.map(block => {
      if ((block.title ?? '') === 'Notes') {
        return renderNotesBand(block.fields[0]?.value ?? '--');
      }

      return `
          <div class="test-block">
            ${block.title ? `<div class="test-block-title">${escape(block.title)}</div>` : ''}
            <div class="field-wrap">
              ${renderFieldItems(block.fields, 'field-item-wide')}
            </div>
          </div>`;
    }).join('');
    const bodyHtml = (() => {
      switch (test.kind) {
        case 'Fluid Loss':
          return renderFluidLossSection(test);
        case 'Free Fluid':
          return renderFreeFluidSection(test);
        case 'Rheology':
          return renderRheologySection(test);
        case 'HP/HT Rheology':
          return renderHpHtRheologySection(test);
        case 'Static Gel Strength':
          return renderStaticGelSection(test);
        case 'Compressive Strength':
        case 'Foam Compressive Strength':
          return renderCompressiveSection(test);
        case 'Foam Stability':
          return renderFoamStabilitySection(test);
        default:
          return genericBlocks;
      }
    })();

    return `
      <section class="test-section">
        <div class="test-title-row">
          <span class="test-title">${escape(test.displayLabel)}</span>
          ${test.isReference ? '<span class="test-reference-tag">(reference)</span>' : ''}
        </div>
        ${test.kind === 'Thickening Time' ? thickeningScheduleHtml(test.isReference) : ''}
        ${bodyHtml}
        ${isBatchSpecific && !test.isReference ? `
          <div class="batch-entry-row">
            <div class="batch-entry-label">Batch#</div>
            <div class="batch-entry-value"></div>
          </div>` : ''}
      </section>`;
  }).join('');

  const mixabilityHtml = !isBatchSpecific && (this.mixScore != null || this.mixTimeToWetSolids != null || this.mixNotes.trim().length > 0)
    ? `
      <section class="sheet-band sheet-band-tight">
        <div class="mixability-row">
          <span class="mixability-label">Mixability</span>
          <span>Score:</span>
          <span class="field-value-inline">${escape(this.formatPrintableValue(this.mixScore))}</span>
          <span>Time To Wet Solids (sec):</span>
          <span class="field-value-inline">${escape(this.formatPrintableValue(this.mixTimeToWetSolids))}</span>
          <span>Notes:</span>
          <span class="field-value-inline">${escape(this.formatPrintableValue(this.mixNotes))}</span>
        </div>
      </section>`
    : '';
  const slurryNotesHtml = this.notes1.trim().length > 0
    ? `
      <section class="sheet-band sheet-band-tight">
        <div class="mixability-row">
          <span class="plain-caption">Notes:</span>
          <span class="plain-value">${escape(this.notes1)}</span>
        </div>
      </section>`
    : '';

  const html = `<!doctype html>
<html>
  <head>
  <meta charset="utf-8" />
  <title>${escape(title)} - Slurry ${escape(this.ufid || this.slurryId)}</title>
  <style>
    @page { margin: 0.75in 0.5in 0.5in; }
    * { box-sizing: border-box; }
    html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: 'Times New Roman', serif; font-size: 13px; margin: 0; color: #000; text-rendering: geometricPrecision; }
    .sheet-root { width: 100%; }
    .header-box, .sheet-band, .materials-box, .notes-band, .batch-entry-row { border: 1px solid #000; }
    .header-box { margin-bottom: 0; padding: 8px 4px 6px; }
    .header-row { display: flex; flex-wrap: wrap; column-gap: 20px; row-gap: 2px; min-height: 18px; }
    .header-field { display: inline-flex; align-items: baseline; }
    .header-label { font-weight: bold; margin-right: 4px; }
    .header-value { display: inline-block; min-width: 72px; line-height: 1.05; padding: 0 0 1px; text-decoration: underline; text-underline-offset: 1px; }
    .sheet-band { border-left-width: 0; border-right-width: 0; padding: 0; }
    .sheet-band-tight { padding: 0; }
    .field-wrap { display: flex; flex-wrap: wrap; gap: 0; margin: 2px 0 5px; }
    .field-item { display: inline-flex; align-items: baseline; margin: 1px 0; padding-right: 8px; }
    .field-item-compact { width: 140px; }
    .field-item-wide { width: 180px; }
    .field-caption, .plain-caption { margin-right: 3px; }
    .field-value, .field-value-inline, .plain-value { font-weight: bold; white-space: pre-wrap; }
    .materials-box { background: Linen; padding: 0 0 4px; }
    .material-title { font-size: 13px; font-weight: bold; margin: 2px 0 2px; }
    .warning-banner { margin: 2px 0 0; color: #c00000; font-size: 12px; font-weight: bold; }
    table { border-collapse: collapse; width: 100%; }
    .materials-table { margin-bottom: 5px; font-size: 13px; }
    .materials-table th, .materials-table td { padding: 2px 2px 1px 0; border: 0; text-align: center; vertical-align: top; line-height: 1.08; }
    .materials-table thead th { font-weight: bold; }
    .materials-table tbody tr { background: #EDE4DC; }
    .materials-table th:nth-child(2),
    .materials-table td:nth-child(2) { text-align: left; }
    .flag-cell { width: 12px; color: #c00000; font-weight: bold; text-align: left !important; }
    .numeric-cell { text-align: center; white-space: nowrap; }
    .location-cell { text-align: center; padding: 1px 8px; }
    .warning-material-row td { color: #c00000; }
    .mixability-row { display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px; padding: 2px 0; }
    .mixability-label { font-weight: bold; margin-right: 10px; }
    .test-section { border-bottom: 1px solid #000; padding: 3px 0 8px; break-inside: avoid-page; page-break-inside: avoid; }
    .test-title-row { display: flex; align-items: baseline; gap: 8px; margin-top: 3px; }
    .test-title { font-weight: bold; font-size: 14px; }
    .test-reference-tag { color: #444; font-size: 12px; }
    .test-block { margin-top: 4px; }
    .test-block-title { font-weight: bold; margin: 0 0 2px; }
    .legacy-meta-row { display: flex; flex-wrap: wrap; align-items: baseline; gap: 7px; margin: 2px 0 2px 10px; }
    .legacy-meta-item { display: inline-flex; align-items: baseline; }
    .notes-band { display: flex; align-items: baseline; gap: 8px; margin-top: 2px; margin-bottom: 2px; padding: 2px 10px; }
    .notes-caption { white-space: nowrap; }
    .warning-inline { color: #c00000; font-size: 12px; font-weight: bold; white-space: nowrap; }
    .notes-value { background: yellow; white-space: pre-wrap; padding: 0 2px; flex: 1; min-height: 16px; }
    .thickening-layout { margin-top: 3px; }
    .schedule-summary-row { display: flex; gap: 6px; align-items: baseline; margin: 2px 0 0 10px; }
    .schedule-grid { display: flex; gap: 5px; align-items: flex-start; margin-top: 5px; }
    .schedule-box { border: 1px solid #808080; padding: 0 5px 5px; }
    .schedule-box-title { font-weight: bold; text-align: center; margin: 0 0 5px; }
    .schedule-table { width: auto; font-size: 12px; }
    .schedule-table th, .schedule-table td { padding: 2px 6px 0 0; border: 0; text-align: left; }
    .schedule-table thead th { text-decoration: underline; font-weight: normal; }
    .schedule-target-row { display: flex; flex-wrap: wrap; gap: 7px; margin: 2px 0 2px 10px; }
    .target-item { display: inline-flex; gap: 4px; align-items: baseline; }
    .legacy-grid { border: 1px solid #000; margin-top: 2px; font-size: 14px; }
    .legacy-grid th,
    .legacy-grid td { border: 1px solid #000; border-right-width: 0; padding: 3px; vertical-align: middle; text-align: center; }
    .legacy-grid thead th { background: #D3D3D3; font-weight: normal; }
    .legacy-grid td { height: 23px; text-align: left; }
    .legacy-input-cell { background: #EFEFEF; }
    .legacy-comment-cell { background: #EFEFEF; }
    .legacy-caption-cell { background: #FFF; }
    .legacy-grid .legacy-caption-cell { white-space: nowrap; }
    .legacy-grid .legacy-input-cell,
    .legacy-grid .legacy-comment-cell { min-height: 23px; }
    .legacy-grid tbody td:last-child,
    .legacy-grid thead th:last-child { border-right-width: 1px; }
    .legacy-grid td[colspan],
    .legacy-grid th[colspan] { text-align: center; }
    .batch-entry-row { display: grid; grid-template-columns: 90px 1fr; margin-top: 4px; }
    .batch-entry-label { border-right: 1px solid #000; padding: 4px 6px; }
    .batch-entry-value { min-height: 24px; background: #efefef; }
  </style>
  </head>
  <body>
  <div class="sheet-root">
    <section class="header-box">
      <div class="header-row">
        <div class="header-field"><span class="header-label">Project:</span><span class="header-value">${renderHeaderValue(this.projectName)}</span></div>
        <div class="header-field"><span class="header-label">Slurry:</span><span class="header-value">${renderHeaderValue(this.ufid || this.slurryId)}</span></div>
        ${isBatchSpecific ? `<div class="header-field"><span class="header-label">Batch:</span><span class="header-value">${renderHeaderValue(this.selectedBatch?.batchNumber)}</span></div>` : ''}
        <div class="header-field"><span class="header-label">Project By:</span><span class="header-value">${renderHeaderValue(this.projectReceivedBy)}</span></div>
      </div>
      <div class="header-row">
        ${this.projectRigName ? `<div class="header-field"><span class="header-label">Rig Name:</span><span class="header-value">${renderHeaderValue(this.projectRigName)}</span></div>` : ''}
        <div class="header-field"><span class="header-label">Well Name:</span><span class="header-value">${renderHeaderValue(this.projectWellName)}</span></div>
      </div>
      <div class="header-row">
        ${this.jobType ? `<div class="header-field"><span class="header-label">Job Type:</span><span class="header-value">${renderHeaderValue(this.jobType)}</span></div>` : ''}
        ${this.projectPipeSize ? `<div class="header-field"><span class="header-label">Pipe Size:</span><span class="header-value">${renderHeaderValue(this.projectPipeSize)}</span></div>` : ''}
      </div>
    </section>
    <section class="sheet-band">
      <div class="field-wrap">${renderFieldItems(mixInfoFields, 'field-item-compact')}</div>
    </section>
    <section class="sheet-band">
      <div class="field-wrap">${renderFieldItems(infoFields, 'field-item-wide')}</div>
    </section>
    <section class="materials-box">
      ${this.shouldShowFoamerFluidLossWarning() ? '<div class="warning-banner">* DO NOT RUN FLUID LOSS TEST WITH FOAMERS</div>' : ''}
      <div class="material-title">Dry Materials</div>
      <table class="materials-table">
        <colgroup>
          ${isBatchSpecific
            ? '<col style="width:12px"><col style="width:120px"><col style="width:60px"><col style="width:50px"><col style="width:120px"><col style="width:120px"><col style="width:70px"><col style="width:100px">'
            : '<col style="width:12px"><col style="width:120px"><col style="width:80px"><col style="width:80px"><col style="width:130px"><col style="width:200px"><col style="width:80px">'}
        </colgroup>
        <thead>
          ${isBatchSpecific
            ? '<tr><th class="flag-cell"></th><th>Material Name</th><th>Percent(%)</th><th>Weight</th><th>Preferred Container</th><th>Captured Container</th><th>Lab Weight</th><th>Measured Weight</th></tr>'
            : '<tr><th class="flag-cell"></th><th>Material Name</th><th>Percent (%)</th><th>Weight(lb)</th><th>Preferred Container</th><th>Container Location</th><th>Lab Weight</th></tr>'}
        </thead>
        <tbody>${dryMaterialRows}</tbody>
      </table>
      <div class="material-title">Liquid Materials</div>
      <table class="materials-table">
        <colgroup>
          ${isBatchSpecific
            ? '<col style="width:12px"><col style="width:120px"><col style="width:110px"><col style="width:120px"><col style="width:120px"><col style="width:70px"><col style="width:100px">'
            : '<col style="width:12px"><col style="width:120px"><col style="width:160px"><col style="width:130px"><col style="width:200px"><col style="width:80px">'}
        </colgroup>
        <thead>
          ${isBatchSpecific
            ? '<tr><th class="flag-cell"></th><th>Material Name</th><th>Volume</th><th>Preferred Container</th><th>Captured Container</th><th>Lab Weight</th><th>Measured Weight</th></tr>'
            : '<tr><th class="flag-cell"></th><th>Material Name</th><th>Gal/Sk</th><th>Preferred Container</th><th>Container Location</th><th>Lab Weight</th></tr>'}
        </thead>
        <tbody>${liquidMaterialRows}</tbody>
      </table>
    </section>
    ${mixabilityHtml}
    ${slurryNotesHtml}
    ${testSections}
  </div>
  </body>
</html>`;
  this.writePrintWindow(popup, html);
  }

  printSelectedBatch(): void {
    if (!this.selectedBatch) {
      return;
    }

    this.printBatchTestSheet('Print Batch Tests', this.collectPrintableSelectedBatchTests(), true);
  }

  private applySelectedBatch(batch: SlurryBatchDto): void {
    this.selectedBatch = batch;
    this.selectedBatchTestInputMasterIds = batch.tests.map(test => test.testInputMasterId);
    this.selectedBatchBlendMeasurement = false;
    this.batchHeader.notes = batch.notes ?? '';
    this.batchHeader.batchLabVolume = batch.batchLabVolume ?? batch.targetLabVolume ?? this.defaultBatchLabVolume;
    this.batchHeader.foamDensity = batch.foamDensity ?? null;
    this.batchHeader.measuredBlendWeight = batch.measuredBlendWeight ?? null;
    this.batchHeader.measurementTolerance = batch.measurementTolerance ?? null;
    this.batchHeader.excessFoamPercent = batch.excessFoamPercent ?? null;
    this.batchHeader.capturedBlendCisProcontainerId = batch.capturedBlendCisProcontainerId ?? '';
    this.batchHeader.replacePreferredBlendCisProcontainerId = batch.replacePreferredBlendCisProcontainerId ?? false;
    this.batchHeader.blendContainerComments = batch.blendContainerComments ?? '';
    this.batchHeader.blendWeightComments = batch.blendWeightComments ?? '';
    this.batchHeader.measuredMudCisProcontainer = batch.measuredMudCisProcontainer ?? '';
    const requiredMaterialIds = new Set((batch.materialOptions ?? [])
      .filter(option => this.isRequiredBatchMaterialOption(option))
      .map(option => option.slurryMaterialId));
    this.selectedFoamerMaterialIds = (batch.selectedFoamerMaterialIds ?? [])
      .filter(slurryMaterialId => requiredMaterialIds.has(slurryMaterialId));
    this.batchToleranceSpecified = batch.measurementTolerance != null;
    this.batchMixAsFoam = batch.foamDensity != null || batch.excessFoamPercent != null;
    this.batchLabVolumeBeforeMixAsFoam = this.batchMixAsFoam
      ? (batch.targetLabVolume ?? batch.batchLabVolume ?? null)
      : (batch.batchLabVolume ?? null);
  }

  private clearSelectedBatch(): void {
    this.selectedBatchId = null;
    this.selectedBatch = null;
    this.selectedBatchTestInputMasterIds = [];
    this.selectedBatchBlendMeasurement = false;
    this.batchHeader = {
      notes: '',
      batchLabVolume: null,
      foamDensity: null,
      measuredBlendWeight: null,
      measurementTolerance: null,
      excessFoamPercent: null,
      capturedBlendCisProcontainerId: '',
      replacePreferredBlendCisProcontainerId: false,
      blendContainerComments: '',
      blendWeightComments: '',
      measuredMudCisProcontainer: ''
    };
    this.batchMixAsFoam = false;
    this.selectedFoamerMaterialIds = [];
    this.batchLabVolumeBeforeMixAsFoam = null;
    this.measuredMaterials = [];
    this.measuredFoam = null;
    this.measuredMud = null;
    this.persistedBatchMaterialsLocked = false;
    this.batchMeasurementsLoading = false;
    this.batchMeasurementsLoadError = '';
  }

  private buildSelectedBatchTestRow(testInputMasterId: number, currentTest?: { status: number } | null): { testInputMasterId: number; displayLabel: string; selected: boolean; locked: boolean; statusLabel: string; } {
    return {
      testInputMasterId,
      displayLabel: this.getBatchTestDisplayLabel(testInputMasterId),
      selected: this.selectedBatchTestInputMasterIds.includes(testInputMasterId),
      locked: this.isSelectedBatchTestLocked(testInputMasterId),
      statusLabel: currentTest ? this.batchTestStatusLabel(currentTest.status) : ''
    };
  }

  private sortSelectedBatchTests(rows: Array<{ testInputMasterId: number; displayLabel: string; selected: boolean; locked: boolean; statusLabel: string; }>): Array<{ testInputMasterId: number; displayLabel: string; selected: boolean; locked: boolean; statusLabel: string; }> {
    return rows.slice().sort((left, right) => {
    const orderDifference = this.getBatchDetailsLegacyTestOrder(left.displayLabel) - this.getBatchDetailsLegacyTestOrder(right.displayLabel);
    if (orderDifference !== 0) {
      return orderDifference;
    }

    const labelDifference = left.displayLabel.localeCompare(right.displayLabel);
    if (labelDifference !== 0) {
      return labelDifference;
    }

    return left.testInputMasterId - right.testInputMasterId;
    });
  }

  private getBatchDetailsLegacyTestOrder(displayLabel: string): number {
    const matchedIndex = this.batchDetailsLegacyTestOrder.findIndex(prefix => displayLabel === prefix || displayLabel.startsWith(`${prefix} #`));
    return matchedIndex >= 0 ? matchedIndex : this.batchDetailsLegacyTestOrder.length;
  }

  private getDryBlendBaseMaterialOptions(): BatchMaterialOptionDto[] {
    return this.getDryBatchBlendMaterialOptions();
  }

  hasPersistedBlendMeasurement(): boolean {
    return this.batchHeader.measuredBlendWeight != null
      || !!this.batchHeader.capturedBlendCisProcontainerId?.trim();
  }

  private shouldShowDryBatchMaterialOption(option: BatchMaterialOptionDto): boolean {
    return this.isRequiredBatchMaterialOption(option);
  }

  private isRequiredBatchMaterialOption(option: BatchMaterialOptionDto): boolean {
    return option.isMeasurementRequired
      ?? (option.sectionType === 'CementBase'
      || option.sectionType === 'LiquidBase'
      || (option.calculatedLabWeight ?? 0) > 0);
  }

  private loadSelectedBatchMeasurements(batchId: number): void {
    const loadGeneration = ++this.batchMeasurementsLoadGeneration;
    this.batchMeasurementsLoading = true;
    this.batchMeasurementsLoadError = '';
    this.persistedBatchMaterialsLocked = true;

    forkJoin({
      materials: this.slurryService.getMeasuredMaterials(this.slurryId, batchId),
      foam: this.slurryService.getMeasuredFoam(this.slurryId, batchId),
      mud: this.slurryService.getMeasuredMud(this.slurryId, batchId)
    }).subscribe({
    next: ({ materials, foam, mud }) => {
      if (this.selectedBatchId !== batchId || this.batchMeasurementsLoadGeneration !== loadGeneration) { return; }
      this.batchMeasurementsLoading = false;
      this.measuredMaterials = materials;
      this.measuredFoam = foam;
      this.measuredFoamForm = {
        measuredWeight: foam?.measuredWeight ?? null,
        cupWeight: foam?.cupWeight ?? null,
        cupVolume: foam?.cupVolume ?? null,
        weightComments: foam?.weightComments ?? ''
      };
      this.measuredMud = mud;
      this.measuredMudForm = {
        measuredMudCisProcontainer: mud?.measuredMudCisProcontainer ?? '',
        mudContainerComments: mud?.mudContainerComments ?? '',
        preferredMudCisProcontainer: mud?.preferredMudCisProcontainer ?? '',
        replacePreferredMudCisProcontainer: mud?.replacePreferredMudCisProcontainer ?? false
      };
      this.persistedBatchMaterialsLocked = this.hasPersistedBatchMeasurementState(materials, foam, mud);
      if (this.batchMaterialOptions.length === 0) {
        this.measuredMaterialForm.slurryMaterialId = null;
        this.selectedBatchBlendMeasurement = false;
        return;
      }

      if (
        this.measuredMaterialForm.slurryMaterialId != null
        && !this.batchMaterialOptions.some(option => option.slurryMaterialId === this.measuredMaterialForm.slurryMaterialId)
      ) {
        this.measuredMaterialForm.slurryMaterialId = null;
      }

      if (this.measuredMaterialForm.slurryMaterialId != null) {
        this.onMeasuredMaterialSelectionChange();
      }
    },
      error: (err) => {
        if (this.selectedBatchId !== batchId || this.batchMeasurementsLoadGeneration !== loadGeneration) { return; }
        this.batchMeasurementsLoading = false;
        this.batchMeasurementsLoadError = this.getApiErrorMessage(err, 'Failed to load saved measurements. Retry by reopening the batch.');
        console.error('Failed to load measured materials:', err);
      }
    });
  }

  private redoSelectedBatchSection(
    action: () => Observable<BatchExecutionResultDto>,
    successMessage: string): void {
    if (this.selectedBatchId == null) {
      return;
    }

    this.batchDetailsMessage = '';
    action().subscribe({
      next: (result) => {
        this.applySelectedBatch(result.batch);
        this.batchDetailsMessage = successMessage;
        this.loadSelectedBatchMeasurements(this.selectedBatchId as number);
        this.loadBatches();
      },
      error: (err) => {
        console.error('Failed to redo batch section:', err);
        this.batchDetailsMessage = this.getApiErrorMessage(err, 'Failed to redo batch section.');
      }
    });
  }

  private confirmBatchReset(title: string, description: string): boolean {
    return window.confirm(`${title}\n\n${description}`);
  }

  private hasPersistedBatchMeasurementState(
    materials: MeasuredMaterialDto[],
    foam: MeasuredFoamDto | null,
    mud: MeasuredMudDto | null): boolean {
    return materials.length > 0
      || this.hasPersistedBlendMeasurement()
      || foam != null
      || !!mud?.measuredMudCisProcontainer;
  }

  private hasAnyBatchMeasurementCapture(): boolean {
    return this.measuredMaterials.length > 0
      || this.hasMeasuredFoamWeight()
      || this.hasMeasuredFoamCup()
      || this.hasMeasuredMudCapture();
  }

  private hasAnyMeasurementSessionDraft(): boolean {
    return this.hasAnyBatchMeasurementCapture()
      || this.batchHeader.measuredBlendWeight != null
      || !!this.batchHeader.capturedBlendCisProcontainerId
      || !!this.batchHeader.blendContainerComments?.trim()
      || !!this.batchHeader.blendWeightComments?.trim()
      || this.batchHeader.replacePreferredBlendCisProcontainerId;
  }

  private hasAnyMeasuredBatchMaterials(): boolean {
    return this.measuredMaterials.length > 0;
  }

  private hasLockedBatchSetupMeasurements(): boolean {
    return this.hasAnyMeasuredBatchMaterials()
      || this.hasMeasuredFoamWeight()
      || this.hasMeasuredFoamCup()
      || this.hasMeasuredMudCapture()
      || this.batchHeader.measuredBlendWeight != null
      || !!this.batchHeader.capturedBlendCisProcontainerId;
  }

  private getDryMeasuredMaterials(): MeasuredMaterialDto[] {
    const liquidIds = new Set(this.batchMaterialOptions
      .filter(option => option.sectionType === 'LiquidAdditive' || option.sectionType === 'LiquidBase')
      .map(option => option.slurryMaterialId));
    return this.measuredMaterials.filter(material => !liquidIds.has(material.slurryMaterialId));
  }

  private getLiquidMeasuredMaterials(): MeasuredMaterialDto[] {
    const liquidIds = new Set(this.batchMaterialOptions
      .filter(option => option.sectionType === 'LiquidAdditive' || option.sectionType === 'LiquidBase')
      .map(option => option.slurryMaterialId));
    return this.measuredMaterials.filter(material => liquidIds.has(material.slurryMaterialId) && !material.isFoamer);
  }

  private getFoamerMeasuredMaterials(): MeasuredMaterialDto[] {
    return this.measuredMaterials.filter(material => material.isFoamer);
  }

  getSelectedBatchEditableOutputCount(): number {
    if (!this.selectedBatch) {
      return 0;
    }
    return this.selectedBatch.tests.filter(x => this.editableBatchOutputStatuses.has(x.status)).length;
  }

  batchTestStatusLabel(statusNum: number, statusName?: string | null): string {
    const dbStatusName = statusName?.trim();
    const statusNameMap: Record<string, string> = {
      started: 'InProgress',
      inprogress: 'InProgress',
      accepted: 'Approved',
      approved: 'Approved',
      rejected: 'Rejected',
      needretake: 'Repeat',
      failed: 'Failed',
      finished: 'Completed',
      completed: 'Completed',
      submitted: 'Completed',
      cancelled: 'Cancelled'
    };

    if (dbStatusName) {
      return statusNameMap[dbStatusName.toLowerCase().replace(/\s+/g, '')] ?? dbStatusName;
    }

    const map: Record<number, string> = { 1: 'InProgress', 2: 'Approved', 3: 'Rejected', 4: 'Repeat', 5: 'Failed', 6: 'Completed', 7: 'Cancelled' };
    return map[statusNum] ?? String(statusNum);
  }

  batchStatusLabel(statusNum: number): string {
    const map: Record<number, string> = { 1: 'Draft', 2: 'Ongoing in Lab', 3: 'Ongoing in Lab', 4: 'Submitted', 5: 'Approved', 6: 'Rejected', 7: 'Redo Required' };
    return map[statusNum] ?? String(statusNum);
  }

  createBatch(): void {
    if (this.slurryId <= 0 || !this.canCreateBatch() || this.selectedTestMasterIds.length === 0) { return; }
    this.batchMessage = '';
    const req: CreateBatchRequestDto = {
      testInputMasterIds: [...this.selectedTestMasterIds],
      notes: this.createBatchNotes || undefined
    };
    this.slurryService.createBatch(this.slurryId, req).subscribe({
      next: (batch) => {
        this.batches.push(batch);
        this.selectedTestMasterIds = [];
        this.createBatchNotes = '';
        this.batchMessage = `${batch.batchNumber} created. Status set to Ongoing in Lab.`;
        this.statusId = 3;
        this.status = this.statusLabels[3];
        this.loadCapabilities();
        this.loadBatches();
        this.openBatchDetails(batch.slurryBatchId);
      },
      error: (err) => { console.error('Failed to create batch:', err); alert('Failed to create batch.'); }
    });
  }

  canAddSingleTest(): boolean {
    // Fallback mirrors MixAddSingleTestDraft: ReadytoTest (2), OngoinginLab (3), SentToLabAgain (7).
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && (this.statusId === 2 || this.statusId === 3 || this.statusId === 7);
    return this.getCapabilityAllowed(c => c.addSingleTest, fallback);
  }

  canEditSingleTestCrushLength(): boolean {
    return this.canAddSingleTest();
  }

  showAddSingleTestFooterAction(): boolean {
    return this.slurryId > 0 && this.canAddSingleTest();
  }

  openAddSingleTestDialog(): void {
    if (!this.canAddSingleTest()) {
      return;
    }

    this.singleTestMessage = '';
    this.selectedSingleTestTypeId = null;
    this.resetSingleTestDialogState();
    this.isAddSingleTestDialogOpen = true;
  }

  closeAddSingleTestDialog(): void {
    this.isAddSingleTestDialogOpen = false;
    this.selectedSingleTestTypeId = null;
    this.resetSingleTestDialogState();
  }

  onSingleTestTypeChanged(): void {
    this.singleTestMessage = '';

    if (this.selectedSingleTestTypeId === 1) {
      this.initializeSingleTestThickening();
    }

    if (this.selectedSingleTestTypeId === 4) {
      this.onRheologyTypeChanged(this.singleTestRheology);
    }

    if (this.selectedSingleTestTypeId === 20) {
      this.applyCompressiveStrengthTypeDefaults(this.singleTestCompStr);
    }

    if (this.selectedSingleTestTypeId === 22) {
      this.applyCompressiveStrengthTypeDefaults(this.singleTestFoamCompStr);
    }
  }

  getSingleTestDialogBlockedReason(): string {
    switch (this.selectedSingleTestTypeId) {
      case 1:
        if (this.singleTestThickening.isHesitation && (this.singleTestThickening.bhst == null || this.singleTestThickening.conditioningTime == null || this.singleTestThickening.gradient == null)) {
          return 'Thickening Hesitation requires BHST, Conditioning Time, and Gradient.';
        }
        if (this.singleTestThickening.isShutdown && (this.singleTestThickening.stopMotor == null || this.singleTestThickening.startMotor == null)) {
          return 'Thickening Shutdown requires Stop Motor and Start Motor.';
        }
        if (this.singleTestThickening.hasSrt && this.singleTestThickening.srt == null) {
          return 'Thickening SRT requires SRT value.';
        }
        return '';
      case 4:
        return !this.singleTestRheology.rheologyTypeId || !this.singleTestRheology.bobSleeveTypeId || this.singleTestRheology.temperature == null
          ? 'Each Rheology row requires Test Type, Bob/Sleeve, and Temperature.'
          : '';
      case 5: {
        return !this.singleTestHpHt.hpHtRheologyTypeId ||
          this.singleTestHpHt.readings.length === 0 ||
          this.singleTestHpHt.readings.some(reading => reading.temperature == null || reading.pressure == null || reading.totalTime == null)
          ? 'Each HP/HT test requires Equipment Type and at least one reading; active readings require Temperature, Pressure, and Time.'
          : '';
      }
      case 2:
        return !this.singleTestFluidLoss.fluidLossTypeId || this.singleTestFluidLoss.temperature == null
          ? 'Each Fluid Loss row requires Test Type and Temperature.'
          : '';
      case 3:
        return this.singleTestFreeFluid.temperature == null || this.singleTestFreeFluid.angle == null
          ? 'Each Free Fluid row requires Angle and Temperature.'
          : '';
      case 6:
        return !this.singleTestStaticGel.criticalStaticGelStrengthTypeId || this.singleTestStaticGel.jpt == null || this.singleTestStaticGel.finalHydrostaticPressure == null || this.singleTestStaticGel.temperature == null || !this.singleTestStaticGel.csgs || this.singleTestStaticGel.csgs <= 0
          ? 'Each Static Gel row requires Type, JPT, Final Hydrostatic Pressure, Temperature, and CSGS.'
          : '';
      case 20:
        return this.getCompressiveDialogBlockedReason(this.singleTestCompStr, 'Compressive Strength');
      case 8:
        return !this.singleTestFoamStability.foamedStabilityTypeId || this.singleTestFoamStability.foamedDensity == null
          ? 'Each Foam Stability row requires Type and Foamed Density.'
          : '';
      case 22:
        return this.getCompressiveDialogBlockedReason(this.singleTestFoamCompStr, 'Foam Compressive Strength');
      default:
        return '';
    }
  }

  canRemoveSingleTest(testInputMasterId: number): boolean {
    return this.getRemoveSingleTestBlockedReason(testInputMasterId) === '';
  }

  canShowRemoveSingleTest(testInputMasterId: number | null | undefined): boolean {
    return this.isPersistedTestInputMaster(testInputMasterId) && this.canRemoveSingleTest(testInputMasterId);
  }

  getRemoveSingleTestBlockedReason(testInputMasterId: number | null | undefined): string {
    if (!(typeof testInputMasterId === 'number' && testInputMasterId > 0)) {
      return 'Only persisted tests can be removed.';
    }

    const test = this.allTestInputs.find(t => t.testInputMasterId === testInputMasterId);
    // General gate mirrors MixRemoveTest: OngoinginLab (3) only; per-test check remains local.
    const capabilityGate = this.getCapabilityAllowed(c => c.removeSingleTest, !this.isParentProjectCompleted && this.statusId === 3);
    if (!capabilityGate) {
      return this.getCapabilityReason(c => c.removeSingleTest) ?? 'Remove Test is available only while the slurry is Ongoing in Lab.';
    }

    if (!test) {
      return 'This test is not available for removal.';
    }

    if (test.hasOutputs) {
      return 'Executed tests cannot be removed.';
    }

    return '';
  }

  addSingleTest(): void {
    if (!this.canAddSingleTest() || this.selectedSingleTestTypeId == null) {
      return;
    }

    const blockedReason = this.getSingleTestDialogBlockedReason();
    if (blockedReason) {
      this.singleTestMessage = blockedReason;
      return;
    }

    const selectedOption = this.singleTestTypeOptions.find(opt => opt.id === this.selectedSingleTestTypeId) ?? null;
    this.singleTestMessage = '';
    const request = this.buildAddSingleTestRequest();
    if (!request) {
      return;
    }

    this.slurryService.addSingleTest(this.slurryId, request).subscribe({
      next: () => {
        this.singleTestMessage = 'Single test added.';
        this.closeAddSingleTestDialog();
        if (selectedOption) {
          this.setTab(selectedOption.tab);
        }
        this.loadTests();
      },
      error: (err) => {
        console.error('Failed to add single test:', err);
        this.singleTestMessage = this.getApiErrorMessage(err, 'Failed to add single test.');
      }
    });
  }

  private resetSingleTestDialogState(): void {
    this.singleTestThickening = this.createDefaultThickening();
    this.singleTestRheology = this.createDefaultRheology();
    this.singleTestHpHt = this.createDefaultHpHt();
    this.singleTestFluidLoss = { fluidLossTypeId: 1, temperature: this.testSettings.bhct, notes: '', isMuted: false, testInputMasterId: null, outputs: [] };
    this.singleTestFreeFluid = { angle: 45, temperature: this.testSettings.bhct, notes: '', isMuted: false, testInputMasterId: null, outputs: [] };
    this.singleTestStaticGel = { criticalStaticGelStrengthTypeId: 1, jpt: null, finalHydrostaticPressure: null, temperature: this.testSettings.bhct, csgs: 0, notes: '', isMuted: false, testInputMasterId: null, outputs: [] };
    this.singleTestCompStr = this.createDefaultCompStr(1, 1);
    this.singleTestFoamStability = this.createDefaultFoamStability();
    this.singleTestFoamCompStr = this.createDefaultCompStr(1, 2);
    this.singleTestOther = this.createDefaultOtherTestRow();
  }

  private initializeSingleTestThickening(): void {
    if (this.singleTestThickening.bhst == null) {
      this.singleTestThickening.bhst = this.testSettings.bhst;
    }
    this.applySingleTestHesitationDefaults();
    this.applySingleTestShutdownDefaults();
    this.applySingleTestSrtDefaults();
  }

  onSingleTestThickeningHesitationChanged(isHesitation: boolean): void {
    this.singleTestThickening.isHesitation = isHesitation;
    this.initializeSingleTestThickening();
  }

  private applySingleTestHesitationDefaults(): void {
    if (!this.singleTestThickening.isHesitation) {
      return;
    }

    if (this.singleTestThickening.bhst == null) {
      this.singleTestThickening.bhst = this.testSettings.bhst;
    }
    if (this.singleTestThickening.conditioningTime == null) {
      this.singleTestThickening.conditioningTime = this.hesitationDefaultConditioningTime;
    }
    if (this.singleTestThickening.gradient == null) {
      this.singleTestThickening.gradient = this.hesitationDefaultGradient;
    }
  }

  private applySingleTestShutdownDefaults(): void {
    if (!this.singleTestThickening.isShutdown) {
      return;
    }

    if (this.singleTestThickening.stopMotor == null) {
      this.singleTestThickening.stopMotor = this.shutdownDefaultMotorTime;
    }
    if (this.singleTestThickening.startMotor == null) {
      this.singleTestThickening.startMotor = this.shutdownDefaultMotorTime;
    }
  }

  private applySingleTestSrtDefaults(): void {
    if (!this.singleTestThickening.hasSrt) {
      return;
    }

    if (this.singleTestThickening.srt == null) {
      this.singleTestThickening.srt = this.srtDefaultTime;
    }
  }

  onSingleTestThickeningShutdownChanged(isShutdown: boolean): void {
    this.singleTestThickening.isShutdown = isShutdown;
    this.applySingleTestShutdownDefaults();
  }

  onSingleTestThickeningSrtChanged(hasSrt: boolean): void {
    this.singleTestThickening.hasSrt = hasSrt;
    this.applySingleTestSrtDefaults();
  }

  addSingleTestHpHtReading(): void {
    this.singleTestHpHt.readings.push(this.createDefaultHpHtReading());
  }

  removeSingleTestHpHtReading(index: number): void {
    if (this.singleTestHpHt.readings.length <= 1) {
      return;
    }

    this.singleTestHpHt.readings.splice(index, 1);
  }

  private getCompressiveDialogBlockedReason(row: CompStrRow, label: string): string {
    if (!row.compressiveStrengthTypeId || !row.compressiveStrengthTestTypeId || row.temperature == null || row.pressure == null) {
      return `${label} rows require Type, Temperature, Pressure, plus UCA Ramp Time or Crush Type and Length Of Test.`;
    }
    if (this.isCompressiveStrengthUca(row) && row.temperatureRampTime == null) {
      return `${label} rows require Type, Temperature, Pressure, plus UCA Ramp Time or Crush Type and Length Of Test.`;
    }
    if (this.isCompressiveStrengthCrush(row) && (row.crushTestTypeId == null || row.lengthOfTest == null)) {
      return `${label} rows require Type, Temperature, Pressure, plus UCA Ramp Time or Crush Type and Length Of Test.`;
    }
    return '';
  }

  private buildAddSingleTestRequest(): AddSingleTestRequestDto | null {
    if (this.selectedSingleTestTypeId == null) {
      return null;
    }

    switch (this.selectedSingleTestTypeId) {
      case 1:
        return {
          testTypeId: 1,
          notes: this.singleTestThickening.notes ?? undefined,
          bhst: this.singleTestThickening.bhst,
          gradient: this.singleTestThickening.gradient,
          stopMotor: this.singleTestThickening.stopMotor,
          startMotor: this.singleTestThickening.startMotor,
          isHesitation: this.singleTestThickening.isHesitation,
          isShutdown: this.singleTestThickening.isShutdown,
          conditioningTime: this.singleTestThickening.conditioningTime,
          contamination: this.singleTestThickening.contamination,
          hasSrt: this.singleTestThickening.hasSrt,
          srt: this.singleTestThickening.srt
        };
      case 4:
        return {
          testTypeId: 4,
          notes: this.singleTestRheology.notes ?? undefined,
          rheologyTypeId: this.singleTestRheology.rheologyTypeId,
          bobSleeveTypeId: this.singleTestRheology.bobSleeveTypeId,
          temperature: this.singleTestRheology.temperature
        };
      case 5: {
        return {
          testTypeId: 5,
          notes: this.singleTestHpHt.notes ?? undefined,
          hpHtRheologyTypeId: this.singleTestHpHt.hpHtRheologyTypeId,
          hpHtReadings: this.singleTestHpHt.readings.map(reading => ({
            temperature: reading.temperature,
            pressure: reading.pressure,
            totalTime: reading.totalTime
          }))
        };
      }
      case 2:
        return {
          testTypeId: 2,
          notes: this.singleTestFluidLoss.notes ?? undefined,
          fluidLossTypeId: this.singleTestFluidLoss.fluidLossTypeId,
          temperature: this.singleTestFluidLoss.temperature
        };
      case 3:
        return {
          testTypeId: 3,
          notes: this.singleTestFreeFluid.notes ?? undefined,
          angle: this.singleTestFreeFluid.angle,
          temperature: this.singleTestFreeFluid.temperature
        };
      case 6:
        return {
          testTypeId: 6,
          notes: this.singleTestStaticGel.notes ?? undefined,
          criticalStaticGelStrengthTypeId: this.singleTestStaticGel.criticalStaticGelStrengthTypeId,
          jpt: this.singleTestStaticGel.jpt,
          finalHydrostaticPressure: this.singleTestStaticGel.finalHydrostaticPressure,
          temperature: this.singleTestStaticGel.temperature,
          csgs: this.singleTestStaticGel.csgs
        };
      case 20:
        return {
          testTypeId: 20,
          notes: this.singleTestCompStr.notes ?? undefined,
          compressiveStrengthTypeId: this.singleTestCompStr.compressiveStrengthTypeId,
          contamination: this.singleTestCompStr.contamination,
          temperatureRampTime: this.singleTestCompStr.temperatureRampTime,
          temperature: this.singleTestCompStr.temperature,
          pressure: this.singleTestCompStr.pressure,
          lengthOfTest: this.singleTestCompStr.lengthOfTest,
          crushTestTypeId: this.singleTestCompStr.crushTestTypeId
        };
      case 8:
        return {
          testTypeId: 8,
          notes: this.singleTestFoamStability.notes ?? undefined,
          foamedStabilityTypeId: this.singleTestFoamStability.foamedStabilityTypeId,
          foamedDensity: this.singleTestFoamStability.foamedDensity
        };
      case 22:
        return {
          testTypeId: 22,
          notes: this.singleTestFoamCompStr.notes ?? undefined,
          compressiveStrengthTypeId: this.singleTestFoamCompStr.compressiveStrengthTypeId,
          contamination: this.singleTestFoamCompStr.contamination,
          temperatureRampTime: this.singleTestFoamCompStr.temperatureRampTime,
          temperature: this.singleTestFoamCompStr.temperature,
          pressure: this.singleTestFoamCompStr.pressure,
          lengthOfTest: this.singleTestFoamCompStr.lengthOfTest,
          crushTestTypeId: this.singleTestFoamCompStr.crushTestTypeId
        };
      default:
        return {
          testTypeId: this.selectedSingleTestTypeId,
          notes: this.singleTestOther.notes ?? undefined
        };
    }
  }

  removeSingleTest(testInputMasterId: number): void {
    if (!this.canRemoveSingleTest(testInputMasterId)) {
      return;
    }

    this.singleTestMessage = '';
    this.slurryService.removeSingleTest(this.slurryId, testInputMasterId).subscribe({
      next: () => {
        this.singleTestMessage = 'Test removed.';
        this.loadTests();
      },
      error: (err) => {
        console.error('Failed to remove test:', err);
        this.singleTestMessage = 'Failed to remove test.';
      }
    });
  }

  // ── Test default add helpers ──

  addRheology():   void { this.rheologySelected = true; this.rheologyTests.push(this.createDefaultRheology()); }
  addHpHt():       void { this.hphtSelected = true; this.hphtTests.push(this.createDefaultHpHt()); }
  addFluidLoss():  void { this.fluidLossSelected = true; this.fluidLossTests.push({ fluidLossTypeId: 1, temperature: this.testSettings.bhct, notes: '', isMuted: false, testInputMasterId: null, outputs: [] }); }
  addFreeFluid():  void { this.freeFluidSelected = true; this.freeFluidTests.push({ angle: 45, temperature: this.testSettings.bhct, notes: '', isMuted: false, testInputMasterId: null, outputs: [] }); }
  addStaticGel():  void { this.staticGelSelected = true; this.staticGelTests.push({ criticalStaticGelStrengthTypeId: 1, jpt: null, finalHydrostaticPressure: null, temperature: this.testSettings.bhct, csgs: 0, notes: '', isMuted: false, testInputMasterId: null, outputs: [] }); }
  addCompStr():    void { this.compStrSelected = true; this.compStrTests.push(this.createDefaultCompStr(1, 1)); }
  addFoamCompStr(): void { this.foamCompStrTests.push(this.createDefaultCompStr(1, 2)); }
  addFoamStab():   void { this.foamStabilityTests.push(this.createDefaultFoamStability()); }

  addHpHtReading(group: HpHtTestRow): void {
    this.hphtSelected = true;
    group.readings.push(this.createDefaultHpHtReading());
  }

  openHpHtGroupNotesDialog(group: HpHtTestRow): void {
    const testInputMasterId = this.getHpHtGroupTestInputMasterId(group);
    if (testInputMasterId != null) {
      this.openPersistedTestNoteDialog(testInputMasterId, group.notes, (updatedNote) => {
        group.notes = updatedNote;
      });
      return;
    }

    this.openRowAppendClearNoteDialog(group.notes, (updatedNote) => {
      group.notes = updatedNote;
    });
  }

  openHpHtGroupAttachments(group: HpHtTestRow): void {
    const testInputMasterIds = this.getHpHtGroupTestInputMasterIds(group);
    const firstTestInputMasterId = testInputMasterIds[0];
    if (!this.canOpenTestAttachments(firstTestInputMasterId)) {
      return;
    }

    this.openTestAttachments(firstTestInputMasterId, 'HP/HT Rheology', testInputMasterIds);
  }

  removeHpHtGroup(index: number): void {
    this.hphtTests.splice(index, 1);
    this.hphtSelected = this.hphtTests.length > 0;
  }

  removeHpHtReading(group: HpHtTestRow, readingIndex: number): void {
    group.readings.splice(readingIndex, 1);
    if (group.readings.length === 0) {
      this.hphtTests = this.hphtTests.filter(candidate => candidate !== group);
    }
    this.hphtSelected = this.hphtTests.length > 0;
  }

  getHphtGroupPrimaryOutput(group: HpHtTestRow): BatchTestBriefDto | null {
    return this.getTestPrimaryOutput(this.getCombinedOutputs(group.readings));
  }

  getHphtGroupOutputs(group: HpHtTestRow): BatchTestBriefDto[] {
    return this.getCombinedOutputs(group.readings);
  }

  openHpHtGroupOutput(group: HpHtTestRow): void {
    this.reviewTestOutput(this.getCombinedOutputs(group.readings));
  }

  private getCombinedOutputs(rows: Array<{ outputs?: BatchTestBriefDto[] | null | undefined }>): BatchTestBriefDto[] {
    return rows.flatMap(row => row.outputs ?? []);
  }

  removeRheology(i: number):   void { this.rheologyTests.splice(i, 1); this.rheologySelected = this.rheologyTests.length > 0; }
  removeHpHt(i: number):       void { this.removeHpHtGroup(i); }
  removeFluidLoss(i: number):  void { this.fluidLossTests.splice(i, 1); this.fluidLossSelected = this.fluidLossTests.length > 0; }
  removeFreeFluid(i: number):  void { this.freeFluidTests.splice(i, 1); this.freeFluidSelected = this.freeFluidTests.length > 0; }
  removeStaticGel(i: number):  void { this.staticGelTests.splice(i, 1); this.staticGelSelected = this.staticGelTests.length > 0; }
  removeCompStr(i: number):    void { this.compStrTests.splice(i, 1); this.compStrSelected = this.compStrTests.length > 0; }
  removeFoamCompStr(i: number): void { this.foamCompStrTests.splice(i, 1); }
  removeFoamStab(i: number):   void { this.foamStabilityTests.splice(i, 1); }

  showMeasuredDepth(): boolean {
    return this.normalizeLegacyKey(this.jobType) === 'plug' && this.normalizeLegacyKey(this.jobSubtype) === 'abandonment';
  }

  private normalizeLegacyKey(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  // ── Load tests ──
  private loadTests(preserveSharedSettings = false): void {
    if (this.slurryId <= 0) { return; }
    this.slurryService.getTests(this.slurryId).subscribe({
      next: (dto) => {
        if (!dto) { return; }
        const s = dto.sharedSettings;
        if (s && !preserveSharedSettings) {
          this.testSettings = {
            initialTemperature: s.initialTemperature ?? null,
            bhct:               s.finalTemperature   ?? null,
            temperatureRampTime: s.temperatureRampTime ?? null,
            initialPressure:    s.initialPressure    ?? null,
            pressureRampTime:   s.pressureRampTime   ?? null,
            finalPressure:      s.finalPressure      ?? null,
            bhst:               s.bhst               ?? null,
            baseDensity:        s.baseDensity        ?? null,
            foamDensity:        s.foamDensity        ?? null
          };
          this.previousBhctDefault = this.testSettings.bhct;
          this.previousBhstDefault = this.testSettings.bhst;
          this.previousFoamDensityDefault = this.testSettings.foamDensity;
        }
        if (dto.thickeningInput) {
          this.thickeningSelected = true;
          const t = dto.thickeningInput;
          this.thickening = {
            bhst: t.bhst ?? null, isHesitation: t.isHesitation ?? false,
            conditioningTime: t.conditioningTime ?? this.hesitationDefaultConditioningTime, gradient: t.gradient ?? this.hesitationDefaultGradient,
            isShutdown: t.isShutdown ?? false, stopMotor: t.stopMotor ?? this.shutdownDefaultMotorTime,
            startMotor: t.startMotor ?? this.shutdownDefaultMotorTime, hasSrt: t.hasSrt ?? false,
            srt: t.srt ?? this.srtDefaultTime, contamination: t.contamination ?? false,
            notes: t.notes ?? '', isMuted: t.isMuted ?? false
          };
          this.thickeningTestInputMasterId = t.testInputMasterId ?? null;
          this.thickeningOutputs = t.outputs ?? [];
          this.applyHesitationDefaults();
        } else {
          this.thickeningSelected = false;
          this.thickening = this.createDefaultThickening();
          this.thickeningTestInputMasterId = null;
          this.thickeningOutputs = [];
        }
        this.rheologyTests   = (dto.rheologyInputs ?? []).map(r => {
          const row = {
            rheologyTypeId: r.rheologyTypeId,
            bobSleeveTypeId: r.bobSleeveTypeId ?? null,
            temperature: r.temperature ?? null,
            notes: r.notes ?? '',
            isMuted: r.isMuted ?? false,
            testInputMasterId: r.testInputMasterId ?? null,
            outputs: r.outputs ?? []
          };
          if (row.temperature == null) {
            row.temperature = this.getRheologyDefaultTemperature(row.rheologyTypeId);
          }
          return row;
        });
        this.hphtTests       = (dto.hpHtRheologyTests ?? []).map(group => ({
          hpHtRheologyTypeId: group.hpHtRheologyTypeId ?? null,
          notes: group.notes ?? '',
          isMuted: group.isMuted ?? false,
          readings: (group.readings ?? []).map(reading => ({
            temperature: reading.temperature ?? null,
            pressure: reading.pressure ?? null,
            totalTime: reading.totalTime ?? null,
            testInputMasterId: reading.testInputMasterId ?? null,
            outputs: reading.outputs ?? []
          }))
        })).filter(group => group.readings.length > 0);
        this.fluidLossTests  = (dto.fluidLossInputs ?? []).map(f => ({ fluidLossTypeId: f.fluidLossTypeId ?? null, temperature: f.temperature ?? null, notes: f.notes ?? '', isMuted: f.isMuted ?? false, testInputMasterId: f.testInputMasterId ?? null, outputs: f.outputs ?? [] }));
        this.freeFluidTests  = (dto.freeFluidInputs ?? []).map(f => ({ angle: f.angle ?? null, temperature: f.temperature ?? null, notes: f.notes ?? '', isMuted: f.isMuted ?? false, testInputMasterId: f.testInputMasterId ?? null, outputs: f.outputs ?? [] }));
        this.staticGelTests  = (dto.staticGelInputs ?? []).map(sg => ({
          criticalStaticGelStrengthTypeId: sg.criticalStaticGelStrengthTypeId,
          jpt: sg.jpt ?? null,
          finalHydrostaticPressure: sg.finalHydrostaticPressure ?? null,
          temperature: sg.temperature ?? null,
          csgs: sg.csgs ?? 0,
          notes: sg.notes ?? '',
          isMuted: sg.isMuted ?? false,
          testInputMasterId: sg.testInputMasterId ?? null,
          outputs: sg.outputs ?? []
        }));
        this.compStrTests    = (dto.compressiveStrengthInputs ?? []).map(cs => {
          const row = {
            compressiveStrengthTypeId: cs.compressiveStrengthTypeId,
            compressiveStrengthTestTypeId: cs.compressiveStrengthTestTypeId || 1,
            crushTestTypeId: cs.crushTestTypeId ?? null,
            contamination: cs.contamination,
            temperatureRampTime: cs.temperatureRampTime ?? null,
            temperature: cs.temperature ?? null,
            pressure: cs.pressure ?? null,
            lengthOfTest: cs.lengthOfTest ?? null,
            notes: cs.notes ?? '',
            isMuted: cs.isMuted ?? false,
            testInputMasterId: cs.testInputMasterId ?? null,
            outputs: cs.outputs ?? []
          };
          this.applyCompressiveStrengthTypeDefaults(row);
          return row;
        });
        this.rheologySelected = this.rheologyTests.length > 0;
        this.hphtSelected = this.hphtTests.length > 0;
        this.fluidLossSelected = this.fluidLossTests.length > 0;
        this.freeFluidSelected = this.freeFluidTests.length > 0;
        this.staticGelSelected = this.staticGelTests.length > 0;
        this.compStrSelected = this.compStrTests.length > 0;
        this.foamCompStrTests = (dto.foamCompressiveStrengthInputs ?? []).map(cs => {
          const row = {
            compressiveStrengthTypeId: cs.compressiveStrengthTypeId,
            compressiveStrengthTestTypeId: cs.compressiveStrengthTestTypeId || 2,
            crushTestTypeId: cs.crushTestTypeId ?? null,
            contamination: cs.contamination,
            temperatureRampTime: cs.temperatureRampTime ?? null,
            temperature: cs.temperature ?? null,
            pressure: cs.pressure ?? null,
            lengthOfTest: cs.lengthOfTest ?? null,
            notes: cs.notes ?? '',
            isMuted: cs.isMuted ?? false,
            testInputMasterId: cs.testInputMasterId ?? null,
            outputs: cs.outputs ?? []
          };
          this.applyCompressiveStrengthTypeDefaults(row);
          return row;
        });
        this.foamStabilityTests = (dto.foamStabilityInputs ?? []).map(fs => ({
          foamedStabilityTypeId: fs.foamedStabilityTypeId,
          foamedDensity: fs.foamedDensity ?? null,
          notes: fs.notes ?? '',
          isMuted: fs.isMuted ?? false,
          testInputMasterId: fs.testInputMasterId ?? null,
          outputs: fs.outputs ?? []
        }));
        // Other Tests (row-based)
        this.otherApiSedimentationRows = (dto.apiSedimentationInputs ?? []).map(x => ({ notes: x.notes ?? '', isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? null, outputs: x.outputs ?? [] }));
        this.otherExpansionRows = (dto.expansionInputs ?? []).map(x => ({ notes: x.notes ?? '', isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? null, outputs: x.outputs ?? [] }));
        this.otherPressurizedMudScaleRows = (dto.pressurizedMudScaleInputs ?? []).map(x => ({ notes: x.notes ?? '', isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? null, outputs: x.outputs ?? [] }));
        this.otherPycnometerRows = (dto.pycnometerInputs ?? []).map(x => ({ notes: x.notes ?? '', isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? null, outputs: x.outputs ?? [] }));
        this.otherAcidSolubilityRows = (dto.acidSolubilityInputs ?? []).map(x => ({ notes: x.notes ?? '', isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? null, outputs: x.outputs ?? [] }));
        this.otherSplittingTsRows = (dto.splittingTsInputs ?? []).map(x => ({ notes: x.notes ?? '', isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? null, outputs: x.outputs ?? [] }));
        this.otherUnconfinedCsRows = (dto.unconfinedCsInputs ?? []).map(x => ({ notes: x.notes ?? '', isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? null, outputs: x.outputs ?? [] }));
        this.otherTriaxilConfinedCsRows = (dto.triaxilConfinedCsInputs ?? []).map(x => ({ notes: x.notes ?? '', isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? null, outputs: x.outputs ?? [] }));
        this.otherBasicCrushRows = (dto.basicCrushTestInputs ?? []).map(x => ({ notes: x.notes ?? '', isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? null, outputs: x.outputs ?? [] }));
        this.shearBondRows = (dto.shearBondInputs ?? []).map(sb => ({
          cureTimeHrs: sb.cureTimeHrs ?? null,
          notes: sb.notes ?? '',
          isMuted: sb.isMuted ?? false,
          testInputMasterId: sb.testInputMasterId ?? null,
          outputs: sb.outputs ?? []
        }));
        this.normalizeVisibleTabSelection();
        // Collect all TestInputMaster IDs for the "Create Batch" test selector
        this.allTestMasterIds = [
          ...this.rheologyTests.map((_, i) => i),   // placeholder — real IDs loaded below
        ];
        const nextIndexByBaseLabel = new Map<string, number>();
        const printableBatchTests = this.buildPrintableBatchTestEntries(dto, nextIndexByBaseLabel);
        this.printableBatchTestsById = new Map(printableBatchTests.map(test => [test.testInputMasterId, test]));
        this.allTestMasterIds = printableBatchTests.map(test => test.testInputMasterId);
        this.allTestInputs = printableBatchTests.map(test => ({
          testInputMasterId: test.testInputMasterId,
          kind: test.kind,
          displayLabel: test.displayLabel,
          hasOutputs: test.outputs.length > 0,
          outputs: test.outputs
        }));
      },
      error: (err) => console.error('Failed to load tests:', err)
    });
  }

  private buildTestsSaveRequest(): SlurryTestInputDto {
    const s = this.testSettings;
    return {
      sharedSettings: {
        initialTemperature: s.initialTemperature ?? 0, finalTemperature: s.bhct ?? 0,
        temperatureRampTime: s.temperatureRampTime ?? 0, initialPressure: s.initialPressure ?? 0,
        pressureRampTime: s.pressureRampTime ?? 0, finalPressure: s.finalPressure ?? 0, bhst: s.bhst ?? 0,
        baseDensity: s.baseDensity ?? undefined,
        foamDensity: s.foamDensity ?? undefined
      },
      thickeningInput: this.thickeningSelected ? {
        bhst: this.thickening.bhst ?? undefined,
        gradient: this.thickening.isHesitation ? this.thickening.gradient ?? undefined : undefined,
        stopMotor: this.thickening.isShutdown ? this.thickening.stopMotor ?? undefined : undefined,
        startMotor: this.thickening.isShutdown ? this.thickening.startMotor ?? undefined : undefined,
        isHesitation: this.thickening.isHesitation, isShutdown: this.thickening.isShutdown,
        conditioningTime: this.thickening.isHesitation ? this.thickening.conditioningTime ?? undefined : undefined,
        contamination: this.thickening.contamination, hasSrt: this.thickening.hasSrt,
        srt: this.thickening.hasSrt ? this.thickening.srt ?? undefined : undefined,
        notes: this.thickening.notes ?? undefined,
        isMuted: this.thickening.isMuted ?? false
      } : null,
      rheologyInputs: this.rheologySelected ? this.rheologyTests.map(r => ({
        rheologyTypeId: r.rheologyTypeId,
        bobSleeveTypeId: r.bobSleeveTypeId ?? undefined,
        temperature: r.temperature ?? undefined,
        notes: r.notes ?? undefined,
        isMuted: r.isMuted ?? false
      })) : [],
      hpHtRheologyTests: this.hphtSelected ? this.hphtTests.map(group => ({
        hpHtRheologyTypeId: group.hpHtRheologyTypeId ?? undefined,
        notes: group.notes ?? undefined,
        isMuted: group.isMuted ?? false,
        readings: group.readings.map(reading => ({
          temperature: reading.temperature ?? undefined,
          pressure: reading.pressure ?? undefined,
          totalTime: reading.totalTime ?? undefined,
          testInputMasterId: reading.testInputMasterId ?? undefined
        }))
      })) : [],
      fluidLossInputs: this.fluidLossSelected ? this.fluidLossTests.map(f => ({
        fluidLossTypeId: f.fluidLossTypeId ?? undefined,
        temperature: f.temperature ?? undefined,
        notes: f.notes ?? undefined,
        isMuted: f.isMuted ?? false
      })) : [],
      freeFluidInputs: this.freeFluidSelected ? this.freeFluidTests.map(f => ({
        angle: f.angle ?? undefined,
        temperature: f.temperature ?? undefined,
        notes: f.notes ?? undefined,
        isMuted: f.isMuted ?? false
      })) : [],
      staticGelInputs: this.staticGelSelected ? this.staticGelTests.map(sg => ({
        criticalStaticGelStrengthTypeId: sg.criticalStaticGelStrengthTypeId,
        jpt: sg.jpt ?? undefined,
        finalHydrostaticPressure: sg.finalHydrostaticPressure ?? undefined,
        temperature: sg.temperature ?? undefined,
        csgs: sg.csgs,
        notes: sg.notes ?? undefined,
        isMuted: sg.isMuted ?? false,
        testInputMasterId: sg.testInputMasterId ?? undefined
      })) : [],
      compressiveStrengthInputs: this.compStrSelected ? this.compStrTests.map(cs => ({
        compressiveStrengthTypeId: cs.compressiveStrengthTypeId,
        compressiveStrengthTestTypeId: 1,
        crushTestTypeId: this.isCompressiveStrengthCrush(cs) ? cs.crushTestTypeId ?? undefined : undefined,
        contamination: cs.contamination,
        temperatureRampTime: this.isCompressiveStrengthUca(cs) ? cs.temperatureRampTime ?? undefined : undefined,
        temperature: cs.temperature ?? undefined,
        pressure: cs.pressure ?? undefined,
        lengthOfTest: this.isCompressiveStrengthCrush(cs) ? cs.lengthOfTest ?? undefined : undefined,
        notes: cs.notes ?? undefined,
        isMuted: cs.isMuted ?? false,
        testInputMasterId: cs.testInputMasterId ?? undefined
      })) : [],
      foamCompressiveStrengthInputs: this.foamCompStrTests.map(cs => ({
        compressiveStrengthTypeId: cs.compressiveStrengthTypeId,
        compressiveStrengthTestTypeId: 2,
        crushTestTypeId: this.isCompressiveStrengthCrush(cs) ? cs.crushTestTypeId ?? undefined : undefined,
        contamination: cs.contamination,
        temperatureRampTime: this.isCompressiveStrengthUca(cs) ? cs.temperatureRampTime ?? undefined : undefined,
        temperature: cs.temperature ?? undefined,
        pressure: cs.pressure ?? undefined,
        lengthOfTest: this.isCompressiveStrengthCrush(cs) ? cs.lengthOfTest ?? undefined : undefined,
        notes: cs.notes?.trim() ? cs.notes.trim() : undefined,
        isMuted: cs.isMuted ?? false,
        testInputMasterId: cs.testInputMasterId ?? undefined
      })),
      foamStabilityInputs: this.foamStabilityTests.map(fs => ({
        foamedStabilityTypeId: fs.foamedStabilityTypeId,
        foamedDensity: fs.foamedDensity ?? undefined,
        notes: fs.notes?.trim() ? fs.notes.trim() : undefined,
        isMuted: fs.isMuted ?? false,
        testInputMasterId: fs.testInputMasterId ?? undefined
      })),
      apiSedimentationInputs: this.otherApiSedimentationRows.map(x => ({ notes: x.notes?.trim() ? x.notes.trim() : undefined, isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? undefined })),
      expansionInputs: this.otherExpansionRows.map(x => ({ notes: x.notes?.trim() ? x.notes.trim() : undefined, isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? undefined })),
      pressurizedMudScaleInputs: this.otherPressurizedMudScaleRows.map(x => ({ notes: x.notes?.trim() ? x.notes.trim() : undefined, isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? undefined })),
      pycnometerInputs: this.otherPycnometerRows.map(x => ({ notes: x.notes?.trim() ? x.notes.trim() : undefined, isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? undefined })),
      shearBondInputs: this.shearBondRows.map(sb => ({ cureTimeHrs: sb.cureTimeHrs ?? undefined, notes: sb.notes?.trim() ? sb.notes.trim() : undefined, isMuted: sb.isMuted ?? false, testInputMasterId: sb.testInputMasterId ?? undefined })),
      acidSolubilityInputs: this.otherAcidSolubilityRows.map(x => ({ notes: x.notes?.trim() ? x.notes.trim() : undefined, isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? undefined })),
      splittingTsInputs: this.otherSplittingTsRows.map(x => ({ notes: x.notes?.trim() ? x.notes.trim() : undefined, isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? undefined })),
      unconfinedCsInputs: this.otherUnconfinedCsRows.map(x => ({ notes: x.notes?.trim() ? x.notes.trim() : undefined, isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? undefined })),
      triaxilConfinedCsInputs: this.otherTriaxilConfinedCsRows.map(x => ({ notes: x.notes?.trim() ? x.notes.trim() : undefined, isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? undefined })),
      basicCrushTestInputs: this.otherBasicCrushRows.map(x => ({ notes: x.notes?.trim() ? x.notes.trim() : undefined, isMuted: x.isMuted ?? false, testInputMasterId: x.testInputMasterId ?? undefined }))
    };
  }

  private saveTestsBeforePrimaryAction(onSaved: () => void, actionLabel: string, includeOtherTests = true): void {
    if (this.slurryId <= 0 || !this.canSaveTests()) {
      onSaved();
      return;
    }

    const validationError = this.getTestsValidationError(includeOtherTests);
    if (validationError) {
      this.showTestValidationErrors = true;
      this.isLoading = false;
      this.isSaving = false;
      this.saveMessage = `${actionLabel} is blocked until test validation errors are fixed: ${validationError}`;
      return;
    }

    this.showTestValidationErrors = false;
    this.slurryService.saveTests(this.slurryId, this.buildTestsSaveRequest()).subscribe({
      next: () => {
        this.testsMessage = '';
        onSaved();
      },
      error: (err) => {
        this.isLoading = false;
        this.isSaving = false;
        console.error('Failed to save tests:', err);
        this.saveMessage = this.getApiErrorMessage(err, `Failed to save tests before ${actionLabel.toLowerCase()}.`);
      }
    });
  }

  updateBaseDensity(): void {
    if (this.slurryId <= 0 || !this.canSubmitBaseDensityUpdate()) { return; }

    this.foamSettingsMessage = '';
    this.slurryService.updateBaseDensity(this.slurryId, {
      baseDensity: this.testSettings.baseDensity
    }).subscribe({
      next: () => {
        this.foamSettingsMessage = 'Base density updated successfully.';
      },
      error: (err) => {
        console.error('Failed to update base density:', err);
        this.foamSettingsMessage = this.getApiErrorMessage(err, 'Failed to update base density.');
      }
    });
  }

  isRequiredTestFieldMissing(value: unknown, mustBePositive = false): boolean {
    if (!this.showTestValidationErrors) { return false; }
    if (value == null || value === '') { return true; }
    return mustBePositive && typeof value === 'number' && value <= 0;
  }

  isHpHtReadingFieldMissing(group: HpHtTestRow, reading: HpHtReadingRow, value: unknown): boolean {
    if (!this.showTestValidationErrors) { return false; }
    const isActive = reading.temperature != null || reading.pressure != null || reading.totalTime != null;
    const groupHasActiveReading = group.readings.some(candidate =>
      candidate.temperature != null || candidate.pressure != null || candidate.totalTime != null);
    return (isActive || (!groupHasActiveReading && group.readings[0] === reading)) && (value == null || value === '');
  }

  isHpHtGroupReadingMissing(group: HpHtTestRow): boolean {
    return this.showTestValidationErrors && !group.readings.some(reading =>
      reading.temperature != null || reading.pressure != null || reading.totalTime != null);
  }

  private getTestsValidationError(includeOtherTests = true): string | null {
    const hasAnyTests =
      this.thickeningSelected ||
      this.rheologySelected ||
      this.hphtSelected ||
      this.fluidLossSelected ||
      this.freeFluidSelected ||
      this.staticGelSelected ||
      this.compStrSelected ||
      this.rheologyTests.length > 0 ||
      this.hphtTests.length > 0 ||
      this.fluidLossTests.length > 0 ||
      this.freeFluidTests.length > 0 ||
      this.staticGelTests.length > 0 ||
      this.compStrTests.length > 0 ||
      this.foamCompStrTests.length > 0 ||
      this.foamStabilityTests.length > 0 ||
      (includeOtherTests && (
        this.otherApiSedimentationRows.length > 0 ||
        this.otherExpansionRows.length > 0 ||
        this.otherPressurizedMudScaleRows.length > 0 ||
        this.otherPycnometerRows.length > 0 ||
        this.otherAcidSolubilityRows.length > 0 ||
        this.otherSplittingTsRows.length > 0 ||
        this.otherUnconfinedCsRows.length > 0 ||
        this.otherTriaxilConfinedCsRows.length > 0 ||
        this.otherBasicCrushRows.length > 0 ||
        this.shearBondRows.length > 0));

    if (!hasAnyTests) {
      return null;
    }

    const s = this.testSettings;
    if (s.initialTemperature == null || s.bhct == null || s.temperatureRampTime == null ||
        s.initialPressure == null || s.pressureRampTime == null || s.finalPressure == null || s.bhst == null) {
      return 'All shared test settings are required when tests are selected.';
    }

    if (this.thickeningSelected) {
      if (this.thickening.isHesitation &&
          (this.thickening.conditioningTime == null || this.thickening.bhst == null || this.thickening.gradient == null)) {
        return 'Thickening Hesitation requires BHST, Conditioning Time, and Gradient.';
      }
      if (this.thickening.isShutdown && (this.thickening.stopMotor == null || this.thickening.startMotor == null)) {
        return 'Thickening Shutdown requires Stop Motor and Start Motor.';
      }
      if (this.thickening.hasSrt && this.thickening.srt == null) {
        return 'Thickening SRT requires SRT value.';
      }
    }

    if (this.rheologySelected && this.rheologyTests.some(r => !r.rheologyTypeId || !r.bobSleeveTypeId || r.temperature == null)) {
      return 'Each Rheology row requires Test Type, Bob/Sleeve, and Temperature.';
    }

    if (this.hphtSelected && this.hphtTests.some(group =>
      !group.hpHtRheologyTypeId ||
      !group.readings.some(reading =>
        reading.temperature != null || reading.pressure != null || reading.totalTime != null) ||
      group.readings.some(reading =>
        ((reading.temperature != null || reading.pressure != null || reading.totalTime != null) &&
        !(reading.temperature != null && reading.pressure != null && reading.totalTime != null))))) {
      return 'Each HP/HT test requires Equipment Type and at least one reading; active readings require Temperature, Pressure, and Time.';
    }

    if (this.fluidLossSelected && this.fluidLossTests.some(f => !f.fluidLossTypeId || f.temperature == null)) {
      return 'Each Fluid Loss row requires Test Type and Temperature.';
    }

    if (this.freeFluidSelected && this.freeFluidTests.some(f => f.temperature == null || f.angle == null)) {
      return 'Each Free Fluid row requires Angle and Temperature.';
    }

    if (this.staticGelSelected && this.staticGelTests.some(sg => !sg.criticalStaticGelStrengthTypeId || sg.jpt == null || sg.finalHydrostaticPressure == null || sg.temperature == null || !sg.csgs || sg.csgs <= 0)) {
      return 'Each Static Gel row requires Type, JPT, Final Hydrostatic Pressure, Temperature, and CSGS.';
    }

    if (this.compStrSelected) {
      const invalidRowIndex = this.compStrTests.findIndex(cs => this.getCompressiveStrengthMissingFields(cs).length > 0);
      if (invalidRowIndex >= 0) {
        const fields = this.getCompressiveStrengthMissingFields(this.compStrTests[invalidRowIndex]);
        return `Slurry Tests > Compressive Strength, row ${invalidRowIndex + 1}: ${fields.join(', ')} ${fields.length === 1 ? 'is' : 'are'} required.`;
      }
    }

    const hasFoamTests = this.foamCompStrTests.length > 0 || this.foamStabilityTests.length > 0;
    if (hasFoamTests && (s.baseDensity == null || s.foamDensity == null)) {
      const fields = [
        ...(s.baseDensity == null ? ['Base Density'] : []),
        ...(s.foamDensity == null ? ['Foamed Density'] : [])
      ];
      return `Foam Tests > Basic Info: ${fields.join(' and ')} ${fields.length === 1 ? 'is' : 'are'} required.`;
    }

    const invalidFoamRowIndex = this.foamCompStrTests.findIndex(cs => this.getCompressiveStrengthMissingFields(cs).length > 0);
    if (invalidFoamRowIndex >= 0) {
      const fields = this.getCompressiveStrengthMissingFields(this.foamCompStrTests[invalidFoamRowIndex]);
      return `Foam Tests > Compressive Strength, row ${invalidFoamRowIndex + 1}: ${fields.join(', ')} ${fields.length === 1 ? 'is' : 'are'} required.`;
    }

    if (this.foamStabilityTests.some(fs => !fs.foamedStabilityTypeId || fs.foamedDensity == null)) {
      return 'Foam Tests > Foam Stability: each row requires Type and Foamed Density.';
    }

    return null;
  }
  private loadMixability(): void {
    if (this.slurryId <= 0) { return; }
    this.slurryService.getMixability(this.slurryId).subscribe({
      next: (m) => {
        if (m) {
          this.mixabilityId = m.id ?? null;
          this.mixScore = m.score ?? null;
          this.mixTimeToWetSolids = m.timeToWetSolids ?? null;
          this.mixNotes = m.notes ?? '';
        } else {
          this.mixabilityId = null;
          this.mixScore = null;
          this.mixTimeToWetSolids = null;
          this.mixNotes = '';
        }
        this.normalizeVisibleTabSelection();
      },
      error: (err) => {
        console.error('Failed to load mixability:', err);
        this.mixabilityId = null;
        this.normalizeVisibleTabSelection();
      }
    });
  }

  /** Called by SlurryMixabilityTabComponent after data load or save. Updates print-cache fields. */
  onMixabilitySaved(event: { mixabilityId: number | null; score: number | null; timeToWetSolids: number | null; notes: string }): void {
    this.mixabilityId = event.mixabilityId;
    this.mixScore = event.score;
    this.mixTimeToWetSolids = event.timeToWetSolids;
    this.mixNotes = event.notes;
    this.normalizeVisibleTabSelection();
    this.loadCapabilities();
  }

  /** Called by SlurryRedesignTabComponent after a successful save. */
  onRedesignSaved(): void {
    this.loadCapabilities();
  }

  /** Loads tab data: mixability tracking (for tab-selection) + tests/batches when a saved slurry. */
  private initTabsData(): void {
    if (this.slurryId > 0) {
      this.loadMixability();
      this.loadTests();
      this.loadBatches();
    }
  }

  createSlurry(): void {
    const request = this.buildSaveRequest();
    if (!request) { return; }
    this.isLoading = true;
    this.slurryService.saveSlurry(request).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (this.embedded) {
          this.back.emit();
          return;
        }
        // Redirect to canonical Slurry Details route after create.
        this.router.navigate(['/projects-dashboard', 'slurry-details', response.slurryId], {
          state: {
            // ── Identity / scalars ──────────────────────────────────────
            projectId:                this.projectId,
            projectName:              this.projectName,
            ufSlurryId:               response.ufSlurryId,
            slurryDensity:            this.slurryDensityPpg,
            labVolumeMl:              this.labVolumeMl,
            slurryYield:              this.slurryYield,
            totalFluidPercent:        this.totalFluidPercent,
            totalFluidGal:            this.totalFluidGal,
            equivalentSackWeight:     this.equivalentSackLb,
            notes:                    this.notes1,
            cementSackWeight:         this.cementSackLb,
            blendCisProContainerId:   this.blendCisProContainer || undefined,
            calculatedSg:             this.calculatedSG,
            targetThickeningTimeFrom: this.targetThickeningTimeFrom,
            targetThickeningTimeTo:   this.targetThickeningTimeTo,
            // ── Material rows (full arrays) ─────────────────────────────
            blendBaseInitial:         { ...this.blendBaseInitial },
            blendBasePozRows:         this.blendBasePozRows.map(r => ({ ...r })),
            blendAdditives:           this.blendAdditives.map(r => ({ ...r })),
            additives:                this.additives.map(r => ({ ...r })),
            liquidMaterials:          this.liquidMaterials.map(r => ({ ...r })),
            liquidAdditives:          this.liquidAdditives.map(r => ({ ...r }))
          }
        });
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error saving slurry:', err);
        alert('Failed to save slurry. Please try again.');
      }
    });
  }

  updateSlurry(): void {
    if (!this.slurryId || this.slurryId <= 0 || this.isSaving) { return; }
    const request = this.buildSaveRequest();
    if (!request) { return; }
    this.isSaving = true;
    this.saveMessage = 'Saving changes...';
    this.saveTestsBeforePrimaryAction(() => {
      this.slurryService.updateSlurry(this.slurryId, request).subscribe({
        next: () => {
          this.isLoading = false;
          this.isSaving = false;
          this.saveMessage = 'Slurry updated successfully.';
          this.refreshSlurryAfterMutation(true);
        },
        error: (err) => {
          this.isLoading = false;
          this.isSaving = false;
          console.error('Error updating slurry:', err);
          this.saveMessage = this.getApiErrorMessage(err, 'Failed to update slurry. Please try again.');
        }
      });
    }, 'Update');
  }

  private loadCapabilities(): void {
    if (!this.slurryId || this.slurryId <= 0) {
      this.capabilities = null;
      return;
    }

    this.slurryService.getCapabilities(this.slurryId).subscribe({
      next: (capabilities) => {
        this.capabilities = capabilities;
        this.normalizeVisibleTabSelection();
      },
      error: (err) => {
        console.error('Failed to load slurry capabilities:', err);
        this.capabilities = null;
        this.normalizeVisibleTabSelection();
      }
    });
  }

  private getDefaultSavedSlurryTab(): 'info' | 'props' | 'foam' | 'other' | 'mixability' {
    if (!this.hasSavedSlurry) {
      return 'info';
    }

    if (this.statusId === 3 && !this.hasEnteredMixability()) {
      return 'mixability';
    }

    if (this.showPropsTab() && this.hasActiveSlurryPropertiesTests()) {
      return 'props';
    }

    if (this.showFoamTab() && this.hasActiveFoamTests()) {
      return 'foam';
    }

    if (this.showOtherTab() && this.hasActiveOtherTests()) {
      return 'other';
    }

    return 'info';
  }

  private isTabVisible(tab: 'info' | 'props' | 'foam' | 'other' | 'mixability' | 'redesign' | 'batches'): boolean {
    switch (tab) {
      case 'info':
        return true;
      case 'props':
        return this.showPropsTab();
      case 'foam':
        return this.showFoamTab();
      case 'other':
        return this.showOtherTab();
      case 'mixability':
        return this.showMixabilityTab();
      case 'redesign':
        return this.showRedesignTab();
      case 'batches':
        return this.showBatchesTab();
      default:
        return false;
    }
  }

  private getCapabilityAllowed(selector: (c: SlurryCapabilitiesDto) => SlurryCapabilityItemDto, fallback: boolean): boolean {
    const item = this.capabilities ? selector(this.capabilities) : null;
    return item?.allowed ?? fallback;
  }

  private getTabVisibility(selector: (c: SlurryCapabilitiesDto) => SlurryCapabilityItemDto, hasActiveTests: boolean): boolean {
    if (hasActiveTests) {
      return true;
    }

    return this.getCapabilityAllowed(selector, false);
  }

  private hasEnteredMixability(): boolean {
    return (this.mixabilityId ?? 0) > 0;
  }

  private hasActiveSlurryPropertiesTests(): boolean {
    return this.thickeningSelected ||
      this.fluidLossTests.length > 0 ||
      this.freeFluidTests.length > 0 ||
      this.rheologyTests.length > 0 ||
      this.staticGelTests.length > 0 ||
      this.compStrTests.length > 0 ||
      this.hphtTests.length > 0;
  }

  private hasActiveFoamTests(): boolean {
    return this.foamCompStrTests.length > 0 || this.foamStabilityTests.length > 0;
  }

  private hasActiveOtherTests(): boolean {
    return this.otherAcidSolubilityRows.length > 0 ||
      this.otherApiSedimentationRows.length > 0 ||
      this.shearBondRows.length > 0 ||
      this.otherPressurizedMudScaleRows.length > 0 ||
      this.otherExpansionRows.length > 0 ||
      this.otherPycnometerRows.length > 0 ||
      this.otherSplittingTsRows.length > 0 ||
      this.otherTriaxilConfinedCsRows.length > 0 ||
      this.otherUnconfinedCsRows.length > 0 ||
      this.otherBasicCrushRows.length > 0;
  }

  private getCapabilityReason(selector: (c: SlurryCapabilitiesDto) => SlurryCapabilityItemDto): string | null {
    const item = this.capabilities ? selector(this.capabilities) : null;
    if (!item || item.allowed || !item.reason) {
      return null;
    }
    return item.reason;
  }

  private clearBlockedActionMessages(): void {
    this.blockedInfoHeaderActionMessage = '';
    this.blockedInfoNotesActionMessage = '';
    this.blockedInfoFooterActionMessage = '';
    this.blockedTestsActionMessage = '';
    this.blockedBatchActionMessage = '';
    this.blockedBatchDetailsActionMessage = '';
  }

  private getApiErrorMessage(err: any, fallback: string): string {
    return err?.error?.detail || err?.error?.title || fallback;
  }

  /** Parses legacy target time input: "hh:mm" or hour-only "hh" to total minutes. */
  private parseTimeToMins(value: string): number | undefined {
    return parseLegacyTimeToMinutes(value);
  }

  goBack(): void {
    if (this.embedded) {
      this.back.emit();
      return;
    }
    this.router.navigate(['/projects-dashboard']);
  }

  /**
   * DEF-015: Breadcrumb navigation to Dashboard
   * Routes back to the projects dashboard from standalone slurry-details route.
   */
  navigateToDashboard(): void {
    this.router.navigate(['/projects-dashboard']);
  }

  /**
   * DEF-015: Breadcrumb navigation to Project
   * Routes to the originating project details page from standalone slurry-details route.
   * In embedded mode, uses the back button instead.
   */
  navigateToProject(): void {
    if (this.embedded) {
      this.back.emit();
    } else if (this.projectId) {
      this.router.navigate(['/projects-dashboard'], {
        state: {
          openProjectId: this.projectId,
          projectInitialTab: 'project'
        }
      });
    }
  }

  openThickeningAttachments(): void {
    this.openTestAttachments(this.thickeningTestInputMasterId, 'Thickening Time');
  }

  private refreshTestAttachmentCounts(): void {
    if (this.slurryId <= 0) {
      this.testAttachmentCounts = {};
      return;
    }

    this.projectDetailsService.getTestAttachmentsBySlurryId(this.slurryId).subscribe({
      next: (attachments) => {
        const counts: Record<number, number> = {};
        for (const attachment of attachments) {
          counts[attachment.ownerId] = (counts[attachment.ownerId] ?? 0) + 1;
        }
        this.testAttachmentCounts = counts;
      },
      error: (err) => {
        console.error('Failed to load test attachment counts:', err);
        this.testAttachmentCounts = {};
      }
    });
  }

  private scrollToTop(): void {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }
}
